import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Plus, Trash, X, CalendarBlank, Clock, MapPin, PencilSimple,
  Check, Confetti, Link, Copy, Users, CookingPot, Baby,
  Eye, ShareNetwork, WhatsappLogo, CaretDown, CaretUp,
  TextT, ListNumbers, ListBullets, RadioButton, CheckSquare,
  ToggleLeft, TextAa, ArrowRight, Export, MagicWand, Sparkle,
  ArrowUp, ArrowDown, ArrowsClockwise, FloppyDisk, CopySimple,
  Image as ImageIcon, Camera, Heart as HeartIcon, FileText, CheckCircle, Phone, QrCode, Wallet
} from '@phosphor-icons/react';
import { QRCodeCanvas } from 'qrcode.react';
import InvitationGenerator from '@/components/InvitationGenerator';
import ThemePreview from '@/components/ThemePreview';
import MoneyTab from '@/components/events/MoneyTab';
import TasksTab from '@/components/events/TasksTab';
import VendorsTab from '@/components/events/VendorsTab';
import TimelineTab from '@/components/events/TimelineTab';
import BagsAndGiftsTab from '@/components/events/BagsAndGiftsTab';
import { BulkActionBar, FilterChips, applyRsvpFilter, WhatsAppQueueModal } from '@/components/events/BulkRsvpControls';
import OpenBalancesModal from '@/components/events/OpenBalancesModal';
import { apiOrigin } from '@/utils/apiBase';

const FIELD_TYPES = [
  { id: 'text', label: 'Text Input', icon: TextT },
  { id: 'textarea', label: 'Long Text', icon: TextAa },
  { id: 'number', label: 'Number', icon: ListNumbers },
  { id: 'dropdown', label: 'Dropdown', icon: CaretDown },
  { id: 'radio', label: 'Single Choice', icon: RadioButton },
  { id: 'checkbox', label: 'Multiple Choice', icon: CheckSquare },
  { id: 'yesno', label: 'Yes / No', icon: ToggleLeft },
  { id: 'siblings', label: 'Siblings (names+ages)', icon: Baby },
  { id: 'image', label: 'Image Upload', icon: ImageIcon },
];

export default function EventManagerPage() {
  const { api } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [duplicateEventSrc, setDuplicateEventSrc] = useState(null);
  const [viewEvent, setViewEvent] = useState(null);
  const [rsvps, setRsvps] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [copied, setCopied] = useState(false);
  const [themes, setThemes] = useState([]);
  const [invitationEvent, setInvitationEvent] = useState(null);
  const [photoEvent, setPhotoEvent] = useState(null);
  const [showOpenBalances, setShowOpenBalances] = useState(false);
  const [openBalCount, setOpenBalCount] = useState(null);   // rows with confirmed balance > 0
  const [openBalTotal, setOpenBalTotal] = useState(0);       // total confirmed amount

  const fetchEvents = useCallback(async () => {
    try {
      const res = await api.get('/events');
      setEvents(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api]);

  const fetchThemes = useCallback(async () => {
    try {
      const [builtin, custom] = await Promise.all([
        api.get('/events/themes'),
        api.get('/events/themes/custom').catch(() => ({ data: { themes: [] } })),
      ]);
      const customThemes = custom.data.themes || [];
      setThemes([...(builtin.data.themes || []), ...customThemes]);
    } catch (e) { console.error(e); }
  }, [api]);

  useEffect(() => { fetchEvents(); fetchThemes(); }, [fetchEvents, fetchThemes]);

  // Load open-balances count for the toolbar chip (only when on the list view)
  useEffect(() => {
    if (viewEvent) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/events/money/open-balances');
        if (cancelled) return;
        const confirmed = r.data?.rows?.filter(x => x.source === 'final') || [];
        setOpenBalCount(confirmed.length);
        setOpenBalTotal(r.data?.totals?.total_confirmed || 0);
      } catch (e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [api, viewEvent, showOpenBalances]);

  const handleDelete = async (eventId) => {
    if (!window.confirm('Delete this event and all RSVPs?')) return;
    try {
      await api.delete(`/events/${eventId}`);
      fetchEvents();
      if (viewEvent?.event_id === eventId) setViewEvent(null);
    } catch (e) { console.error(e); }
  };

  const loadRsvps = async (event) => {
    setViewEvent(event);
    const bust = `?_t=${Date.now()}`;
    try {
      const [rsvpRes, analyticsRes, draftsRes] = await Promise.all([
        api.get(`/events/${event.event_id}/rsvps${bust}`),
        api.get(`/events/${event.event_id}/analytics${bust}`),
        api.get(`/events/${event.event_id}/drafts${bust}`).catch(() => ({ data: { drafts: [] } })),
      ]);
      setRsvps(rsvpRes.data);
      setAnalytics(analyticsRes.data);
      setDrafts(draftsRes.data.drafts || []);
    } catch (e) { console.error(e); }
  };

  const refreshDrafts = async () => {
    if (!viewEvent) return;
    try {
      const r = await api.get(`/events/${viewEvent.event_id}/drafts?_t=${Date.now()}`);
      setDrafts(r.data.drafts || []);
    } catch (e) { console.error(e); }
  };

  const handleDeleteDraft = async (draftId) => {
    if (!viewEvent) return;
    if (!window.confirm('Discard this incomplete draft?')) return;
    try {
      await api.delete(`/events/${viewEvent.event_id}/drafts/${draftId}`);
      refreshDrafts();
    } catch (e) { console.error(e); }
  };

  const handlePromoteDraft = async (draftId) => {
    if (!viewEvent) return;
    if (!window.confirm("Convert this draft into a confirmed RSVP? Use this only if the guest told you they're coming offline.")) return;
    try {
      await api.post(`/events/${viewEvent.event_id}/drafts/${draftId}/promote`);
      loadRsvps(viewEvent); // refreshes both rsvps + drafts
    } catch (e) { console.error(e); alert('Promote failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const handleDeleteRsvp = async (rsvpId) => {
    try {
      await api.delete(`/events/${viewEvent.event_id}/rsvps/${rsvpId}`);
      loadRsvps(viewEvent);
    } catch (e) { console.error(e); }
  };

  // Called by AdminEditRsvpModal with the fresh RSVP returned by the backend
  // so we can update the list immediately without depending on a network re-fetch.
  const handleRsvpUpdated = (updatedRsvp) => {
    if (!updatedRsvp) return;
    setRsvps(prev => prev.map(r => r.rsvp_id === updatedRsvp.rsvp_id ? updatedRsvp : r));
    // Best-effort: refresh analytics in the background (counter cards depend on it)
    if (viewEvent) {
      api.get(`/events/${viewEvent.event_id}/analytics?_t=${Date.now()}`)
        .then(r => setAnalytics(r.data))
        .catch(() => {});
    }
  };

  const getRSVPLink = (event) => `${window.location.origin}/party/${event.slug}`;

  const handleCopyLink = (event) => {
    navigator.clipboard.writeText(getRSVPLink(event));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getWhatsAppMsg = (event) => {
    const link = getRSVPLink(event);
    return encodeURIComponent(`${event.title}\n\n${event.description ? event.description + '\n\n' : ''}${event.event_date ? formatDate(event.event_date) : ''}${event.event_time ? ' | ' + event.event_time + (event.end_time ? '-' + event.end_time : '') : ''}${event.venue ? '\n' + event.venue : ''}\n\nPlease RSVP here:\n${link}`);
  };

  const downloadExport = async (kind) => {
    if (!viewEvent) return;
    try {
      const resp = await api.get(`/events/${viewEvent.event_id}/export/${kind}`, { responseType: 'blob' });
      const blob = new Blob([resp.data]);
      const url = URL.createObjectURL(blob);
      const ext = kind === 'xlsx' ? 'xlsx' : kind === 'pdf' ? 'pdf' : 'csv';
      const safe = (viewEvent.title || 'rsvps').replace(/[^a-z0-9._-]+/gi, '_').slice(0, 80);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${safe}_rsvps.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      console.error('export failed', e);
      alert('Export failed. Please try again.');
    }
  };

  const exportCSV = () => downloadExport('csv');
  const exportXLSX = () => downloadExport('xlsx');
  const exportPDF = () => downloadExport('pdf');


  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="event-manager-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-neutral-500 text-xs sm:text-sm font-medium">Events</p>
          <h1 className="font-heading text-xl sm:text-3xl md:text-4xl font-bold text-white tracking-tight mt-1">Party & Events</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {!viewEvent && openBalCount !== null && openBalCount > 0 && (
            <button onClick={() => setShowOpenBalances(true)}
              data-testid="open-balances-chip"
              title="Pending vendor payments across all events — click to sweep"
              className="flex items-center gap-1.5 px-3 py-2.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/40 text-xs font-bold hover:bg-amber-500/25 transition-all">
              <Wallet size={14} weight="fill" />
              <span className="hidden sm:inline">Open Balances</span>
              <span className="px-1.5 py-0.5 rounded-full bg-amber-500 text-black text-[10px] font-extrabold">
                {openBalCount}
              </span>
              <span className="font-extrabold text-amber-200">{'₹' + Math.round(openBalTotal).toLocaleString('en-IN')}</span>
            </button>
          )}
          {!viewEvent && openBalCount === 0 && (
            <button onClick={() => setShowOpenBalances(true)}
              data-testid="open-balances-chip"
              title="Nothing pending — but click to double-check estimated balances"
              className="hidden sm:flex items-center gap-1.5 px-3 py-2.5 rounded-full bg-green-500/15 text-green-400 border border-green-500/30 text-xs font-semibold hover:bg-green-500/25">
              <Wallet size={14} /> Open Balances · ✓
            </button>
          )}
          <button onClick={() => { setEditingEvent(null); setShowCreate(true); }} data-testid="create-event-btn"
            className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#FF6B35] text-white text-xs font-semibold btn-glow hover:scale-105 transition-transform">
            <Plus size={16} /> New Event
          </button>
        </div>
      </div>

      {showOpenBalances && <OpenBalancesModal onClose={() => setShowOpenBalances(false)} onChanged={fetchEvents} />}

      {/* Event Cards */}
      {events.length === 0 && !viewEvent ? (
        <div className="glass-card p-12 text-center">
          <Confetti size={48} className="text-neutral-600 mx-auto mb-3" />
          <p className="text-base text-neutral-400 mb-1">No events yet</p>
          <p className="text-sm text-neutral-600">Create your first party or event to start collecting RSVPs</p>
        </div>
      ) : !viewEvent ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="events-grid">
          {events.map(event => (
            <div key={event.event_id} className="glass-card p-5 flex flex-col" data-testid={`event-card-${event.event_id}`}>
              <div className="flex items-start justify-between gap-2 mb-3">
                <div>
                  {event.theme_emoji && <span className="text-2xl">{event.theme_emoji}</span>}
                  <h3 className="text-sm font-bold text-white mt-1">{event.title}</h3>
                </div>
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${event.status === 'active' ? 'bg-green-500/10 text-green-400' : event.status === 'closed' ? 'bg-red-500/10 text-red-400' : 'bg-yellow-500/10 text-yellow-400'}`}>
                  {event.status}
                </span>
              </div>
              <div className="space-y-1.5 text-xs text-neutral-400 mb-4 flex-1">
                {event.event_date && <div className="flex items-center gap-2"><CalendarBlank size={12} /> {formatDate(event.event_date)}</div>}
                {event.event_time && <div className="flex items-center gap-2"><Clock size={12} /> {event.event_time}{event.end_time ? ` - ${event.end_time}` : ''}</div>}
                {event.venue && <div className="flex items-center gap-2"><MapPin size={12} /> {event.venue}</div>}
              </div>
              <div className="flex items-center gap-2 text-xs mb-4 flex-wrap">
                <span className="px-2.5 py-1 rounded-lg bg-green-500/10 text-green-400 font-semibold">{event.attending_count || 0} attending</span>
                <span className="px-2.5 py-1 rounded-lg bg-white/5 text-neutral-400">{event.rsvp_count || 0} total</span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={() => loadRsvps(event)} data-testid={`view-rsvps-${event.event_id}`}
                  className="flex items-center gap-1 px-3 py-2 rounded-xl bg-[#FF6B35]/10 text-[#FF6B35] text-xs font-semibold hover:bg-[#FF6B35]/20">
                  <Eye size={12} /> Responses
                </button>
                <button onClick={() => setInvitationEvent(event)} data-testid={`invitation-${event.event_id}`}
                  className="flex items-center gap-1 px-3 py-2 rounded-xl bg-purple-500/10 text-purple-300 text-xs font-semibold hover:bg-purple-500/20">
                  <MagicWand size={12} weight="fill" /> Invitation
                </button>
                <button onClick={() => handleCopyLink(event)} data-testid={`copy-link-${event.event_id}`}
                  className="flex items-center gap-1 px-3 py-2 rounded-xl bg-white/5 text-neutral-400 text-xs font-semibold hover:bg-white/10">
                  {copied ? <Check size={12} className="text-green-400" /> : <Link size={12} />}
                  {copied ? 'Copied!' : 'Copy Link'}
                </button>
                <a href={`https://wa.me/?text=${getWhatsAppMsg(event)}`} target="_blank" rel="noopener noreferrer"
                  data-testid={`whatsapp-share-${event.event_id}`}
                  className="flex items-center gap-1 px-3 py-2 rounded-xl bg-green-500/10 text-green-400 text-xs font-semibold hover:bg-green-500/20">
                  <WhatsappLogo size={12} weight="fill" /> Share
                </a>
                <button onClick={() => { setEditingEvent(event); setShowCreate(true); }}
                  data-testid={`edit-event-${event.event_id}`} title="Edit"
                  className="p-2 rounded-xl bg-white/5 text-neutral-400 hover:bg-white/10"><PencilSimple size={12} /></button>
                <button onClick={() => setPhotoEvent(event)}
                  data-testid={`photo-gallery-${event.event_id}`} title="Photo Gallery"
                  className="p-2 rounded-xl bg-white/5 text-neutral-400 hover:text-pink-300 hover:bg-pink-500/10"><Camera size={12} /></button>
                <button onClick={() => setDuplicateEventSrc(event)}
                  data-testid={`duplicate-event-${event.event_id}`} title="Duplicate event"
                  className="p-2 rounded-xl bg-white/5 text-neutral-400 hover:text-blue-300 hover:bg-blue-500/10"><CopySimple size={12} /></button>
                <button onClick={() => handleDelete(event.event_id)}
                  data-testid={`delete-event-${event.event_id}`} title="Delete"
                  className="p-2 rounded-xl bg-white/5 text-neutral-400 hover:text-red-400 hover:bg-red-500/10"><Trash size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* RSVP Dashboard */
        <RSVPDashboard event={viewEvent} rsvps={rsvps} drafts={drafts} analytics={analytics}
          onBack={() => setViewEvent(null)} onDeleteRsvp={handleDeleteRsvp}
          onRefreshRsvps={() => loadRsvps(viewEvent)}
          onRsvpUpdated={handleRsvpUpdated}
          onDeleteDraft={handleDeleteDraft} onPromoteDraft={handlePromoteDraft}
          onRefreshDrafts={refreshDrafts}
          onCopyLink={() => handleCopyLink(viewEvent)}
          onExport={exportCSV}
          onExportXLSX={exportXLSX}
          onExportPDF={exportPDF}
          whatsappUrl={`https://wa.me/?text=${getWhatsAppMsg(viewEvent)}`}
          rsvpLink={getRSVPLink(viewEvent)} copied={copied} />
      )}

      {/* Create/Edit Modal */}
      {showCreate && <EventFormModal api={api} event={editingEvent} themes={themes} onThemesChanged={fetchThemes} onClose={() => { setShowCreate(false); setEditingEvent(null); }} onSaved={() => { setShowCreate(false); setEditingEvent(null); fetchEvents(); }} />}

      {/* Duplicate Modal */}
      {duplicateEventSrc && (
        <DuplicateEventModal api={api} source={duplicateEventSrc}
          onClose={() => setDuplicateEventSrc(null)}
          onDuplicated={(newEvent) => { setDuplicateEventSrc(null); fetchEvents(); setEditingEvent(newEvent); setShowCreate(true); }} />
      )}

      {/* Photo Gallery Admin Modal */}
      {photoEvent && (
        <PhotoGalleryAdminModal api={api} event={photoEvent}
          onClose={() => setPhotoEvent(null)} />
      )}

      {/* Invitation Generator Modal */}
      {invitationEvent && (
        <InvitationGenerator
          event={invitationEvent}
          theme={themes.find(t => t.theme_id === (invitationEvent.theme_id || 'default')) || { primary: invitationEvent.theme_color || '#FF6B35', accent: invitationEvent.theme_color || '#FF6B35', card_bg: `linear-gradient(135deg, ${invitationEvent.theme_color || '#FF6B35'}, ${invitationEvent.theme_color || '#FF6B35'}DD)`, name: 'Custom', emojis: [invitationEvent.theme_emoji || '🎉'] }}
          api={api}
          rsvpLink={getRSVPLink(invitationEvent)}
          onClose={() => { setInvitationEvent(null); fetchEvents(); }}
        />
      )}
    </div>
  );
}


function RSVPDashboard({ event, rsvps, drafts = [], analytics, onBack, onDeleteRsvp, onRefreshRsvps, onRsvpUpdated, onDeleteDraft, onPromoteDraft, onRefreshDrafts, onCopyLink, onExport, onExportXLSX, onExportPDF, whatsappUrl, rsvpLink, copied }) {
  const { api } = useAuth();
  const attending = rsvps.filter(r => r.can_attend?.toLowerCase()?.includes('yes'));
  const notAttending = rsvps.filter(r => !r.can_attend?.toLowerCase()?.includes('yes'));
  const [tab, setTab] = useState('responses');
  const [invitees, setInvitees] = useState([]);
  const [reminders, setReminders] = useState(null);
  const [addName, setAddName] = useState('');
  const [addPhone, setAddPhone] = useState('');
  const [bulkText, setBulkText] = useState('');
  const [showBulk, setShowBulk] = useState(false);
  const [showSendQueue, setShowSendQueue] = useState(false);
  const [qrRsvp, setQrRsvp] = useState(null);  // {rsvp_id, personal_token, child_name,...} when QR modal open
  const [editRsvp, setEditRsvp] = useState(null);  // RSVP being edited by admin
  const [showBadgeSheet, setShowBadgeSheet] = useState(false);

  // Bulk-edit state
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [filter, setFilter] = useState('all');
  const [waQueueRsvps, setWaQueueRsvps] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const visibleRsvps = useMemo(
    () => applyRsvpFilter(rsvps, filter, event.custom_fields || []),
    [rsvps, filter, event.custom_fields]
  );

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAllVisible = () => setSelectedIds(new Set(visibleRsvps.map(r => r.rsvp_id)));
  const clearSelection = () => setSelectedIds(new Set());

  const selectedRsvps = useMemo(
    () => rsvps.filter(r => selectedIds.has(r.rsvp_id)),
    [rsvps, selectedIds]
  );

  const runBulk = async (body) => {
    if (selectedIds.size === 0) return;
    setBulkBusy(true);
    try {
      await api.post(`/events/${event.event_id}/rsvps/bulk`, {
        rsvp_ids: Array.from(selectedIds),
        ...body,
      });
      // Server-of-truth refresh — keep selection so admin can chain actions
      if (onRefreshRsvps) onRefreshRsvps();
    } catch (e) {
      window.alert(e.response?.data?.detail?.message || e.response?.data?.detail || 'Bulk action failed');
    }
    setBulkBusy(false);
  };

  const handleBulkSetDiet = (d) => runBulk({ action: 'set_dietary', set_dietary: d, admin_note: `Bulk: set diet → ${d}` });
  const handleBulkAddAcc = (v) => runBulk({ action: 'add_accompanying', add_accompanying: [v], admin_note: `Bulk: + ${v}` });
  const handleBulkRemoveAcc = (v) => runBulk({ action: 'remove_accompanying', remove_accompanying: [v], admin_note: `Bulk: − ${v}` });
  const handleBulkMarkNo = () => {
    if (!window.confirm(`Mark ${selectedIds.size} RSVP(s) as Not Attending?`)) return;
    runBulk({ action: 'set_can_attend', set_can_attend: 'No', admin_note: 'Bulk: marked Not Attending' });
  };
  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedIds.size} RSVP(s)? This cannot be undone.`)) return;
    await runBulk({ action: 'delete' });
    clearSelection();
  };
  const handleBulkWhatsApp = () => {
    if (selectedRsvps.length === 0) return;
    setWaQueueRsvps(selectedRsvps);
  };
  const handleBulkPrintLabels = async () => {
    // Download the labels PDF — backend currently prints all attending labels for the event,
    // so we just inform the user that the selection is informational for now.
    try {
      const r = await api.get(`/events/${event.event_id}/gifts/labels.pdf`, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url;
      a.download = `${(event.title || 'event').replace(/[^A-Za-z0-9._-]+/g, '_')}_packing_labels.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      window.alert('Generate labels from the Bags & Gifts tab first.');
    }
  };

  const fetchInvitees = useCallback(async () => {
    try {
      const res = await api.get(`/events/${event.event_id}/invitees`);
      setInvitees(res.data);
    } catch (e) { console.error(e); }
  }, [api, event.event_id]);

  const fetchReminders = useCallback(async () => {
    try {
      const res = await api.get(`/events/${event.event_id}/reminders?days=0`);
      setReminders(res.data);
    } catch (e) { console.error(e); }
  }, [api, event.event_id]);

  useEffect(() => { fetchInvitees(); fetchReminders(); }, [fetchInvitees, fetchReminders]);

  const handleAddInvitee = async () => {
    if (!addName) return;
    try {
      await api.post(`/events/${event.event_id}/invitees`, { name: addName, phone: addPhone });
      setAddName(''); setAddPhone('');
      fetchInvitees(); fetchReminders();
    } catch (e) { console.error(e); }
  };

  const handleBulkAdd = async () => {
    if (!bulkText.trim()) return;
    const lines = bulkText.trim().split('\n').filter(l => l.trim());
    const invs = lines.map(line => {
      const parts = line.split(',').map(p => p.trim());
      return { name: parts[0] || '', phone: parts[1] || '' };
    }).filter(i => i.name);
    try {
      await api.post(`/events/${event.event_id}/invitees/bulk`, { invitees: invs });
      setBulkText(''); setShowBulk(false);
      fetchInvitees(); fetchReminders();
    } catch (e) { console.error(e); }
  };

  const handleDeleteInvitee = async (id) => {
    try {
      await api.delete(`/events/${event.event_id}/invitees/${id}`);
      fetchInvitees(); fetchReminders();
    } catch (e) { console.error(e); }
  };

  const handleMarkSent = async (inviteeId) => {
    try {
      await api.post(`/events/${event.event_id}/reminders/${inviteeId}/mark-sent`);
      fetchReminders();
    } catch (e) { console.error(e); }
  };

  const getReminderWhatsAppUrl = (inv) => {
    const msg = (inv.whatsapp_message || '').replace('[link]', rsvpLink);
    const phone = (inv.phone || '').replace('+', '');
    return `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
  };

  return (
    <div className="space-y-5" data-testid="rsvp-dashboard">
      {/* Back + Share */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <button onClick={onBack} className="flex items-center gap-2 text-sm text-neutral-400 hover:text-white">
          <ArrowRight size={14} className="rotate-180" /> Back to Events
        </button>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={onCopyLink} className="flex items-center gap-1 px-3 py-2 rounded-xl bg-white/5 text-xs text-neutral-400 font-semibold hover:bg-white/10">
            {copied ? <Check size={12} className="text-green-400" /> : <Copy size={12} />} {copied ? 'Copied!' : 'Copy Link'}
          </button>
          <a href={whatsappUrl} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1 px-3 py-2 rounded-xl bg-green-500/10 text-green-400 text-xs font-semibold hover:bg-green-500/20">
            <WhatsappLogo size={12} weight="fill" /> WhatsApp
          </a>
          <ExportMenu rsvpsCount={rsvps.length} onCSV={onExport} onXLSX={onExportXLSX} onPDF={onExportPDF} />
          <a href={`/party/${event.slug}/kiosk`} target="_blank" rel="noopener noreferrer"
            data-testid="open-kiosk-btn"
            className="flex items-center gap-1 px-3 py-2 rounded-xl bg-purple-500/10 text-purple-400 text-xs font-semibold hover:bg-purple-500/20"
            title="Open the live TV kiosk view in a new tab — show on a venue display">
            <Eye size={12} weight="fill" /> Open Kiosk
          </a>
          <a href={`/party/${event.slug}/welcome-wall`} target="_blank" rel="noopener noreferrer"
            data-testid="open-welcome-wall-btn"
            className="flex items-center gap-1 px-3 py-2 rounded-xl bg-amber-500/10 text-amber-400 text-xs font-semibold hover:bg-amber-500/20"
            title="Open the entrance Welcome Wall — full-screen tablet display that plays a 5-sec greeting on every QR check-in">
            <Confetti size={12} weight="fill" /> Welcome Wall
          </a>
          <button onClick={() => setShowBadgeSheet(true)}
            data-testid="print-badges-btn"
            className="flex items-center gap-1 px-3 py-2 rounded-xl bg-pink-500/10 text-pink-400 text-xs font-semibold hover:bg-pink-500/20"
            title="Print a single A4 sheet of name-tag QR badges for every attending guest">
            <QrCode size={12} /> Print Badges
          </button>
          <ShareLiveViewButton api={api} event={event} />
        </div>
      </div>

      {/* Event Title */}
      <div className="glass-card p-5">
        <div className="flex items-center gap-3">
          {event.theme_emoji && <span className="text-3xl">{event.theme_emoji}</span>}
          <div>
            <h2 className="text-lg font-bold text-white">{event.title}</h2>
            <div className="flex flex-wrap gap-3 text-xs text-neutral-400 mt-1">
              {event.event_date && <span><CalendarBlank size={12} className="inline mr-1" />{formatDate(event.event_date)}</span>}
              {event.event_time && <span><Clock size={12} className="inline mr-1" />{event.event_time}{event.end_time ? `-${event.end_time}` : ''}</span>}
              {event.venue && <span><MapPin size={12} className="inline mr-1" />{event.venue}</span>}
            </div>
          </div>
        </div>
        <div className="mt-3 px-3 py-2 rounded-xl bg-white/5 text-xs text-neutral-500 break-all">
          <Link size={12} className="inline mr-1" /> {rsvpLink}
        </div>
      </div>

      {/* Stats Cards */}
      {analytics && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          <StatCard label="Total RSVPs" value={analytics.total_responses} color="#FF6B35" />
          <StatCard label="Attending" value={analytics.attending} color="#22C55E" />
          <StatCard label="Can't Attend" value={analytics.not_attending} color="#EF4444" />
          <StatCard label="Kids" value={analytics.total_kids} color="#00E5FF" icon={Baby} />
          <StatCard label="Siblings" value={analytics.total_siblings || 0} color="#EC4899" icon={Baby} />
          <StatCard label="Adults" value={analytics.total_adults} color="#A855F7" icon={Users} />
          <StatCard label="Total Head" value={analytics.total_headcount} color="#F59E0B" />
        </div>
      )}

      {/* Dashboard Tabs */}
      <div className="flex gap-1 p-1 rounded-2xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
        {[
          { id: 'responses', label: `Responses (${rsvps.length})` },
          { id: 'drafts', label: `Incomplete (${drafts.length})`, badge: drafts.length > 0 ? '#F59E0B' : null },
          { id: 'match', label: 'Match' },
          { id: 'tasks', label: 'Tasks' },
          { id: 'money', label: 'Money' },
          { id: 'gifts', label: 'Bags & Gifts' },
          { id: 'vendors', label: 'Vendors' },
          { id: 'timeline', label: 'Timeline' },
          { id: 'invitees', label: `Invitees (${invitees.length})` },
          { id: 'reminders', label: `Reminders${reminders?.pending_reminders ? ` (${reminders.pending_reminders})` : ''}` },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`rsvp-tab-${t.id}`}
            className={`px-4 py-2.5 rounded-xl text-xs font-semibold transition-all flex items-center gap-1.5 ${tab === t.id ? 'bg-[#FF6B35]/15 text-[#FF6B35]' : 'text-neutral-400 hover:text-white hover:bg-white/5'}`}>
            {t.badge && <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.badge, boxShadow: `0 0 6px ${t.badge}` }} />}
            {t.label}
          </button>
        ))}
      </div>

      {/* Responses Tab */}
      {tab === 'responses' && (
      <>
      {/* Dietary + Accompanying Breakdown */}
      {analytics && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {analytics.dietary_breakdown?.length > 0 && (
            <div className="glass-card p-4">
              <div className="flex items-center gap-2 mb-3">
                <CookingPot size={14} className="text-[#FF6B35]" />
                <span className="overline">Dietary Breakdown</span>
              </div>
              <div className="space-y-2">
                {analytics.dietary_breakdown.map(d => (
                  <div key={d.label} className="flex items-center justify-between">
                    <span className="text-xs text-neutral-300">{d.label}</span>
                    <span className="text-xs font-bold text-white">{d.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {analytics.accompanying_breakdown?.length > 0 && (
            <div className="glass-card p-4">
              <div className="flex items-center gap-2 mb-3">
                <Users size={14} className="text-blue-400" />
                <span className="overline">Accompanying</span>
              </div>
              <div className="space-y-2">
                {analytics.accompanying_breakdown.map(a => (
                  <div key={a.label} className="flex items-center justify-between">
                    <span className="text-xs text-neutral-300">{a.label}</span>
                    <span className="text-xs font-bold text-white">{a.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* RSVP List */}
      <div className="glass-card overflow-hidden" data-testid="rsvp-list">
        <div className="p-4 border-b border-white/5 flex items-center justify-between flex-wrap gap-2">
          <span className="text-sm font-bold text-white">Responses ({visibleRsvps.length}{filter !== 'all' ? ` of ${rsvps.length}` : ''})</span>
          <button onClick={() => { setSelectMode(m => !m); if (selectMode) clearSelection(); }}
            data-testid="toggle-select-mode"
            className={`text-[11px] font-bold px-2.5 py-1 rounded-lg inline-flex items-center gap-1.5 ${selectMode ? 'bg-[#FF6B35]/15 text-[#FF6B35] border border-[#FF6B35]/40' : 'bg-white/5 text-neutral-400 border border-white/10 hover:bg-white/10'}`}>
            <CheckSquare size={11} weight={selectMode ? 'fill' : 'regular'} /> {selectMode ? 'Cancel select' : 'Select multiple'}
          </button>
        </div>

        <div className="px-4 pt-3">
          <FilterChips value={filter} onChange={setFilter} />
          {selectMode && (
            <BulkActionBar
              selectedCount={selectedIds.size}
              totalCount={visibleRsvps.length}
              onClearSelection={clearSelection}
              onSelectAllVisible={selectAllVisible}
              onSetDietary={handleBulkSetDiet}
              onAddAcc={handleBulkAddAcc}
              onRemoveAcc={handleBulkRemoveAcc}
              onSetAttendingNo={handleBulkMarkNo}
              onDelete={handleBulkDelete}
              onSendWhatsApp={handleBulkWhatsApp}
              onPrintLabels={handleBulkPrintLabels}
              dietaryOptions={event?.built_in_fields?.dietary_options || ['Veg', 'Non-veg', 'Jain', 'Allergies']}
              accompanyingOptions={event?.built_in_fields?.accompanying_options || ['Mom', 'Dad', 'Nanny', 'Driver', 'Grandparent']}
            />
          )}
        </div>

        {/* Attending */}
        {(() => {
          const vAttending = visibleRsvps.filter(r => r.can_attend?.toLowerCase()?.includes('yes'));
          if (vAttending.length === 0) return null;
          return (
            <div>
              <div className="px-4 py-2 bg-green-500/5 border-b border-white/5">
                <span className="text-xs font-bold text-green-400">Attending ({vAttending.length})</span>
              </div>
              {vAttending.map(r => (
                <RSVPRow key={r.rsvp_id} rsvp={r}
                  onDelete={() => onDeleteRsvp(r.rsvp_id)} onShowQR={setQrRsvp} onEdit={setEditRsvp}
                  event={event}
                  selected={selectedIds.has(r.rsvp_id)} onToggleSelect={toggleSelect} selectMode={selectMode} />
              ))}
            </div>
          );
        })()}

        {/* Not Attending */}
        {(() => {
          const vNot = visibleRsvps.filter(r => !r.can_attend?.toLowerCase()?.includes('yes'));
          if (vNot.length === 0) return null;
          return (
            <div>
              <div className="px-4 py-2 bg-red-500/5 border-b border-white/5">
                <span className="text-xs font-bold text-red-400">Can't Attend ({vNot.length})</span>
              </div>
              {vNot.map(r => (
                <RSVPRow key={r.rsvp_id} rsvp={r}
                  onDelete={() => onDeleteRsvp(r.rsvp_id)} onShowQR={setQrRsvp} onEdit={setEditRsvp}
                  event={event}
                  selected={selectedIds.has(r.rsvp_id)} onToggleSelect={toggleSelect} selectMode={selectMode} />
              ))}
            </div>
          );
        })()}

        {rsvps.length === 0 && (
          <div className="p-8 text-center text-neutral-500 text-sm">No responses yet. Share the RSVP link to start collecting!</div>
        )}
        {rsvps.length > 0 && visibleRsvps.length === 0 && (
          <div className="p-8 text-center text-neutral-500 text-sm">No matches for the current filter. <button onClick={() => setFilter('all')} className="text-[#FF6B35] underline ml-1">Reset filter</button></div>
        )}
      </div>
      </>
      )}

      {/* Drafts Tab — incomplete / abandoned RSVPs auto-saved while guests typed */}
      {tab === 'drafts' && (
        <DraftsTab event={event} drafts={drafts} onDelete={onDeleteDraft}
          onPromote={onPromoteDraft} onRefresh={onRefreshDrafts} />
      )}

      {/* Match Tab — invitation list vs RSVPs side-by-side */}
      {tab === 'match' && (
        <MatchTab event={event} />
      )}

      {/* Money Tab — Budget + Shagun + Net Settlement summary */}
      {tab === 'money' && (
        <MoneyTab event={event} />
      )}

      {/* Bags & Gifts Tab — auto-tally return gifts from RSVPs */}
      {tab === 'gifts' && (
        <BagsAndGiftsTab event={event} />
      )}

      {/* Tasks Tab — pre-event checklist with Indian event templates */}
      {tab === 'tasks' && (
        <TasksTab event={event} />
      )}

      {/* Vendors Tab — caterers, decorators, photographers with payments */}
      {tab === 'vendors' && (
        <VendorsTab event={event} />
      )}

      {/* Timeline Tab — minute-by-minute event-day schedule */}
      {tab === 'timeline' && (
        <TimelineTab event={event} />
      )}

      {/* Invitees Tab */}
      {tab === 'invitees' && (
        <div className="space-y-4" data-testid="invitees-section">
          {/* Add invitee form */}
          <div className="glass-card p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-bold text-white">Add Invitees</span>
              <button onClick={() => setShowBulk(!showBulk)} data-testid="bulk-add-toggle"
                className="text-xs text-[#FF6B35] font-semibold hover:underline">
                {showBulk ? 'Single Add' : 'Bulk Add'}
              </button>
            </div>
            {showBulk ? (
              <div className="space-y-3">
                <textarea value={bulkText} onChange={e => setBulkText(e.target.value)}
                  data-testid="bulk-invitees-input"
                  placeholder="One per line: Name, Phone&#10;Aarav Sharma, +919876543210&#10;Myra Patel, +919876543211"
                  className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none resize-none min-h-[100px]" />
                <button onClick={handleBulkAdd} data-testid="bulk-add-btn"
                  className="px-4 py-2 rounded-xl bg-[#FF6B35] text-white text-xs font-semibold">Add All</button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input type="text" value={addName} onChange={e => setAddName(e.target.value)}
                  data-testid="add-invitee-name" placeholder="Child / Parent Name"
                  className="flex-1 px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none" />
                <input type="tel" value={addPhone} onChange={e => setAddPhone(e.target.value)}
                  data-testid="add-invitee-phone" placeholder="+91..."
                  className="w-36 px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none" />
                <button onClick={handleAddInvitee} disabled={!addName} data-testid="add-invitee-btn"
                  className="px-4 py-2.5 rounded-xl bg-[#FF6B35] text-white text-xs font-semibold disabled:opacity-50">
                  <Plus size={14} />
                </button>
              </div>
            )}
          </div>

          {/* Invitees List */}
          <div className="glass-card overflow-hidden" data-testid="invitees-list">
            <div className="p-4 border-b border-white/5 flex items-center justify-between gap-2 flex-wrap">
              <span className="text-sm font-bold text-white">Guest List ({invitees.length})</span>
              {invitees.some(i => i.phone) && (
                <button onClick={() => setShowSendQueue(true)} data-testid="open-send-queue-btn"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-green-500/15 text-green-300 text-xs font-bold hover:bg-green-500/25">
                  <WhatsappLogo size={12} weight="fill" /> Send Invitations
                </button>
              )}
            </div>
            {invitees.length === 0 ? (
              <div className="p-8 text-center text-neutral-500 text-sm">No invitees added yet. Add your guest list to track RSVPs and send reminders.</div>
            ) : (
              <div className="divide-y divide-white/5">
                {invitees.map(inv => (
                  <div key={inv.invitee_id} className="px-4 py-3 flex items-center gap-3 hover:bg-white/[0.02]" data-testid={`invitee-${inv.invitee_id}`}>
                    <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-xs font-bold text-neutral-400 flex-shrink-0">
                      {inv.name?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-white">{inv.name}</p>
                      <div className="flex items-center gap-2 text-[10px] text-neutral-500 mt-0.5 flex-wrap">
                        {inv.phone && <span>{inv.phone}</span>}
                        {inv.invitation_sent && <span className="text-green-400">✓ Invited{inv.invitation_count > 1 ? ` ${inv.invitation_count}x` : ''}</span>}
                        {inv.reminder_count > 0 && <span className="text-yellow-400">Reminded {inv.reminder_count}x</span>}
                      </div>
                    </div>
                    <button onClick={() => handleDeleteInvitee(inv.invitee_id)}
                      className="text-neutral-600 hover:text-red-400 p-1"><Trash size={12} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Send Invitations Queue Modal */}
      {showSendQueue && (
        <SendInvitationsQueue
          api={api}
          event={event}
          rsvpLink={rsvpLink}
          onClose={() => { setShowSendQueue(false); fetchInvitees(); fetchReminders(); }}
        />
      )}

      {/* Reminders Tab */}
      {tab === 'reminders' && (
        <div className="space-y-4" data-testid="reminders-section">
          {/* Summary */}
          {reminders && (
            <div className="grid grid-cols-3 gap-3">
              <StatCard label="Total Invited" value={reminders.total_invitees} color="#FF6B35" />
              <StatCard label="Responded" value={reminders.total_responded} color="#22C55E" />
              <StatCard label="Pending" value={reminders.pending_reminders} color="#F59E0B" />
            </div>
          )}

          {/* Pending Reminders */}
          <div className="glass-card overflow-hidden" data-testid="pending-reminders-list">
            <div className="p-4 border-b border-white/5">
              <span className="text-sm font-bold text-white">Pending Reminders</span>
              <p className="text-[10px] text-neutral-500 mt-0.5">Guests who haven't RSVPed yet</p>
            </div>
            {(!reminders?.invitees || reminders.invitees.length === 0) ? (
              <div className="p-8 text-center text-neutral-500 text-sm">
                {invitees.length === 0 ? 'Add invitees first to track who hasn\'t responded' : 'Everyone has responded! No reminders needed.'}
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {reminders.invitees.map(inv => (
                  <div key={inv.invitee_id} className="px-4 py-3 flex items-center gap-3 hover:bg-white/[0.02]" data-testid={`reminder-${inv.invitee_id}`}>
                    <div className="w-8 h-8 rounded-full bg-yellow-500/10 flex items-center justify-center text-xs font-bold text-yellow-400 flex-shrink-0">
                      {inv.name?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-white">{inv.name}</p>
                      <div className="flex items-center gap-2 text-[10px] text-neutral-500 mt-0.5">
                        {inv.phone && <span>{inv.phone}</span>}
                        <span>Invited {inv.days_since_invited}d ago</span>
                        {inv.reminder_count > 0 && <span className="text-yellow-400">Reminded {inv.reminder_count}x</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {inv.phone && (
                        <a href={getReminderWhatsAppUrl(inv)} target="_blank" rel="noopener noreferrer"
                          onClick={() => handleMarkSent(inv.invitee_id)}
                          data-testid={`send-reminder-${inv.invitee_id}`}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-xl bg-green-500/10 text-green-400 text-[10px] font-semibold hover:bg-green-500/20">
                          <WhatsappLogo size={12} weight="fill" /> Remind
                        </a>
                      )}
                      {!inv.phone && (
                        <span className="text-[10px] text-neutral-600 italic">No phone</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {qrRsvp && (
        <RSVPCheckinQRModal event={event} rsvp={qrRsvp} onClose={() => setQrRsvp(null)} />
      )}
      {editRsvp && (
        <AdminEditRsvpModal event={event} rsvp={editRsvp} api={api}
          onClose={() => setEditRsvp(null)}
          onSaved={(freshRsvp) => {
            setEditRsvp(null);
            if (freshRsvp && onRsvpUpdated) onRsvpUpdated(freshRsvp);
            if (onRefreshRsvps) onRefreshRsvps(); // also kick a full refresh in background
          }} />
      )}
      {showBadgeSheet && (
        <BadgeSheetModal event={event} rsvps={rsvps.filter(r => r.can_attend?.toLowerCase()?.includes('yes'))} onClose={() => setShowBadgeSheet(false)} />
      )}

      {waQueueRsvps && (
        <WhatsAppQueueModal event={event} rsvps={waQueueRsvps} onClose={() => setWaQueueRsvps(null)} />
      )}
    </div>
  );
}

// 4 ready-made field packs the admin can apply with one click.
// Each pack defines built_in_fields override + optional starter custom_fields.
const FIELD_PACKS = [
  {
    id: 'kids_birthday',
    label: 'Kids Birthday',
    emoji: '🎂',
    description: 'Child name, who is bringing them, dietary',
    built_in_fields: {
      child_name: { label: "Child's Name", required: true },
      parent_name: { enabled: true, label: "Parent's Name", required: false },
      parent_phone: { enabled: true, label: 'Phone Number', required: false },
      can_attend: { label: 'Can your child attend?', options: ['Yes', "Sorry, can't make it"], required: true },
      accompanying: { enabled: true, label: 'Who will be attending with your child?', options: ['Mom', 'Dad', 'Parent + Nanny', 'Nanny'], allow_other: true, required: true },
      dietary: { enabled: true, label: 'Any allergies or dietary restrictions?', options: ['Jain', 'Veg', 'Non-Veg', 'No Restrictions'], allow_other: true, required: true },
    },
    custom_fields: [],
  },
  {
    id: 'adults_cocktail',
    label: 'Adults Cocktail',
    emoji: '🍸',
    description: 'Name, +1, drink preference',
    built_in_fields: {
      child_name: { label: 'Your Name', required: true },
      parent_name: { enabled: false, label: "Parent's Name" },
      parent_phone: { enabled: true, label: 'Mobile Number', required: true },
      can_attend: { label: 'Will you attend?', options: ["Yes, I'll be there", "Can't make it"], required: true },
      accompanying: { enabled: true, label: 'Bringing a +1?', options: ['Just me', '+1'], allow_other: false, required: true },
      dietary: { enabled: false },
    },
    custom_fields: [
      { label: 'Drink preference', field_type: 'dropdown', options: ['Beer', 'Wine', 'Cocktails', 'Non-Alcoholic'], required: false, placeholder: '' },
      { label: "Plus-one's name", field_type: 'text', options: [], required: false, placeholder: 'Optional' },
    ],
  },
  {
    id: 'wedding',
    label: 'Wedding',
    emoji: '💒',
    description: 'Family name, headcount, dietary, outfit',
    built_in_fields: {
      child_name: { label: 'Family Name', required: true },
      parent_name: { enabled: true, label: 'Primary Contact Name', required: true },
      parent_phone: { enabled: true, label: 'Phone Number', required: true },
      can_attend: { label: 'Will your family attend?', options: ['With pleasure!', 'Regretfully decline'], required: true },
      accompanying: { enabled: false },
      dietary: { enabled: true, label: 'Meal Preference', options: ['Veg', 'Non-Veg', 'Jain', 'Halal', 'Vegan'], allow_other: true, required: false },
    },
    custom_fields: [
      { label: 'Total guests in your party', field_type: 'number', options: [], required: true, placeholder: 'e.g., 4' },
      { label: 'Outfit colour-coordination acknowledgement', field_type: 'yesno', options: [], required: false, placeholder: '' },
      { label: 'Need overnight accommodation?', field_type: 'yesno', options: [], required: false, placeholder: '' },
    ],
  },
  {
    id: 'corporate',
    label: 'Corporate Event',
    emoji: '💼',
    description: 'Name, company, designation, dietary',
    built_in_fields: {
      child_name: { label: 'Full Name', required: true },
      parent_name: { enabled: false },
      parent_phone: { enabled: true, label: 'Phone Number', required: false },
      can_attend: { label: 'Will you be attending?', options: ['Yes', 'No'], required: true },
      accompanying: { enabled: false },
      dietary: { enabled: true, label: 'Dietary Restrictions', options: ['Veg', 'Non-Veg', 'Vegan', 'Jain', 'Halal', 'Gluten-free'], allow_other: true, required: false },
    },
    custom_fields: [
      { label: 'Company / Organization', field_type: 'text', options: [], required: true, placeholder: 'Where do you work?' },
      { label: 'Designation', field_type: 'text', options: [], required: false, placeholder: 'Your role' },
      { label: 'Email address (for calendar invite)', field_type: 'text', options: [], required: false, placeholder: 'name@company.com' },
    ],
  },
];


function BackgroundImageUploader({ api, form, setForm, eventId }) {
  const fileRef = React.useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { setError('Image too large (4MB max)'); return; }
    setError('');

    // If we already have an event id, upload immediately so it persists.
    // Otherwise, just stash as data URL — the create flow will pick it up.
    if (eventId) {
      setBusy(true);
      const fd = new FormData();
      fd.append('file', file);
      try {
        const r = await api.post(`/events/${eventId}/background-image`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setForm(f => ({ ...f, background_image: r.data.background_image }));
      } catch (e) {
        setError(e.response?.data?.detail || 'Upload failed');
      }
      setBusy(false);
    } else {
      // Read as base64 data URL (for new events not yet saved)
      const reader = new FileReader();
      reader.onload = () => setForm(f => ({ ...f, background_image: reader.result }));
      reader.readAsDataURL(file);
    }
  };

  const clear = async () => {
    if (eventId && form.background_image) {
      try { await api.delete(`/events/${eventId}/background-image`); } catch { /* noop */ }
    }
    setForm(f => ({ ...f, background_image: '' }));
  };

  return (
    <div className="p-3 rounded-xl bg-white/[0.02] border border-white/5" data-testid="background-image-uploader">
      <p className="text-xs font-bold text-white flex items-center gap-1.5 mb-2">
        <ImageIcon size={12} weight="bold" className="text-pink-300" /> Background Image (optional)
      </p>
      <p className="text-[10px] text-neutral-500 mb-3">Upload a hero photo behind the form. We'll add a soft dark overlay so text stays readable.</p>

      {form.background_image ? (
        <div className="space-y-2">
          <div className="relative rounded-xl overflow-hidden h-32" data-testid="bg-image-preview"
            style={{ backgroundImage: `url(${form.background_image})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
            <div className="absolute inset-0" style={{ background: `rgba(0,0,0,${form.background_overlay_opacity})` }} />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-white font-bold text-sm" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.6)' }}>Preview ✨</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[10px] text-neutral-500 whitespace-nowrap">Overlay darkness</label>
            <input type="range" min="0" max="0.85" step="0.05"
              value={form.background_overlay_opacity}
              onChange={e => setForm(f => ({ ...f, background_overlay_opacity: parseFloat(e.target.value) }))}
              data-testid="bg-overlay-slider"
              className="flex-1 accent-pink-400" />
            <span className="text-[10px] text-neutral-400 font-mono w-8 text-right">{Math.round(form.background_overlay_opacity * 100)}%</span>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => fileRef.current?.click()} disabled={busy}
              data-testid="bg-image-replace-btn"
              className="flex-1 py-2 rounded-lg bg-white/5 text-neutral-300 text-[11px] font-semibold hover:bg-white/10 disabled:opacity-40">
              {busy ? 'Uploading…' : 'Replace Image'}
            </button>
            <button type="button" onClick={clear}
              data-testid="bg-image-clear-btn"
              className="px-3 py-2 rounded-lg bg-red-500/10 text-red-300 text-[11px] font-semibold hover:bg-red-500/20">
              Remove
            </button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => fileRef.current?.click()} disabled={busy}
          data-testid="bg-image-upload-btn"
          className="w-full py-6 rounded-xl bg-white/[0.03] border border-dashed border-white/15 text-neutral-400 text-xs hover:bg-white/[0.06] hover:text-white transition-colors flex flex-col items-center gap-1.5">
          {busy ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Uploading...</>) : (<><Plus size={20} weight="bold" />Click to upload (max 4MB)</>)}
        </button>
      )}
      <input ref={fileRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif"
        onChange={e => handleFile(e.target.files?.[0])}
        className="hidden" data-testid="bg-image-file-input" />
      {error && <p className="text-[10px] text-red-300 mt-1.5" data-testid="bg-image-error">{error}</p>}
    </div>
  );
}


function SlugEditor({ form, setForm }) {
  // Domain shown in preview — strip protocol so it looks clean
  const baseDomain = (() => {
    try {
      const u = new URL(typeof window !== 'undefined' ? window.location.origin : 'https://homecontrol.in');
      return u.host;
    } catch { return 'homecontrol.in'; }
  })();

  // Live preview of the slug — if not locked, auto-mirrors the title; if locked, shows the saved slug.
  const previewSlug = (() => {
    if (form.slug_locked && form.slug) return form.slug;
    if (form.slug) return form.slug;
    // auto-generated preview from title
    const s = (form.title || '').toLowerCase().replace(/\s+/g, '-').replace(/'/g, '')
      .replace(/[^a-z0-9-]/g, '').replace(/-+/g, '-').replace(/^-|-$/g, '');
    return s ? `${s.slice(0, 50)}-XXXXXX` : 'auto-generated';
  })();

  const normalizeInput = (raw) => raw
    .toLowerCase().replace(/\s+/g, '-').replace(/'/g, '')
    .replace(/[^a-z0-9-]/g, '').replace(/-+/g, '-').slice(0, 50);

  const handleSlugChange = (raw) => {
    const norm = normalizeInput(raw);
    setForm(f => ({ ...f, slug: norm, slug_locked: norm.length > 0 }));
  };

  const handleResetToAuto = () => {
    setForm(f => ({ ...f, slug: '', slug_locked: false }));
  };

  return (
    <div className="p-3 rounded-xl bg-white/[0.02] border border-white/5" data-testid="slug-editor">
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <p className="text-xs font-bold text-white flex items-center gap-1.5">
          <ShareNetwork size={12} weight="bold" className="text-blue-300" /> RSVP Link
        </p>
        {form.slug_locked ? (
          <button type="button" onClick={handleResetToAuto} data-testid="slug-reset-auto-btn"
            className="text-[10px] text-neutral-500 hover:text-white underline">
            Reset to auto
          </button>
        ) : (
          <span className="text-[9px] uppercase font-bold text-green-400 px-2 py-0.5 rounded bg-green-500/10 tracking-wider">
            Auto-syncs with title
          </span>
        )}
      </div>

      <div className="flex items-center gap-1.5">
        <span className="text-[11px] text-neutral-500 font-mono whitespace-nowrap">{baseDomain}/party/</span>
        <input type="text" value={form.slug} onChange={e => handleSlugChange(e.target.value)}
          data-testid="slug-input"
          placeholder="auto-generated from title"
          className="flex-1 min-w-0 px-2 py-1.5 rounded-lg bg-white/5 border border-white/10 text-[11px] text-white outline-none font-mono" />
      </div>

      <p className="text-[10px] text-neutral-600 mt-1.5 break-all">
        Final URL: <span className="text-blue-300 font-mono">{baseDomain}/party/{previewSlug}</span>
      </p>
      <p className="text-[10px] text-neutral-600 mt-1">
        {form.slug_locked
          ? "✓ Custom URL. Title changes won't affect this link."
          : "Tip: rename the event and this URL auto-updates. Type your own slug above to lock it."}
      </p>
    </div>
  );
}


function CustomThemeBuilder({ api, onClose, onSaved }) {
  const [tab, setTab] = useState('ai'); // 'ai' or 'manual'
  const [vibe, setVibe] = useState('');
  const [generating, setGenerating] = useState(false);
  const [aiError, setAiError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const [theme, setTheme] = useState({
    name: '',
    tagline: '',
    primary: '#FF6B35',
    accent: '#FFB85C',
    background: 'linear-gradient(135deg, #1a1a2e 0%, #0a0a0a 100%)',
    text_color: '#FFFFFF',
    muted_text: '#FFE5D6',
    emojis: ['🎉', '✨', '🎊'],
    decorations: ['confetti'],
    font_family: "'Inter', system-ui, sans-serif",
  });

  const FONT_OPTIONS = [
    { id: "'Inter', system-ui, sans-serif", label: 'Modern (Inter)' },
    { id: "'Poppins', sans-serif", label: 'Friendly (Poppins)' },
    { id: "'Fredoka', 'Comic Neue', cursive", label: 'Playful (Fredoka)' },
    { id: "'Bangers', cursive", label: 'Bold (Bangers)' },
    { id: "'Playfair Display', serif", label: 'Elegant (Playfair)' },
    { id: "'Cormorant Garamond', serif", label: 'Royal (Cormorant)' },
    { id: "'Dancing Script', cursive", label: 'Script (Dancing)' },
    { id: "'Quicksand', sans-serif", label: 'Soft (Quicksand)' },
  ];
  const DECORATION_OPTIONS = ['confetti', 'sparkles', 'waves', 'hearts', 'polka', 'stars', 'paw-prints', 'leaves'];
  const EMOJI_QUICK = ['🎉','✨','🎊','💖','🎂','🌸','🌺','🪔','🎆','🎈','🪅','🎁','🎵','💫','⭐','🌟','💃','🕺','👑','🤴','🌼','🍰','🥂','🎀','🌹','🦄','🐾','🐝','🦋','🌈','☁️','🌙','🍾','🎄','🎃','🪐'];

  const generateFromVibe = async () => {
    if (!vibe.trim()) { setAiError('Describe the vibe first'); return; }
    setGenerating(true);
    setAiError('');
    try {
      const r = await api.post('/events/themes/ai-generate', { vibe: vibe.trim() });
      const d = r.data;
      setTheme(t => ({
        ...t,
        name: d.name || t.name || 'Custom Theme',
        tagline: d.tagline || '',
        primary: d.primary,
        accent: d.accent,
        background: d.background,
        text_color: d.text_color,
        muted_text: d.muted_text,
        emojis: d.emojis,
        decorations: d.decorations,
        font_family: d.font_family,
      }));
      setTab('manual');
    } catch (e) {
      setAiError(e.response?.data?.detail || 'AI generation failed');
    }
    setGenerating(false);
  };

  const toggleEmoji = (e) => {
    setTheme(t => {
      const has = t.emojis.includes(e);
      if (has) return { ...t, emojis: t.emojis.filter(x => x !== e) };
      if (t.emojis.length >= 5) return t;
      return { ...t, emojis: [...t.emojis, e] };
    });
  };
  const toggleDecoration = (d) => {
    setTheme(t => {
      const has = t.decorations.includes(d);
      if (has) return { ...t, decorations: t.decorations.filter(x => x !== d) };
      if (t.decorations.length >= 2) return { ...t, decorations: [t.decorations[1] || t.decorations[0], d] };
      return { ...t, decorations: [...t.decorations, d] };
    });
  };

  const save = async () => {
    if (!theme.name.trim()) { setSaveError('Theme needs a name'); return; }
    setSaving(true);
    setSaveError('');
    try {
      const r = await api.post('/events/themes/custom', theme);
      onSaved(r.data);
    } catch (e) {
      setSaveError(e.response?.data?.detail || 'Save failed');
      setSaving(false);
    }
  };

  // Live preview block uses theme directly
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[70] flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="glass-card w-full max-w-2xl my-8 p-6 animate-fade-up" data-testid="custom-theme-builder-modal" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-base font-bold text-white flex items-center gap-2">
            <Sparkle size={18} weight="fill" className="text-purple-300" /> Create Custom Theme
          </h3>
          <button onClick={onClose} className="text-neutral-500 hover:text-white"><X size={18} /></button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1 rounded-xl mb-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <button type="button" onClick={() => setTab('ai')} data-testid="theme-builder-tab-ai"
            className={`flex-1 px-3 py-2 rounded-lg text-[11px] font-semibold transition-all flex items-center justify-center gap-1.5 ${tab === 'ai' ? 'bg-purple-500/15 text-purple-300' : 'text-neutral-400 hover:text-white hover:bg-white/5'}`}>
            <MagicWand size={11} weight="fill" /> AI Suggest
          </button>
          <button type="button" onClick={() => setTab('manual')} data-testid="theme-builder-tab-manual"
            className={`flex-1 px-3 py-2 rounded-lg text-[11px] font-semibold transition-all ${tab === 'manual' ? 'bg-purple-500/15 text-purple-300' : 'text-neutral-400 hover:text-white hover:bg-white/5'}`}>
            Builder
          </button>
        </div>

        {tab === 'ai' && (
          <div className="mb-4 p-4 rounded-xl space-y-3" style={{ background: 'rgba(168,85,247,0.06)', border: '1px solid rgba(168,85,247,0.22)' }} data-testid="theme-ai-tab">
            <p className="text-[11px] text-purple-200">
              Describe the vibe in your words — examples: <span className="text-white/80">"Bollywood retro disco"</span>, <span className="text-white/80">"Royal Rajasthani mehendi"</span>, <span className="text-white/80">"Cricket world cup final viewing party"</span>, <span className="text-white/80">"Pastel pink ballet recital"</span>.
            </p>
            <textarea value={vibe} onChange={e => setVibe(e.target.value)} maxLength={240}
              data-testid="theme-ai-vibe-input"
              placeholder="Describe the party vibe..."
              className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none resize-none"
              rows={3} />
            {aiError && <p className="text-[11px] text-red-300" data-testid="theme-ai-error">{aiError}</p>}
            <button type="button" onClick={generateFromVibe} disabled={generating || !vibe.trim()}
              data-testid="theme-ai-generate-btn"
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-purple-500 hover:bg-purple-600 text-white text-xs font-bold disabled:opacity-40">
              {generating ? (<><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Generating...</>) : (<><MagicWand size={12} weight="fill" /> Paint My Theme</>)}
            </button>
          </div>
        )}

        {tab === 'manual' && (
          <div className="space-y-4" data-testid="theme-manual-tab">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-neutral-500 mb-1 block font-semibold">Theme Name *</label>
                <input type="text" value={theme.name} onChange={e => setTheme(t => ({ ...t, name: e.target.value }))} maxLength={60}
                  data-testid="theme-builder-name" placeholder="e.g., Diwali Glow"
                  className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none" />
              </div>
              <div>
                <label className="text-[10px] text-neutral-500 mb-1 block font-semibold">Tagline</label>
                <input type="text" value={theme.tagline} onChange={e => setTheme(t => ({ ...t, tagline: e.target.value }))} maxLength={80}
                  data-testid="theme-builder-tagline" placeholder="Short subtitle"
                  className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none" />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              {[
                { key: 'primary', label: 'Primary' },
                { key: 'accent', label: 'Accent' },
                { key: 'text_color', label: 'Text' },
              ].map(({ key, label }) => (
                <div key={key}>
                  <label className="text-[10px] text-neutral-500 mb-1 block font-semibold">{label}</label>
                  <div className="flex items-center gap-1.5">
                    <input type="color" value={/^#[0-9A-Fa-f]{6}$/.test(theme[key]) ? theme[key] : '#FF6B35'}
                      onChange={e => setTheme(t => ({ ...t, [key]: e.target.value }))}
                      data-testid={`theme-color-${key}`}
                      className="w-9 h-9 rounded-lg bg-transparent border border-white/10 cursor-pointer" />
                    <input type="text" value={theme[key]} onChange={e => setTheme(t => ({ ...t, [key]: e.target.value }))}
                      className="flex-1 px-2 py-1.5 rounded-lg bg-white/5 border border-white/10 text-[11px] text-white outline-none font-mono" />
                  </div>
                </div>
              ))}
            </div>

            <div>
              <label className="text-[10px] text-neutral-500 mb-1 block font-semibold">Background (hex or CSS gradient)</label>
              <input type="text" value={theme.background} onChange={e => setTheme(t => ({ ...t, background: e.target.value }))}
                data-testid="theme-background-input"
                placeholder="linear-gradient(135deg, #color1 0%, #color2 100%)"
                className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-[11px] text-white outline-none font-mono" />
            </div>

            <div>
              <label className="text-[10px] text-neutral-500 mb-1 block font-semibold">Font</label>
              <select value={theme.font_family} onChange={e => setTheme(t => ({ ...t, font_family: e.target.value }))}
                data-testid="theme-font-select"
                className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-xs text-white outline-none">
                {FONT_OPTIONS.map(f => <option key={f.id} value={f.id} style={{ background: '#0a0a0a', color: 'white', fontFamily: f.id }}>{f.label}</option>)}
              </select>
            </div>

            <div>
              <label className="text-[10px] text-neutral-500 mb-1 block font-semibold">Floating Emojis (up to 5) — {theme.emojis.length}/5</label>
              <div className="flex flex-wrap gap-1 mb-2">
                {theme.emojis.map(e => (
                  <button key={e} type="button" onClick={() => toggleEmoji(e)}
                    className="px-2 py-1 rounded-lg bg-purple-500/30 text-white text-sm hover:bg-purple-500/50">
                    {e} <span className="text-[9px] text-purple-200">×</span>
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto p-2 rounded-lg bg-black/20">
                {EMOJI_QUICK.map(e => (
                  <button key={e} type="button" onClick={() => toggleEmoji(e)}
                    data-testid={`theme-emoji-${e}`}
                    disabled={theme.emojis.includes(e) || theme.emojis.length >= 5}
                    className={`w-7 h-7 rounded-md flex items-center justify-center text-sm transition-all ${theme.emojis.includes(e) ? 'bg-purple-500/30 opacity-50' : 'hover:bg-white/10'}`}>
                    {e}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-[10px] text-neutral-500 mb-1 block font-semibold">Particle Effect (max 2)</label>
              <div className="flex flex-wrap gap-1.5">
                {DECORATION_OPTIONS.map(d => (
                  <button key={d} type="button" onClick={() => toggleDecoration(d)}
                    data-testid={`theme-decor-${d}`}
                    className={`px-2.5 py-1 rounded-lg text-[10px] font-semibold transition-all ${theme.decorations.includes(d) ? 'bg-purple-500/30 text-white border border-purple-400' : 'bg-white/5 text-neutral-400 border border-white/5 hover:bg-white/10'}`}>
                    {d}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Live Preview */}
        <div className="mt-4 mb-4 p-4 rounded-xl relative overflow-hidden" style={{ background: theme.background, fontFamily: theme.font_family, border: '1px solid rgba(255,255,255,0.08)' }} data-testid="theme-builder-preview">
          <div className="absolute top-2 left-2 text-[9px] uppercase tracking-wider font-bold text-white/40">Live preview</div>
          <div className="text-center py-2">
            <div className="flex justify-center gap-1.5 mb-2">
              {theme.emojis.slice(0, 5).map((e, i) => <span key={i} style={{ fontSize: 22 + i * 2, opacity: 0.8 - i * 0.1 }}>{e}</span>)}
            </div>
            <h4 className="text-lg font-bold" style={{ color: theme.text_color, fontFamily: theme.font_family }}>
              {theme.name || 'Theme name preview'}
            </h4>
            {theme.tagline && <p className="text-xs mt-1" style={{ color: theme.muted_text }}>{theme.tagline}</p>}
            <button type="button"
              className="mt-3 px-4 py-2 rounded-xl text-white text-xs font-bold"
              style={{ background: `linear-gradient(135deg, ${theme.primary}, ${theme.accent})`, boxShadow: `0 4px 12px ${theme.primary}40` }}>
              Submit RSVP
            </button>
          </div>
        </div>

        {saveError && <p className="text-[11px] text-red-300 mb-2" data-testid="theme-save-error">{saveError}</p>}

        <div className="flex gap-2">
          <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-white/5 text-neutral-300 text-xs font-semibold hover:bg-white/10">
            Cancel
          </button>
          <button type="button" onClick={save} disabled={saving || !theme.name.trim()}
            data-testid="theme-save-btn"
            className="flex-1 py-2.5 rounded-xl bg-purple-500 hover:bg-purple-600 text-white text-xs font-bold disabled:opacity-40">
            {saving ? 'Saving...' : 'Save Theme'}
          </button>
        </div>
      </div>
    </div>
  );
}


function DuplicateEventModal({ api, source, onClose, onDuplicated }) {
  // Pre-fill title with " (Copy)" suffix
  const defaultTitle = source ? `${source.title} (Copy)` : '';
  // Pre-fill date with next-year-same-day
  const defaultDate = (() => {
    if (!source?.event_date) return '';
    try {
      const d = new Date(source.event_date + 'T00:00:00');
      d.setFullYear(d.getFullYear() + 1);
      return d.toISOString().split('T')[0];
    } catch { return source.event_date; }
  })();

  const [title, setTitle] = useState(defaultTitle);
  const [eventDate, setEventDate] = useState(defaultDate);
  const [copyInvitees, setCopyInvitees] = useState(false);
  const [copyImage, setCopyImage] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!title.trim()) { setError('Please provide a title'); return; }
    setBusy(true);
    setError('');
    try {
      const r = await api.post(`/events/${source.event_id}/duplicate`, {
        title: title.trim(),
        event_date: eventDate,
        copy_invitees: copyInvitees,
        copy_invitation_image: copyImage,
      });
      onDuplicated(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not duplicate event');
      setBusy(false);
    }
  };

  const customFieldsCount = (source?.custom_fields || []).length;
  const builtinOverridesCount = Object.keys(source?.built_in_fields || {}).length;
  const hasTemplate = !!(source?.invitation_template || source?.reminder_template);
  const hasInviteImage = !!source?.invitation_image;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="glass-card w-full max-w-md p-6 animate-fade-up" data-testid="duplicate-event-modal" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-base font-bold text-white flex items-center gap-2">
            <CopySimple size={18} weight="fill" className="text-blue-300" /> Duplicate Event
          </h3>
          <button onClick={onClose} className="text-neutral-500 hover:text-white"><X size={18} /></button>
        </div>

        <p className="text-[11px] text-neutral-500 mb-4">
          Create a clean copy of <span className="text-white font-semibold">"{source.title}"</span> as a new <span className="text-yellow-400 font-semibold">draft</span> event. No RSVPs, fresh slug, ready for next time.
        </p>

        <div className="space-y-3">
          <div>
            <label className="text-xs text-neutral-500 mb-1 block">New title *</label>
            <input type="text" value={title} onChange={e => setTitle(e.target.value)} maxLength={120}
              data-testid="duplicate-title-input"
              autoFocus
              className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none" />
          </div>
          <div>
            <label className="text-xs text-neutral-500 mb-1 block">New date</label>
            <input type="date" value={eventDate} onChange={e => setEventDate(e.target.value)}
              data-testid="duplicate-date-input"
              className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none" />
            <p className="text-[10px] text-neutral-600 mt-1">Defaults to one year after the original event.</p>
          </div>

          {/* What is always copied */}
          <div className="p-3 rounded-xl bg-white/[0.02] border border-white/5">
            <p className="text-[10px] font-bold text-green-400 mb-2 uppercase tracking-wider">Always copied</p>
            <ul className="space-y-1 text-[11px] text-neutral-300">
              <li className="flex items-center gap-1.5"><Check size={11} weight="bold" className="text-green-400" />Theme, cover &amp; thank-you messages, branding footer</li>
              <li className="flex items-center gap-1.5"><Check size={11} weight="bold" className="text-green-400" />{customFieldsCount} custom field{customFieldsCount === 1 ? '' : 's'} + {builtinOverridesCount} built-in override{builtinOverridesCount === 1 ? '' : 's'}</li>
              {hasTemplate && <li className="flex items-center gap-1.5"><Check size={11} weight="bold" className="text-green-400" />WhatsApp invitation &amp; reminder templates</li>}
            </ul>
          </div>

          {/* Optional copies */}
          <div className="p-3 rounded-xl bg-white/[0.02] border border-white/5 space-y-2">
            <p className="text-[10px] font-bold text-blue-300 mb-1 uppercase tracking-wider">Optional</p>
            <label className="flex items-start gap-2 text-[11px] text-neutral-300 cursor-pointer">
              <input type="checkbox" checked={copyInvitees} onChange={e => setCopyInvitees(e.target.checked)}
                data-testid="duplicate-copy-invitees"
                className="w-3.5 h-3.5 rounded accent-blue-400 mt-0.5" />
              <span>
                Copy invitee list (names &amp; phones) <span className="text-neutral-600">— RSVPs &amp; send history are NOT copied</span>
              </span>
            </label>
            {hasInviteImage && (
              <label className="flex items-start gap-2 text-[11px] text-neutral-300 cursor-pointer">
                <input type="checkbox" checked={copyImage} onChange={e => setCopyImage(e.target.checked)}
                  data-testid="duplicate-copy-image"
                  className="w-3.5 h-3.5 rounded accent-blue-400 mt-0.5" />
                <span>Copy AI-generated invitation image <span className="text-neutral-600">— useful if same theme</span></span>
              </label>
            )}
          </div>

          {error && <p className="text-[11px] text-red-300" data-testid="duplicate-error">{error}</p>}

          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-white/5 text-neutral-300 text-xs font-semibold hover:bg-white/10">
              Cancel
            </button>
            <button type="button" onClick={submit} disabled={busy || !title.trim()}
              data-testid="duplicate-confirm-btn"
              className="flex-1 py-2.5 rounded-xl bg-blue-500 hover:bg-blue-600 text-white text-xs font-bold disabled:opacity-40 flex items-center justify-center gap-1.5">
              {busy ? 'Duplicating...' : (<>
                <CopySimple size={12} weight="fill" /> Duplicate
              </>)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


function SavePackModal({ api, form, onClose, onSaved }) {
  const [name, setName] = useState('');
  const [emoji, setEmoji] = useState('✨');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const EMOJI_CHOICES = ['✨', '🎂', '🎉', '🍸', '💒', '💼', '🪔', '🎄', '🎃', '🕉️', '☕', '🎊', '🥂', '🍰'];

  const save = async () => {
    if (!name.trim()) { setError('Please name your pack'); return; }
    setSaving(true);
    setError('');
    try {
      const r = await api.post('/events/field-packs/custom', {
        name: name.trim(),
        emoji,
        description: description.trim(),
        built_in_fields: form.built_in_fields || {},
        custom_fields: form.custom_fields || [],
      });
      onSaved(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Save failed');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4" onClick={onClose}>
      <div className="glass-card w-full max-w-md p-6 animate-fade-up" data-testid="save-pack-modal" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-base font-bold text-white flex items-center gap-2">
            <FloppyDisk size={18} weight="fill" className="text-purple-300" /> Save as Field Pack
          </h3>
          <button onClick={onClose} className="text-neutral-500 hover:text-white"><X size={18} /></button>
        </div>

        <p className="text-[11px] text-neutral-500 mb-4">
          Reuse this exact form setup ({Object.keys(form.built_in_fields || {}).length} built-in overrides + {(form.custom_fields || []).length} custom fields) for future events.
        </p>

        <div className="space-y-3">
          <div>
            <label className="text-xs text-neutral-500 mb-1 block">Pack name *</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} maxLength={60}
              data-testid="save-pack-name-input"
              placeholder="e.g., My Diwali Get-Together"
              autoFocus
              className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none" />
          </div>
          <div>
            <label className="text-xs text-neutral-500 mb-1 block">Emoji</label>
            <div className="flex flex-wrap gap-1.5">
              {EMOJI_CHOICES.map(e => (
                <button key={e} type="button" onClick={() => setEmoji(e)}
                  data-testid={`save-pack-emoji-${e}`}
                  className={`w-9 h-9 rounded-lg flex items-center justify-center text-base transition-all ${emoji === e ? 'bg-purple-500/30 ring-2 ring-purple-400' : 'bg-white/5 hover:bg-white/10'}`}>
                  {e}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs text-neutral-500 mb-1 block">Description (optional)</label>
            <input type="text" value={description} onChange={e => setDescription(e.target.value)} maxLength={120}
              data-testid="save-pack-description-input"
              placeholder="Brief reminder of what this pack collects"
              className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none" />
          </div>

          {error && (
            <p className="text-[11px] text-red-300" data-testid="save-pack-error">{error}</p>
          )}

          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-white/5 text-neutral-300 text-xs font-semibold hover:bg-white/10">
              Cancel
            </button>
            <button type="button" onClick={save} disabled={saving || !name.trim()}
              data-testid="save-pack-confirm-btn"
              className="flex-1 py-2.5 rounded-xl bg-purple-500 hover:bg-purple-600 text-white text-xs font-bold disabled:opacity-40">
              {saving ? 'Saving...' : 'Save Pack'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


function BuiltInFieldsEditor({ form, setForm, api }) {
  const bif = form.built_in_fields || {};
  const [showPacks, setShowPacks] = useState(false);
  const [customPacks, setCustomPacks] = useState([]);
  const [showSaveModal, setShowSaveModal] = useState(false);

  const fetchCustomPacks = React.useCallback(async () => {
    if (!api) return;
    try {
      const r = await api.get('/events/field-packs/custom');
      setCustomPacks(r.data.packs || []);
    } catch (e) { /* noop */ }
  }, [api]);

  useEffect(() => { fetchCustomPacks(); }, [fetchCustomPacks]);

  const applyPack = (pack) => {
    const hasData = Object.keys(form.built_in_fields || {}).length > 0 || (form.custom_fields || []).length > 0;
    if (hasData) {
      const ok = window.confirm(
        `Apply "${pack.label || pack.name}" field pack?\n\n` +
        `This will REPLACE all current built-in field settings and custom fields with this preset.\n\n` +
        `Continue?`
      );
      if (!ok) return;
    }
    const customWithIds = (pack.custom_fields || []).map(cf => ({
      ...cf,
      field_id: `cf_${Math.random().toString(36).slice(2, 10)}`,
    }));
    setForm(f => ({
      ...f,
      built_in_fields: pack.built_in_fields || {},
      custom_fields: customWithIds,
    }));
    setShowPacks(false);
  };

  const deleteCustomPack = async (packId, name) => {
    if (!window.confirm(`Delete saved pack "${name}"?`)) return;
    try {
      await api.delete(`/events/field-packs/custom/${packId}`);
      setCustomPacks(p => p.filter(x => x.pack_id !== packId));
    } catch (e) { /* noop */ }
  };

  const handlePackSaved = (newPack) => {
    setCustomPacks(p => [newPack, ...p]);
    setShowSaveModal(false);
  };

  // Defaults mirror backend DEFAULT_BIRTHDAY_FIELDS so we render placeholders consistently.
  const DEFAULTS = {
    child_name: { label: "Child's Name", required: true, removable: false, hasOptions: false, supportsOther: false, kind: 'text' },
    parent_name: { label: "Parent's Name", required: false, removable: true, hasOptions: false, supportsOther: false, kind: 'text' },
    parent_phone: { label: "Phone Number", required: false, removable: true, hasOptions: false, supportsOther: false, kind: 'text' },
    can_attend: { label: "Can your child attend?", required: true, removable: false, hasOptions: true, supportsOther: false, kind: 'choice', defaultOptions: ['Yes', "Sorry, can't make it"] },
    accompanying: { label: "Who will be attending with your child?", required: true, removable: true, hasOptions: true, supportsOther: true, kind: 'choice', defaultOptions: ['Mom', 'Dad', 'Parent + Nanny', 'Nanny'] },
    dietary: { label: "Any allergies or dietary restrictions?", required: true, removable: true, hasOptions: true, supportsOther: true, kind: 'choice', defaultOptions: ['Jain', 'Veg', 'Non-Veg', 'No Restrictions'] },
  };

  const getValue = (key, prop, fallback) => {
    const v = bif[key]?.[prop];
    return v === undefined ? fallback : v;
  };

  const setField = (key, patch) => {
    setForm(f => ({
      ...f,
      built_in_fields: {
        ...(f.built_in_fields || {}),
        [key]: { ...(f.built_in_fields?.[key] || {}), ...patch },
      },
    }));
  };

  const resetField = (key) => {
    setForm(f => {
      const next = { ...(f.built_in_fields || {}) };
      delete next[key];
      return { ...f, built_in_fields: next };
    });
  };

  return (
    <div className="border-t border-white/5 pt-4 mt-4" data-testid="builtin-fields-editor">
      <div className="mb-3 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h4 className="text-sm font-bold text-white flex items-center gap-2">
            <PencilSimple size={14} weight="bold" className="text-[#FF6B35]" /> Built-in Form Fields
          </h4>
          <p className="text-[10px] text-neutral-500 mt-0.5">Rename, hide, or customize the standard RSVP fields. Child Name & Can Attend are required for every event.</p>
        </div>
        <button type="button" onClick={() => setShowPacks(!showPacks)} data-testid="field-packs-toggle"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-purple-500/15 text-purple-300 text-xs font-semibold hover:bg-purple-500/25">
          <Sparkle size={11} weight="fill" /> Field Packs
        </button>
      </div>

      {/* Field Pack Picker */}
      {showPacks && (
        <div className="mb-4 p-3 rounded-xl space-y-3" style={{ background: 'linear-gradient(135deg, rgba(168,85,247,0.06), rgba(255,107,53,0.04))', border: '1px solid rgba(168,85,247,0.22)' }} data-testid="field-packs-panel">
          <div className="flex items-center gap-1.5 mb-1">
            <Sparkle size={12} weight="fill" className="text-purple-300" />
            <span className="text-[11px] font-bold text-purple-200">Pick a preset to populate the entire form</span>
            <span className="text-[10px] text-neutral-500 ml-auto">Replaces current settings</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {FIELD_PACKS.map(pack => (
              <button key={pack.id} type="button" onClick={() => applyPack(pack)}
                data-testid={`field-pack-${pack.id}`}
                className="text-left p-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/5 hover:border-purple-400/40 transition-all group">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-base">{pack.emoji}</span>
                  <span className="text-sm font-bold text-white group-hover:text-purple-200">{pack.label}</span>
                </div>
                <p className="text-[10px] text-neutral-500 leading-snug">{pack.description}</p>
                <p className="text-[9px] text-neutral-600 mt-1">
                  {(pack.custom_fields || []).length} custom field{(pack.custom_fields || []).length === 1 ? '' : 's'} included
                </p>
              </button>
            ))}
          </div>

          {/* Custom user packs */}
          {customPacks.length > 0 && (
            <div className="pt-2 border-t border-white/5">
              <p className="text-[10px] font-bold text-purple-300 mb-2 uppercase tracking-wider">Your saved packs</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {customPacks.map(pack => (
                  <div key={pack.pack_id} className="relative group" data-testid={`custom-pack-${pack.pack_id}`}>
                    <button type="button" onClick={() => applyPack(pack)}
                      data-testid={`custom-pack-apply-${pack.pack_id}`}
                      className="w-full text-left p-3 rounded-xl bg-purple-500/5 hover:bg-purple-500/15 border border-purple-400/20 hover:border-purple-400/50 transition-all">
                      <div className="flex items-center gap-2 mb-1 pr-7">
                        <span className="text-base">{pack.emoji}</span>
                        <span className="text-sm font-bold text-white truncate">{pack.name}</span>
                      </div>
                      {pack.description && <p className="text-[10px] text-neutral-500 leading-snug">{pack.description}</p>}
                      <p className="text-[9px] text-neutral-600 mt-1">
                        {(pack.custom_fields || []).length} custom field{(pack.custom_fields || []).length === 1 ? '' : 's'} · saved
                      </p>
                    </button>
                    <button type="button" onClick={() => deleteCustomPack(pack.pack_id, pack.name)}
                      data-testid={`custom-pack-delete-${pack.pack_id}`}
                      title="Delete saved pack"
                      className="absolute top-2 right-2 p-1.5 rounded-lg bg-black/30 hover:bg-red-500/30 text-neutral-400 hover:text-red-300 opacity-0 group-hover:opacity-100 transition-all">
                      <Trash size={11} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Save current */}
          <div className="pt-2 border-t border-white/5">
            <button type="button" onClick={() => setShowSaveModal(true)}
              disabled={Object.keys(form.built_in_fields || {}).length === 0 && (form.custom_fields || []).length === 0}
              data-testid="save-current-pack-btn"
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-purple-500 hover:bg-purple-600 text-white text-xs font-bold disabled:opacity-30 disabled:cursor-not-allowed">
              <FloppyDisk size={12} weight="fill" /> Save Current as Pack
            </button>
            <p className="text-[9px] text-neutral-600 text-center mt-1.5">
              Save this form's setup to reuse on future events
            </p>
          </div>
        </div>
      )}

      {showSaveModal && (
        <SavePackModal api={api} form={form} onClose={() => setShowSaveModal(false)} onSaved={handlePackSaved} />
      )}

      <div className="space-y-3">
        {Object.entries(DEFAULTS).map(([key, meta]) => {
          const enabled = getValue(key, 'enabled', true);
          const label = getValue(key, 'label', meta.label);
          const required = getValue(key, 'required', meta.required);
          const options = getValue(key, 'options', meta.defaultOptions || []);
          const allowOther = getValue(key, 'allow_other', true);
          const isHidden = meta.removable && enabled === false;

          return (
            <div key={key} className={`rounded-xl p-3 transition-all ${isHidden ? 'opacity-50' : ''}`}
              style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}
              data-testid={`builtin-field-${key}`}>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="text-[9px] uppercase font-bold tracking-wider text-neutral-500 px-2 py-0.5 rounded bg-white/5">
                  {meta.kind === 'choice' ? 'Choice' : 'Text'}
                </span>
                {!meta.removable && (
                  <span className="text-[9px] uppercase font-bold tracking-wider text-[#FF6B35] px-2 py-0.5 rounded bg-[#FF6B35]/10">
                    Locked
                  </span>
                )}
                <input type="text" value={label} onChange={e => setField(key, { label: e.target.value })}
                  data-testid={`builtin-label-${key}`}
                  placeholder={meta.label}
                  className="flex-1 min-w-[160px] px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs text-white outline-none"
                  disabled={isHidden} />
                {meta.removable && (
                  <button type="button" onClick={() => setField(key, { enabled: !enabled })}
                    data-testid={`builtin-toggle-${key}`}
                    title={enabled ? 'Hide this field on the RSVP form' : 'Show this field on the RSVP form'}
                    className={`px-2.5 py-1.5 rounded-lg text-[11px] font-semibold transition-colors ${enabled ? 'bg-green-500/15 text-green-300 hover:bg-green-500/25' : 'bg-red-500/15 text-red-300 hover:bg-red-500/25'}`}>
                    {enabled ? 'Visible' : 'Hidden'}
                  </button>
                )}
                {!meta.removable && (
                  <span className="px-2.5 py-1.5 rounded-lg text-[11px] font-semibold bg-green-500/10 text-green-400">Visible</span>
                )}
              </div>

              {!isHidden && (
                <div className="space-y-2">
                  {/* Required toggle (locked fields always required) */}
                  <label className="flex items-center gap-2 text-[11px] text-neutral-300 cursor-pointer">
                    <input type="checkbox" checked={!!required} disabled={!meta.removable}
                      onChange={e => setField(key, { required: e.target.checked })}
                      data-testid={`builtin-required-${key}`}
                      className="w-3.5 h-3.5 rounded accent-[#FF6B35]" />
                    Required field
                    {!meta.removable && <span className="text-[9px] text-neutral-600 ml-1">(can't be changed)</span>}
                  </label>

                  {/* Options editor (choice fields) */}
                  {meta.hasOptions && (
                    <BuiltInOptionsEditor
                      options={options}
                      onChange={(opts) => setField(key, { options: opts })}
                      fieldKey={key}
                    />
                  )}

                  {/* Allow "Other" (only choice fields that support it) */}
                  {meta.hasOptions && meta.supportsOther && (
                    <label className="flex items-center gap-2 text-[11px] text-neutral-300 cursor-pointer">
                      <input type="checkbox" checked={allowOther}
                        onChange={e => setField(key, { allow_other: e.target.checked })}
                        data-testid={`builtin-allow-other-${key}`}
                        className="w-3.5 h-3.5 rounded accent-[#FF6B35]" />
                      Show "Other (specify)" free-text input
                    </label>
                  )}

                  {/* Reset to default */}
                  {bif[key] && Object.keys(bif[key]).length > 0 && (
                    <button type="button" onClick={() => resetField(key)} data-testid={`builtin-reset-${key}`}
                      className="text-[10px] text-neutral-500 hover:text-white underline">
                      Reset to default
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


function BuiltInOptionsEditor({ options, onChange, fieldKey }) {
  const [draft, setDraft] = useState('');
  const list = Array.isArray(options) ? options : [];

  const addOption = () => {
    const v = draft.trim();
    if (!v || list.includes(v)) return;
    onChange([...list, v]);
    setDraft('');
  };
  const removeOption = (opt) => onChange(list.filter(o => o !== opt));

  return (
    <div>
      <p className="text-[10px] text-neutral-500 mb-1.5 font-semibold">Options</p>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {list.map(opt => (
          <span key={opt} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-white/5 text-[11px] text-white border border-white/10"
            data-testid={`builtin-option-${fieldKey}-${opt.replace(/[^a-z0-9]/gi, '')}`}>
            {opt}
            <button type="button" onClick={() => removeOption(opt)}
              className="text-neutral-500 hover:text-red-400 ml-0.5" title="Remove option">
              <X size={10} weight="bold" />
            </button>
          </span>
        ))}
        {list.length === 0 && (
          <span className="text-[10px] text-neutral-600 italic">No options yet — add at least one.</span>
        )}
      </div>
      <div className="flex gap-1.5">
        <input type="text" value={draft} onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addOption(); } }}
          data-testid={`builtin-option-input-${fieldKey}`}
          placeholder="Add option…"
          className="flex-1 px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 text-[11px] text-white outline-none" />
        <button type="button" onClick={addOption} disabled={!draft.trim()}
          data-testid={`builtin-option-add-${fieldKey}`}
          className="px-3 py-1.5 rounded-lg bg-[#FF6B35] text-white text-[11px] font-semibold disabled:opacity-40">
          Add
        </button>
      </div>
    </div>
  );
}


function WhatsAppTemplateDesigner({ api, form, setForm }) {
  const [tab, setTab] = useState('invitation');
  const [presets, setPresets] = useState([]);
  const [showPresets, setShowPresets] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get('/events/templates/presets')
      .then(r => { if (!cancelled) setPresets(r.data.presets || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [api]);

  // Sample invitee for live preview
  const sample = { name: 'Aarav Sharma', phone: '+919876543210' };
  const vars = {
    first_name: 'Aarav',
    title: form.title || 'Your Event',
    date: form.event_date ? new Date(form.event_date + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : 'Date',
    time: form.event_time + (form.end_time ? ` – ${form.end_time}` : ''),
    venue: form.venue || 'Venue',
    emoji: form.theme_emoji || '🎉',
  };

  const renderTemplate = (tpl) => {
    let out = (tpl || '').toString();
    Object.entries(vars).forEach(([k, v]) => { out = out.replaceAll('{' + k + '}', String(v || '')); });
    return out.replace('[link]', `https://homecontrol.in/party/<slug>`);
  };

  const currentKey = tab === 'invitation' ? 'invitation_template' : 'reminder_template';
  const currentValue = form[currentKey] || '';
  const defaultHint = tab === 'invitation'
    ? 'Leave empty to use the default warm invitation message.'
    : 'Leave empty to use the default friendly reminder message.';
  const previewText = renderTemplate(currentValue || (tab === 'invitation' ? presets.find(p => p.id === 'warm')?.invitation : presets.find(p => p.id === 'warm')?.reminder) || '');

  const applyPreset = (preset) => {
    setForm(f => ({ ...f, [currentKey]: preset[tab] }));
    setShowPresets(false);
  };

  const insertPlaceholder = (ph) => {
    setForm(f => ({ ...f, [currentKey]: (f[currentKey] || '') + ph }));
  };

  const clearOverride = () => setForm(f => ({ ...f, [currentKey]: '' }));

  return (
    <div className="border-t border-white/5 pt-4 mt-4" data-testid="whatsapp-template-designer">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <WhatsappLogo size={16} weight="fill" className="text-green-400" />
          <div>
            <h4 className="text-sm font-bold text-white">WhatsApp Templates</h4>
            <p className="text-[10px] text-neutral-500 mt-0.5">Customize the message guests receive</p>
          </div>
        </div>
        <button type="button" onClick={() => setShowPresets(!showPresets)} data-testid="template-tone-toggle"
          className="flex items-center gap-1 px-3 py-1.5 rounded-xl bg-purple-500/15 text-purple-300 text-xs font-semibold hover:bg-purple-500/25">
          <Sparkle size={11} weight="fill" /> Tone Presets
        </button>
      </div>

      {/* Tab toggle: invitation / reminder */}
      <div className="flex gap-1 p-1 rounded-xl mb-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
        {[
          { id: 'invitation', label: 'First Invitation' },
          { id: 'reminder', label: 'Reminder' },
        ].map(t => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} data-testid={`template-tab-${t.id}`}
            className={`flex-1 px-3 py-2 rounded-lg text-[11px] font-semibold transition-all ${tab === t.id ? 'bg-green-500/15 text-green-300' : 'text-neutral-400 hover:text-white hover:bg-white/5'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Preset chooser */}
      {showPresets && (
        <div className="mb-3 p-3 rounded-xl" style={{ background: 'rgba(168,85,247,0.06)', border: '1px solid rgba(168,85,247,0.18)' }} data-testid="template-preset-panel">
          <p className="text-[10px] text-purple-200 mb-2 font-bold">Pick a tone to overwrite the {tab === 'invitation' ? 'invitation' : 'reminder'} template:</p>
          <div className="grid grid-cols-3 gap-2">
            {presets.map(p => (
              <button key={p.id} type="button" onClick={() => applyPreset(p)}
                data-testid={`preset-${p.id}`}
                className="px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-xs font-semibold text-white border border-white/5">
                {p.id === 'warm' && '💛 '}{p.id === 'formal' && '🎩 '}{p.id === 'playful' && '🎈 '}{p.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Editor + Preview side by side on desktop, stacked on mobile */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] text-neutral-500 mb-1 block flex items-center justify-between">
            <span>Template (Markdown · *bold*, _italic_)</span>
            {currentValue && (
              <button type="button" onClick={clearOverride} data-testid="template-clear-btn"
                className="text-[10px] text-neutral-500 hover:text-white">Reset to default</button>
            )}
          </label>
          <textarea
            value={currentValue}
            onChange={e => setForm(f => ({ ...f, [currentKey]: e.target.value }))}
            placeholder={defaultHint}
            data-testid={`template-textarea-${tab}`}
            className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-[11px] text-white outline-none resize-none font-mono leading-relaxed"
            style={{ minHeight: 180 }}
          />
          <div className="flex flex-wrap gap-1 mt-2">
            {['{first_name}', '{title}', '{date}', '{time}', '{venue}', '{emoji}', '[link]'].map(ph => (
              <button key={ph} type="button" onClick={() => insertPlaceholder(ph)}
                data-testid={`insert-placeholder-${ph.replace(/[^a-z]/gi, '')}`}
                title={`Insert ${ph}`}
                className="px-2 py-1 rounded-md bg-white/5 hover:bg-white/10 text-[9px] font-mono text-blue-300 border border-white/5">
                {ph}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-[10px] text-neutral-500 mb-1 block">Live Preview (Aarav Sharma)</label>
          <div className="rounded-xl p-3 whitespace-pre-wrap text-[11px] leading-relaxed text-neutral-200" style={{ background: '#0F1E14', border: '1px solid rgba(34,197,94,0.18)', minHeight: 180 }} data-testid={`template-preview-${tab}`}>
            {previewText || <span className="text-neutral-600">Preview will appear here...</span>}
          </div>
          {!currentValue && (
            <p className="text-[9px] text-neutral-600 mt-1">Showing the built-in {tab === 'invitation' ? 'warm invitation' : 'friendly reminder'} template.</p>
          )}
        </div>
      </div>
    </div>
  );
}


function SendInvitationsQueue({ api, event, rsvpLink, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('pending'); // 'pending' or 'all'
  const [cursor, setCursor] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (m) => {
    setLoading(true);
    try {
      const res = await api.get(`/events/${event.event_id}/invitations-queue?mode=${m}`);
      setData(res.data);
      setCursor(0);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api, event.event_id]);

  useEffect(() => { load(mode); }, [load, mode]);

  const queue = data?.queue || [];
  const current = queue[cursor];
  const isLast = cursor >= queue.length - 1;
  const done = queue.length > 0 && cursor >= queue.length;

  const buildWaUrl = (inv) => {
    const phone = (inv.phone || '').replace(/[^0-9]/g, '');
    const text = (inv.whatsapp_message || '').replace('[link]', rsvpLink);
    return `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
  };

  // Single user-gesture: open WhatsApp tab AND mark sent. Browser allows ONE window.open per click.
  const handleSendAndAdvance = (inv) => {
    if (!inv || busy) return;
    setBusy(true);
    // 1) Open WhatsApp first (must happen synchronously in click handler)
    window.open(buildWaUrl(inv), '_blank', 'noopener,noreferrer');
    // 2) Mark sent async — don't block
    api.post(`/events/${event.event_id}/invitees/${inv.invitee_id}/mark-invitation-sent`)
      .catch(() => {});
    // 3) Advance
    setTimeout(() => {
      setCursor(c => c + 1);
      setBusy(false);
    }, 250);
  };

  const handleSkip = () => setCursor(c => c + 1);
  const handleMarkOnly = async (inv) => {
    if (!inv) return;
    setBusy(true);
    try { await api.post(`/events/${event.event_id}/invitees/${inv.invitee_id}/mark-invitation-sent`); } catch (e) { /* noop */ }
    setCursor(c => c + 1);
    setBusy(false);
  };

  const progress = queue.length === 0 ? 0 : Math.min(cursor, queue.length) / queue.length * 100;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="glass-card w-full max-w-lg p-6 animate-fade-up" data-testid="send-invitations-queue-modal" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-heading text-base font-bold text-white flex items-center gap-2">
              <WhatsappLogo size={18} weight="fill" className="text-green-400" /> Bulk Send Invitations
            </h3>
            <p className="text-[10px] text-neutral-500 mt-0.5">Tap "Send" to open WhatsApp for each guest one-by-one</p>
          </div>
          <button onClick={onClose} className="text-neutral-500 hover:text-white"><X size={18} /></button>
        </div>

        {/* Mode Toggle */}
        <div className="flex gap-1 p-1 rounded-xl mb-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
          {[
            { id: 'pending', label: `Pending (${data?.pending_count ?? '…'})` },
            { id: 'all', label: `All with phone (${data?.total_with_phone ?? '…'})` },
          ].map(t => (
            <button key={t.id} onClick={() => setMode(t.id)} data-testid={`queue-mode-${t.id}`}
              className={`flex-1 px-3 py-2 rounded-lg text-[11px] font-semibold transition-all ${mode === t.id ? 'bg-green-500/15 text-green-300' : 'text-neutral-400 hover:text-white hover:bg-white/5'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-green-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : queue.length === 0 ? (
          <div className="p-8 text-center" data-testid="queue-empty-state">
            <Check size={36} className="text-green-400 mx-auto mb-3" />
            <p className="text-sm font-bold text-white mb-1">
              {mode === 'pending' ? 'Everyone has been invited!' : 'No invitees with phone numbers yet'}
            </p>
            <p className="text-xs text-neutral-500">
              {mode === 'pending' ? `${data?.sent_count || 0} of ${data?.total_with_phone || 0} guests already received invitations.` : 'Add guests with phone numbers to use bulk send.'}
            </p>
            {(data?.invitees_without_phone || 0) > 0 && (
              <p className="text-[10px] text-yellow-400/80 mt-2">
                {data.invitees_without_phone} invitee{data.invitees_without_phone > 1 ? 's have' : ' has'} no phone — share manually
              </p>
            )}
          </div>
        ) : done ? (
          <div className="p-8 text-center" data-testid="queue-done-state">
            <div className="w-14 h-14 rounded-full bg-green-500/15 flex items-center justify-center mx-auto mb-3">
              <Check size={28} weight="bold" className="text-green-400" />
            </div>
            <p className="text-base font-bold text-white">Done! 🎉</p>
            <p className="text-xs text-neutral-400 mt-1">All {queue.length} invitations sent in this round.</p>
            <button onClick={onClose} data-testid="queue-close-btn"
              className="mt-4 px-5 py-2 rounded-xl bg-[#FF6B35] text-white text-xs font-bold">Close</button>
          </div>
        ) : (
          <>
            {/* Progress */}
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] text-neutral-500">Progress</span>
                <span className="text-[10px] font-bold text-green-400">{cursor} / {queue.length}</span>
              </div>
              <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full bg-green-400 transition-all duration-300" style={{ width: `${progress}%` }} />
              </div>
            </div>

            {/* Current Card */}
            <div className="rounded-2xl p-4 mb-3" style={{ background: 'rgba(34,197,94,0.05)', border: '1px solid rgba(34,197,94,0.15)' }} data-testid="queue-current-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-green-500/15 flex items-center justify-center text-sm font-bold text-green-300 flex-shrink-0">
                  {current.name?.[0]?.toUpperCase() || '?'}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-white truncate">{current.name}</p>
                  <p className="text-[11px] text-neutral-400">{current.phone}</p>
                </div>
                {current.invitation_sent && (
                  <span className="text-[9px] font-bold uppercase px-2 py-0.5 rounded-full bg-green-500/15 text-green-400">Already Sent</span>
                )}
              </div>
              <div className="rounded-lg p-3 max-h-40 overflow-y-auto whitespace-pre-wrap text-[11px] leading-relaxed text-neutral-300" style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.05)' }} data-testid="queue-message-preview">
                {(current.whatsapp_message || '').replace('[link]', rsvpLink)}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <button onClick={() => handleSendAndAdvance(current)} disabled={busy}
                data-testid="queue-send-btn"
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-green-500 hover:bg-green-600 text-white text-sm font-bold disabled:opacity-50">
                <WhatsappLogo size={16} weight="fill" />
                Open WhatsApp & {isLast ? 'Finish' : 'Next →'}
              </button>
              <button onClick={handleSkip} disabled={busy} data-testid="queue-skip-btn"
                title="Skip without marking sent"
                className="px-3 py-3 rounded-xl bg-white/5 text-neutral-300 text-xs font-semibold hover:bg-white/10 disabled:opacity-50">
                Skip
              </button>
              <button onClick={() => handleMarkOnly(current)} disabled={busy} data-testid="queue-mark-only-btn"
                title="Mark as sent without opening WhatsApp"
                className="px-3 py-3 rounded-xl bg-white/5 text-neutral-300 text-xs font-semibold hover:bg-white/10 disabled:opacity-50">
                <Check size={14} />
              </button>
            </div>
            <p className="text-[9px] text-neutral-600 text-center mt-3">
              Tip: after sending, switch back to this tab and tap the green button again for the next guest.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function RSVPRow({ rsvp, onDelete, onShowQR, onEdit, event, selected, onToggleSelect, selectMode }) {
  const [showHistory, setShowHistory] = useState(false);
  const editCount = rsvp.edit_count || 0;
  const isEdited = !!rsvp.last_edited_by_admin || (Array.isArray(rsvp.edit_history) && rsvp.edit_history.length > 0);
  return (
    <div className={`px-4 py-3 flex items-center gap-3 border-b border-white/5 hover:bg-white/[0.02] relative ${selected ? 'bg-[#FF6B35]/[0.06]' : ''}`} data-testid={`rsvp-row-${rsvp.rsvp_id}`}>
      {selectMode && (
        <input type="checkbox" checked={!!selected} onChange={(e) => { e.stopPropagation(); onToggleSelect && onToggleSelect(rsvp.rsvp_id); }}
          data-testid={`rsvp-select-${rsvp.rsvp_id}`}
          className="w-4 h-4 accent-[#FF6B35] cursor-pointer shrink-0" />
      )}
      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${rsvp.can_attend?.toLowerCase()?.includes('yes') ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
        {rsvp.child_name?.[0]?.toUpperCase() || '?'}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          <p className="text-sm font-semibold text-white truncate">{rsvp.child_name}</p>
          {isEdited && (
            <button onClick={(e) => { e.stopPropagation(); setShowHistory(v => !v); }}
              data-testid={`rsvp-history-badge-${rsvp.rsvp_id}`}
              title={`${editCount} edit${editCount !== 1 ? 's' : ''} — click to view history`}
              className="inline-flex items-center gap-0.5 text-[8px] font-bold px-1.5 py-0.5 rounded-full bg-purple-500/15 text-purple-300 border border-purple-500/30 hover:bg-purple-500/25 cursor-pointer">
              <PencilSimple size={8} weight="bold" /> EDITED · {editCount}
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2 text-[10px] text-neutral-500 mt-0.5">
          {rsvp.parent_name && <span>{rsvp.parent_name}</span>}
          {rsvp.parent_phone && <span>{rsvp.parent_phone}</span>}
          {(rsvp.accompanying || []).length > 0 && <span>With: {rsvp.accompanying.join(', ')}{rsvp.accompanying_other ? ', ' + rsvp.accompanying_other : ''}</span>}
          {rsvp.dietary && <span>Diet: {rsvp.dietary}{rsvp.dietary_other ? ` (${rsvp.dietary_other})` : ''}</span>}
          {/* Custom field responses */}
          {event?.custom_fields?.map(cf => {
            const val = rsvp.custom_responses?.[cf.field_id];
            if (!val || (Array.isArray(val) && val.length === 0)) return null;
            if (cf.field_type === 'siblings') {
              if (typeof val !== 'object' || Array.isArray(val)) return null;
              const lst = Array.isArray(val.list) ? val.list : [];
              if (lst.length === 0) {
                if ((val.attending || '').toLowerCase() === 'no') return <span key={cf.field_id}>{cf.label}: No</span>;
                return null;
              }
              const str = lst.map(s => `${s.name || '—'}${s.age != null && s.age !== '' ? ` (${s.age})` : ''}`).join(', ');
              return <span key={cf.field_id}>{cf.label}: {str}</span>;
            }
            if (val && typeof val === 'object' && !Array.isArray(val)) return null;
            return <span key={cf.field_id}>{cf.label}: {Array.isArray(val) ? val.join(', ') : val}</span>;
          })}
        </div>
      </div>
      <button onClick={(e) => { e.stopPropagation(); onEdit && onEdit(rsvp); }} title="Edit response" data-testid={`rsvp-edit-${rsvp.rsvp_id}`}
        className="text-neutral-600 hover:text-[#FF6B35] flex-shrink-0 p-1"><PencilSimple size={12} /></button>
      <button onClick={(e) => { e.stopPropagation(); onShowQR && onShowQR(rsvp); }} title="Show check-in QR" data-testid={`rsvp-qr-${rsvp.rsvp_id}`}
        className="text-neutral-600 hover:text-[#FF6B35] flex-shrink-0 p-1"><QrCode size={12} /></button>
      <button onClick={onDelete} className="text-neutral-600 hover:text-red-400 flex-shrink-0 p-1"><Trash size={12} /></button>
      {showHistory && (
        <EditHistoryPanel rsvp={rsvp} onClose={() => setShowHistory(false)} />
      )}
    </div>
  );
}

function EditHistoryPanel({ rsvp, onClose }) {
  const history = Array.isArray(rsvp.edit_history) ? [...rsvp.edit_history].reverse() : [];
  return (
    <div className="absolute z-30 top-full left-12 right-2 mt-1 sm:right-auto sm:left-12 sm:w-[440px] bg-neutral-900 border border-white/15 rounded-xl shadow-2xl shadow-black/60"
      data-testid={`history-panel-${rsvp.rsvp_id}`}
      onClick={(e) => e.stopPropagation()}>
      <div className="px-4 py-2.5 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <PencilSimple size={12} weight="bold" className="text-purple-400" />
          <span className="text-xs font-bold text-white">Edit history · {rsvp.child_name}</span>
        </div>
        <button onClick={onClose} className="text-neutral-500 hover:text-white" data-testid="history-close"><X size={14} /></button>
      </div>
      <div className="max-h-[60vh] overflow-y-auto p-3 space-y-2.5">
        {history.length === 0 && (
          <p className="text-xs text-neutral-500 italic px-1 py-3 text-center">No edits recorded yet.</p>
        )}
        {history.map((h, idx) => {
          const isAdmin = h.source === 'admin';
          return (
            <div key={idx} className="rounded-lg border border-white/8 px-3 py-2.5"
              style={{ background: isAdmin ? 'rgba(168,85,247,0.06)' : 'rgba(34,197,94,0.05)' }}>
              <div className="flex items-center justify-between mb-1.5 gap-2">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold ${isAdmin ? 'bg-purple-500/15 text-purple-300' : 'bg-emerald-500/15 text-emerald-300'}`}>
                    {isAdmin ? 'ADMIN' : 'GUEST'}
                  </span>
                  <span className="text-[10px] text-neutral-300 font-semibold truncate">{h.by || '—'}</span>
                </div>
                <span className="text-[9px] text-neutral-500 tabular-nums whitespace-nowrap" title={h.at}>{formatHistoryTime(h.at)}</span>
              </div>
              {(h.changes || []).length === 0 ? (
                <p className="text-[10px] text-neutral-500 italic">No tracked changes</p>
              ) : (
                <ul className="space-y-1">
                  {h.changes.map((c, ci) => (
                    <li key={ci} className="text-[11px] text-neutral-300 flex items-baseline gap-1.5">
                      <span className="text-[9px] uppercase font-bold text-neutral-500 shrink-0 mt-0.5">{c.label}</span>
                      <span className="break-words">
                        <span className="text-red-300 line-through">{c.from || '—'}</span>
                        <span className="text-neutral-600 mx-1">→</span>
                        <span className="text-emerald-300 font-semibold">{c.to || '—'}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {h.note && (
                <p className="mt-1.5 text-[10px] text-neutral-400 italic pl-1 border-l-2 border-purple-500/40">
                  Note: {h.note}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function formatHistoryTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMin = Math.round((now - d) / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffMin < 60 * 24) return `${Math.round(diffMin / 60)}h ago`;
    return d.toLocaleString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}


function AdminEditRsvpModal({ event, rsvp, api, onClose, onSaved }) {
  // Initialise editable copy from rsvp.
  const [form, setForm] = useState(() => ({
    child_name: rsvp.child_name || '',
    parent_name: rsvp.parent_name || '',
    parent_phone: rsvp.parent_phone || '',
    can_attend: (rsvp.can_attend || '').toLowerCase().includes('yes') ? 'Yes' : 'No',
    accompanying: Array.isArray(rsvp.accompanying) ? [...rsvp.accompanying] : [],
    accompanying_other: rsvp.accompanying_other || '',
    dietary: rsvp.dietary || '',
    dietary_other: rsvp.dietary_other || '',
    custom_responses: JSON.parse(JSON.stringify(rsvp.custom_responses || {})),
    admin_note: '',
  }));
  const [notifyGuest, setNotifyGuest] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const accompanyingOptions = (event.built_in_fields?.accompanying_options) || ['Mom', 'Dad', 'Nanny', 'Other'];
  const dietaryOptions = (event.built_in_fields?.dietary_options) || ['Veg', 'Non-veg', 'Jain', 'Allergies'];

  const toggleAccompanying = (opt) => {
    setForm(f => {
      const has = f.accompanying.includes(opt);
      return { ...f, accompanying: has ? f.accompanying.filter(a => a !== opt) : [...f.accompanying, opt] };
    });
  };

  const updateCR = (fid, v) => setForm(f => ({ ...f, custom_responses: { ...f.custom_responses, [fid]: v } }));

  const updateSib = (fid, idx, patch) => {
    setForm(f => {
      const cur = f.custom_responses?.[fid] || { attending: '', list: [] };
      const lst = (cur.list || []).map((s, i) => i === idx ? { ...s, ...patch } : s);
      return { ...f, custom_responses: { ...f.custom_responses, [fid]: { ...cur, list: lst } } };
    });
  };
  const addSib = (fid) => setForm(f => {
    const cur = f.custom_responses?.[fid] || { attending: 'Yes', list: [] };
    return { ...f, custom_responses: { ...f.custom_responses, [fid]: { attending: 'Yes', list: [...(cur.list || []), { name: '', age: '' }] } } };
  });
  const rmSib = (fid, idx) => setForm(f => {
    const cur = f.custom_responses?.[fid] || { attending: '', list: [] };
    const lst = (cur.list || []).filter((_, i) => i !== idx);
    return { ...f, custom_responses: { ...f.custom_responses, [fid]: { attending: lst.length > 0 ? 'Yes' : '', list: lst } } };
  });

  const save = async () => {
    setSaving(true); setError('');
    try {
      const res = await api.put(`/events/${event.event_id}/rsvps/${rsvp.rsvp_id}`, {
        child_name: form.child_name,
        parent_name: form.parent_name,
        parent_phone: form.parent_phone,
        can_attend: form.can_attend,
        accompanying: form.accompanying,
        accompanying_other: form.accompanying_other,
        dietary: form.dietary,
        dietary_other: form.dietary_other,
        custom_responses: form.custom_responses,
        admin_note: form.admin_note,
      });
      // Optional notify
      if (notifyGuest && rsvp.parent_phone && rsvp.personal_token) {
        const editUrl = `${window.location.origin}/party/${event.slug}/edit/${rsvp.personal_token}`;
        const ph = (rsvp.parent_phone || '').replace(/[^\d+]/g, '');
        const msg = `Hi ${rsvp.parent_name || rsvp.child_name}, we updated your RSVP for "${event.title}". Please open this link to review and confirm:\n\n${editUrl}`;
        window.open(`https://wa.me/${ph}?text=${encodeURIComponent(msg)}`, '_blank');
      }
      // Pass the fresh RSVP back so the list can update instantly.
      onSaved && onSaved(res.data?.rsvp);
    } catch (e) {
      setError(e.response?.data?.detail?.message || e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4">
      <div className="bg-neutral-900 border border-white/10 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-xl max-h-[92vh] overflow-y-auto" data-testid="admin-edit-rsvp-modal">
        {/* Header */}
        <div className="sticky top-0 z-10 px-5 py-3.5 border-b border-white/10 bg-neutral-900 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(255,107,53,0.15)' }}>
              <PencilSimple size={14} weight="bold" className="text-[#FF6B35]" />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-bold text-white truncate">Edit RSVP · {rsvp.child_name}</h3>
              <p className="text-[10px] text-neutral-500">Admin override · audit logged</p>
            </div>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-white" data-testid="admin-edit-close">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {error && <div className="px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-xs text-red-300" data-testid="admin-edit-error">{error}</div>}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Child name" required>
              <input type="text" value={form.child_name} onChange={e => setForm(f => ({ ...f, child_name: e.target.value }))}
                data-testid="admin-edit-child-name"
                className="ae-input" />
            </Field>
            <Field label="Attending">
              <div className="flex gap-2">
                {['Yes', 'No'].map(v => (
                  <button key={v} type="button" onClick={() => setForm(f => ({ ...f, can_attend: v }))}
                    data-testid={`admin-edit-attend-${v.toLowerCase()}`}
                    className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${form.can_attend === v ? (v === 'Yes' ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30' : 'bg-red-500/15 text-red-300 border border-red-500/30') : 'bg-white/5 text-neutral-400 border border-white/10'}`}>
                    {v}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Parent name">
              <input type="text" value={form.parent_name} onChange={e => setForm(f => ({ ...f, parent_name: e.target.value }))}
                data-testid="admin-edit-parent-name"
                className="ae-input" />
            </Field>
            <Field label="Parent phone">
              <input type="tel" value={form.parent_phone} onChange={e => setForm(f => ({ ...f, parent_phone: e.target.value }))}
                data-testid="admin-edit-parent-phone"
                className="ae-input" />
            </Field>
          </div>

          <Field label="Accompanying">
            <div className="flex flex-wrap gap-1.5">
              {accompanyingOptions.map(opt => (
                <button key={opt} type="button" onClick={() => toggleAccompanying(opt)}
                  data-testid={`admin-edit-acc-${opt.toLowerCase()}`}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-bold transition-all ${form.accompanying.includes(opt) ? 'bg-[#FF6B35]/15 text-[#FF6B35] border border-[#FF6B35]/40' : 'bg-white/5 text-neutral-400 border border-white/10'}`}>
                  {opt}
                </button>
              ))}
            </div>
            {form.accompanying.includes('Other') && (
              <input type="text" value={form.accompanying_other} placeholder="Other (specify)"
                onChange={e => setForm(f => ({ ...f, accompanying_other: e.target.value }))}
                data-testid="admin-edit-acc-other" className="ae-input mt-2" />
            )}
          </Field>

          <Field label="Dietary">
            <div className="flex flex-wrap gap-1.5">
              {dietaryOptions.map(opt => (
                <button key={opt} type="button" onClick={() => setForm(f => ({ ...f, dietary: f.dietary === opt ? '' : opt }))}
                  data-testid={`admin-edit-diet-${opt.toLowerCase()}`}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-bold transition-all ${form.dietary === opt ? 'bg-amber-500/15 text-amber-300 border border-amber-500/40' : 'bg-white/5 text-neutral-400 border border-white/10'}`}>
                  {opt}
                </button>
              ))}
            </div>
            {form.dietary === 'Allergies' && (
              <input type="text" value={form.dietary_other} placeholder="Specify allergies"
                onChange={e => setForm(f => ({ ...f, dietary_other: e.target.value }))}
                data-testid="admin-edit-diet-other" className="ae-input mt-2" />
            )}
          </Field>

          {/* Custom fields */}
          {(event.custom_fields || []).map(cf => (
            <Field key={cf.field_id} label={cf.label}>
              <CustomFieldEditor cf={cf} value={form.custom_responses?.[cf.field_id]}
                onChange={(v) => updateCR(cf.field_id, v)}
                onSibUpdate={(idx, patch) => updateSib(cf.field_id, idx, patch)}
                onSibAdd={() => addSib(cf.field_id)}
                onSibRemove={(idx) => rmSib(cf.field_id, idx)} />
            </Field>
          ))}

          <Field label="Audit note (optional)">
            <input type="text" value={form.admin_note} onChange={e => setForm(f => ({ ...f, admin_note: e.target.value }))}
              placeholder="e.g., Parent called - kid switched to Jain diet"
              data-testid="admin-edit-note" className="ae-input" />
          </Field>

          <label className="flex items-start gap-2.5 px-3 py-2.5 rounded-lg bg-emerald-500/5 border border-emerald-500/20 cursor-pointer" data-testid="admin-edit-notify-wrap">
            <input type="checkbox" checked={notifyGuest} onChange={e => setNotifyGuest(e.target.checked)}
              data-testid="admin-edit-notify-toggle"
              className="mt-0.5 accent-emerald-500" />
            <div className="text-[11px] text-neutral-300 leading-snug">
              <span className="font-bold text-emerald-300">Notify guest on WhatsApp</span> — opens a chat with their edit link so they can verify the change. (Requires parent phone &amp; personal token.)
            </div>
          </label>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 px-5 py-3 border-t border-white/10 bg-neutral-900 flex items-center justify-end gap-2">
          <button onClick={onClose} disabled={saving} data-testid="admin-edit-cancel"
            className="px-3 py-2 rounded-lg bg-white/5 text-neutral-300 text-xs font-semibold hover:bg-white/10">
            Cancel
          </button>
          <button onClick={save} disabled={saving || !form.child_name.trim()} data-testid="admin-edit-save"
            className="px-4 py-2 rounded-lg bg-[#FF6B35] text-white text-xs font-bold hover:bg-[#ff803a] disabled:opacity-50 inline-flex items-center gap-1.5">
            <PencilSimple size={12} weight="bold" /> {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
      <style>{`.ae-input{width:100%;padding:8px 12px;border-radius:10px;background:rgba(255,255,255,0.05);border:1px solid rgba(255,255,255,0.10);font-size:13px;color:#fff;outline:none}.ae-input:focus{border-color:#FF6B35}`}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 block mb-1.5">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

function CustomFieldEditor({ cf, value, onChange, onSibUpdate, onSibAdd, onSibRemove }) {
  const t = cf.field_type;
  if (t === 'text' || t === 'textarea') {
    if (t === 'textarea') {
      return <textarea value={value || ''} onChange={e => onChange(e.target.value)}
        className="ae-input min-h-[60px]" placeholder={cf.placeholder || ''} />;
    }
    return <input type="text" value={value || ''} onChange={e => onChange(e.target.value)}
      className="ae-input" placeholder={cf.placeholder || ''} />;
  }
  if (t === 'number') {
    return <input type="number" value={value ?? ''} onChange={e => onChange(e.target.value)} className="ae-input" />;
  }
  if (t === 'yesno') {
    return (
      <div className="flex gap-2">
        {['Yes', 'No'].map(o => (
          <button key={o} type="button" onClick={() => onChange(o)}
            className={`flex-1 py-2 rounded-lg text-xs font-bold ${value === o ? 'bg-[#FF6B35]/15 text-[#FF6B35] border border-[#FF6B35]/40' : 'bg-white/5 text-neutral-400 border border-white/10'}`}>
            {o}
          </button>
        ))}
      </div>
    );
  }
  if (t === 'dropdown' || t === 'radio') {
    return (
      <div className="flex flex-wrap gap-1.5">
        {(cf.options || []).map(opt => (
          <button key={opt} type="button" onClick={() => onChange(value === opt ? '' : opt)}
            className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${value === opt ? 'bg-[#FF6B35]/15 text-[#FF6B35] border border-[#FF6B35]/40' : 'bg-white/5 text-neutral-400 border border-white/10'}`}>
            {opt}
          </button>
        ))}
      </div>
    );
  }
  if (t === 'checkbox') {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div className="flex flex-wrap gap-1.5">
        {(cf.options || []).map(opt => (
          <button key={opt} type="button"
            onClick={() => onChange(arr.includes(opt) ? arr.filter(v => v !== opt) : [...arr, opt])}
            className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${arr.includes(opt) ? 'bg-[#FF6B35]/15 text-[#FF6B35] border border-[#FF6B35]/40' : 'bg-white/5 text-neutral-400 border border-white/10'}`}>
            {opt}
          </button>
        ))}
      </div>
    );
  }
  if (t === 'siblings') {
    const v = (value && typeof value === 'object' && !Array.isArray(value)) ? value : { attending: '', list: [] };
    const att = v.attending || '';
    const lst = Array.isArray(v.list) ? v.list : [];
    return (
      <div className="space-y-2">
        <div className="flex gap-2">
          {['Yes', 'No'].map(o => (
            <button key={o} type="button"
              onClick={() => {
                if (o === 'No') onChange({ attending: 'No', list: [] });
                else onChange({ attending: 'Yes', list: lst.length > 0 ? lst : [{ name: '', age: '' }] });
              }}
              className={`flex-1 py-2 rounded-lg text-xs font-bold ${att === o ? 'bg-pink-500/15 text-pink-300 border border-pink-500/40' : 'bg-white/5 text-neutral-400 border border-white/10'}`}>
              {o}
            </button>
          ))}
        </div>
        {att === 'Yes' && (
          <div className="space-y-1.5">
            {lst.map((s, idx) => (
              <div key={idx} className="grid grid-cols-[1fr_70px_28px] gap-2">
                <input type="text" value={s.name || ''} onChange={e => onSibUpdate(idx, { name: e.target.value })}
                  placeholder={`Sibling ${idx + 1}`} className="ae-input" />
                <input type="number" value={s.age ?? ''} onChange={e => onSibUpdate(idx, { age: e.target.value })}
                  placeholder="Age" className="ae-input" />
                {lst.length > 1 ? (
                  <button type="button" onClick={() => onSibRemove(idx)} className="text-neutral-500 hover:text-red-400 flex items-center justify-center">
                    <X size={12} weight="bold" />
                  </button>
                ) : <span />}
              </div>
            ))}
            <button type="button" onClick={onSibAdd}
              className="text-[11px] font-bold px-2.5 py-1 rounded-lg bg-pink-500/15 text-pink-300 border border-pink-500/30">
              + Add another sibling
            </button>
          </div>
        )}
      </div>
    );
  }
  if (t === 'image') {
    return <div className="text-[11px] text-neutral-500 italic">Image uploads can only be changed by the guest.</div>;
  }
  return <input type="text" value={value || ''} onChange={e => onChange(e.target.value)} className="ae-input" />;
}


function RSVPCheckinQRModal({ event, rsvp, onClose }) {
  const url = `${window.location.origin}/party/${event.slug}/checkin/${rsvp.personal_token}`;
  const [copied, setCopied] = useState(false);
  const printIt = () => window.print();
  const copy = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  const waUrl = (() => {
    const ph = (rsvp.parent_phone || '').replace(/[^\d+]/g, '');
    const msg = `Hi ${rsvp.parent_name || rsvp.child_name || 'there'}, here's your check-in QR for "${event.title}". Scan it on arrival to be welcomed on the venue display:\n\n${url}`;
    return ph ? `https://wa.me/${ph}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`;
  })();
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 print:bg-white print:backdrop-blur-none"
      data-testid="rsvp-qr-modal" onClick={onClose}>
      <div className="glass-card max-w-md w-full p-6 print:shadow-none print:bg-white print:text-black"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4 print:hidden">
          <h3 className="font-heading text-base font-bold">Check-in QR</h3>
          <button onClick={onClose} className="text-neutral-500 hover:text-white"><X size={18} /></button>
        </div>
        <div className="text-center">
          <p className="text-xs text-neutral-400 mb-2 print:text-black">For</p>
          <p className="text-lg font-bold mb-4 print:text-black">{rsvp.child_name}{rsvp.parent_name ? ` (${rsvp.parent_name})` : ''}</p>
          <div className="inline-block p-4 rounded-2xl bg-white">
            <QRCodeCanvas value={url} size={220} level="M" includeMargin={false} />
          </div>
          <p className="text-[10px] text-neutral-500 mt-4 break-all px-2 print:text-black">{url}</p>
          <p className="text-xs text-neutral-400 mt-3 print:text-black">
            Scanning this on arrival auto-checks in &amp; lights up their name on the venue display.
          </p>
        </div>
        <div className="flex gap-2 mt-5 flex-wrap print:hidden">
          <button onClick={copy} data-testid="qr-copy-btn"
            className="flex-1 px-3 py-2 rounded-full border border-white/15 text-xs font-semibold hover:bg-white/5">
            {copied ? '✓ Copied' : 'Copy link'}
          </button>
          {rsvp.parent_phone && (
            <a href={waUrl} target="_blank" rel="noopener noreferrer"
              data-testid="qr-wa-btn"
              className="flex-1 text-center px-3 py-2 rounded-full bg-[#25D366]/15 text-[#25D366] border border-[#25D366]/30 text-xs font-semibold hover:bg-[#25D366]/25">
              Send WhatsApp
            </a>
          )}
          <button onClick={printIt} data-testid="qr-print-btn"
            className="flex-1 px-3 py-2 rounded-full bg-[#FF6B35] text-white text-xs font-semibold hover:opacity-90">
            Print
          </button>
        </div>
      </div>
    </div>
  );
}

function BadgeSheetModal({ event, rsvps, onClose }) {
  const handlePrint = () => {
    // Add class on <html> so print CSS scopes only to this sheet
    document.documentElement.classList.add('badge-sheet-printing');
    setTimeout(() => {
      window.print();
      setTimeout(() => document.documentElement.classList.remove('badge-sheet-printing'), 200);
    }, 80);
  };

  const themeColor = event.theme_color || '#FF6B35';
  const themeEmoji = event.theme_emoji || '🎉';
  const eventDate = event.event_date ? formatDate(event.event_date) : '';
  const eventTime = event.event_time || '';

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex flex-col"
      data-testid="badge-sheet-modal">
      {/* Modal chrome — hidden in print */}
      <div className="badge-sheet-chrome flex items-center justify-between px-5 py-3 border-b border-white/10 bg-[#0A0A0A]">
        <div>
          <h3 className="font-heading text-base font-bold text-white">Check-in QR Badges</h3>
          <p className="text-[11px] text-neutral-400 mt-0.5">
            {rsvps.length} attending guest{rsvps.length === 1 ? '' : 's'} · Print on A4 · Cut along dashed lines
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handlePrint} data-testid="badge-sheet-print-btn"
            className="px-4 py-2 rounded-full bg-[#FF6B35] text-white text-xs font-bold hover:opacity-90">
            Print / Save as PDF
          </button>
          <button onClick={onClose} data-testid="badge-sheet-close-btn"
            className="text-neutral-400 hover:text-white p-2"><X size={18} /></button>
        </div>
      </div>

      {/* Sheet preview (scrollable on screen, paginated on print) */}
      <div className="badge-sheet-scroller flex-1 overflow-y-auto p-6 bg-neutral-200">
        {rsvps.length === 0 ? (
          <div className="badge-sheet-chrome text-center text-neutral-600 text-sm py-16">
            No attending guests yet. Once guests RSVP "Yes", their badges will appear here.
          </div>
        ) : (
          <div className="badge-sheet mx-auto">
            {rsvps.map((rsvp) => {
              const url = `${window.location.origin}/party/${event.slug}/checkin/${rsvp.personal_token}`;
              return (
                <div key={rsvp.rsvp_id} className="badge-card">
                  <div className="badge-emoji">{themeEmoji}</div>
                  <div className="badge-name">{rsvp.child_name}</div>
                  {rsvp.parent_name && <div className="badge-sub">{rsvp.parent_name}</div>}
                  <div className="badge-qr">
                    <QRCodeCanvas value={url} size={130} level="M" includeMargin={false} />
                  </div>
                  <div className="badge-scan" style={{ color: themeColor }}>SCAN ON ARRIVAL</div>
                  <div className="badge-event">{event.title}</div>
                  {(eventDate || eventTime) && (
                    <div className="badge-when">{eventDate}{eventTime ? ` · ${eventTime}` : ''}</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Print + sheet styles, scoped */}
      <style>{`
        .badge-sheet {
          width: 210mm;
          background: white;
          padding: 12mm 10mm;
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 4mm;
          box-shadow: 0 6px 30px rgba(0,0,0,0.25);
        }
        .badge-card {
          height: 85mm;
          padding: 6mm 4mm 4mm;
          border: 1.5px dashed #999;
          border-radius: 4mm;
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: flex-start;
          background: white;
          color: #111;
          break-inside: avoid;
          page-break-inside: avoid;
          font-family: system-ui, -apple-system, sans-serif;
        }
        .badge-emoji { font-size: 22px; line-height: 1; margin-bottom: 2mm; }
        .badge-name {
          font-size: 14px;
          font-weight: 800;
          line-height: 1.15;
          max-width: 60mm;
          overflow-wrap: anywhere;
        }
        .badge-sub { font-size: 9px; color: #555; margin-top: 0.5mm; }
        .badge-qr {
          margin-top: 2.5mm;
          padding: 2mm;
          background: white;
        }
        .badge-qr canvas { display: block; width: 32mm !important; height: 32mm !important; }
        .badge-scan {
          font-size: 8px;
          font-weight: 800;
          letter-spacing: 0.12em;
          margin-top: 2mm;
        }
        .badge-event {
          font-size: 9px;
          font-weight: 600;
          margin-top: 1.5mm;
          color: #222;
          max-width: 60mm;
          overflow-wrap: anywhere;
        }
        .badge-when { font-size: 8px; color: #666; margin-top: 0.5mm; }

        @media print {
          @page { size: A4; margin: 0; }
          html.badge-sheet-printing, html.badge-sheet-printing body {
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          html.badge-sheet-printing body > *:not(.fixed) { display: none !important; }
          html.badge-sheet-printing [data-testid="badge-sheet-modal"] {
            position: static !important;
            background: white !important;
            backdrop-filter: none !important;
            inset: auto !important;
            z-index: auto !important;
          }
          html.badge-sheet-printing .badge-sheet-chrome { display: none !important; }
          html.badge-sheet-printing .badge-sheet-scroller {
            overflow: visible !important;
            padding: 0 !important;
            background: white !important;
          }
          html.badge-sheet-printing .badge-sheet {
            box-shadow: none !important;
            margin: 0 auto !important;
            width: 100% !important;
            padding: 8mm 6mm !important;
          }
          /* Force 12 per page = 4 rows */
          html.badge-sheet-printing .badge-card:nth-child(12n) {
            page-break-after: always;
          }
        }
      `}</style>
    </div>
  );
}

function StatCard({ label, value, color, icon: Icon }) {
  return (
    <div className="glass-card p-3 sm:p-4">
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon size={12} style={{ color }} />}
        <p className="overline text-[9px]">{label}</p>
      </div>
      <p className="text-lg sm:text-xl font-bold" style={{ color }}>{value || 0}</p>
    </div>
  );
}


// ---- Event Create/Edit Form Modal ----

function EventFormModal({ api, event, themes = [], onThemesChanged, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: event?.title || '',
    description: event?.description || '',
    event_date: event?.event_date || '',
    event_time: event?.event_time || '',
    end_time: event?.end_time || '',
    venue: event?.venue || '',
    venue_map_url: event?.venue_map_url || '',
    theme_color: event?.theme_color || '#FF6B35',
    theme_emoji: event?.theme_emoji || '',
    theme_id: event?.theme_id || 'default',
    cover_message: event?.cover_message || '',
    thank_you_message: event?.thank_you_message || 'Thank you for your response! We look forward to seeing you!',
    branding_footer: event?.branding_footer || '',
    invitation_template: event?.invitation_template || '',
    reminder_template: event?.reminder_template || '',
    built_in_fields: event?.built_in_fields || {},
    show_countdown: event?.show_countdown !== false,
    show_guest_counter: event?.show_guest_counter !== false,
    countdown_hide_within_hours: event?.countdown_hide_within_hours ?? 24,
    guest_counter_min_rsvps: event?.guest_counter_min_rsvps ?? 3,
    slug: event?.slug || '',
    slug_locked: event?.slug_locked || false,
    background_image: event?.background_image || '',
    background_overlay_opacity: event?.background_overlay_opacity ?? 0.55,
    gallery_enabled: event?.gallery_enabled || false,
    gallery_visibility: event?.gallery_visibility || 'after_event',
    gallery_allow_reactions: event?.gallery_allow_reactions !== false,
    gallery_face_match_enabled: event?.gallery_face_match_enabled !== false,
    gallery_face_consent_text: event?.gallery_face_consent_text || '',
    attachments: event?.attachments || [],
    require_unique_phone: event?.require_unique_phone !== false,
    allow_edit_after_submit: event?.allow_edit_after_submit !== false,
    rsvp_closes_at: event?.rsvp_closes_at || '',
    max_attendees: event?.max_attendees || 0,
    enable_waitlist: event?.enable_waitlist || false,
    status: event?.status || 'active',
    custom_fields: event?.custom_fields || [],
  });
  const [saving, setSaving] = useState(false);
  const [showFieldPicker, setShowFieldPicker] = useState(false);
  const [editField, setEditField] = useState(null);
  const [showThemeBuilder, setShowThemeBuilder] = useState(false);

  const handleCustomThemeSaved = async (newTheme) => {
    setShowThemeBuilder(false);
    if (onThemesChanged) await onThemesChanged();
    // Auto-select the new theme
    setForm(f => ({ ...f, theme_id: newTheme.theme_id, theme_color: newTheme.primary }));
  };

  const handleDeleteCustomTheme = async (themeId) => {
    if (!window.confirm('Delete this custom theme?')) return;
    const bareId = themeId.startsWith('custom:') ? themeId.slice(7) : themeId;
    try {
      await api.delete(`/events/themes/custom/${bareId}`);
      if (onThemesChanged) await onThemesChanged();
      // If currently selected, fall back to default
      if (form.theme_id === themeId) {
        setForm(f => ({ ...f, theme_id: 'default' }));
      }
    } catch (e) { alert(e.response?.data?.detail || 'Delete failed'); }
  };

  const handleSubmit = async () => {
    if (!form.title || !form.event_date) return;
    setSaving(true);
    try {
      if (event?.event_id) {
        await api.put(`/events/${event.event_id}`, form);
      } else {
        await api.post('/events', form);
      }
      onSaved();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  const addField = (type) => {
    const newField = {
      field_id: `cf_${Date.now().toString(36)}`,
      label: '',
      field_type: type,
      required: false,
      options: ['text', 'textarea', 'number', 'image', 'siblings'].includes(type) ? [] : ['Option 1', 'Option 2'],
      placeholder: '',
    };
    setEditField(newField);
    setShowFieldPicker(false);
  };

  const saveField = (field) => {
    const existing = form.custom_fields.findIndex(f => f.field_id === field.field_id);
    if (existing >= 0) {
      const updated = [...form.custom_fields];
      updated[existing] = field;
      setForm(f => ({ ...f, custom_fields: updated }));
    } else {
      setForm(f => ({ ...f, custom_fields: [...f.custom_fields, field] }));
    }
    setEditField(null);
  };

  const removeField = (fieldId) => {
    setForm(f => ({ ...f, custom_fields: f.custom_fields.filter(cf => cf.field_id !== fieldId) }));
  };

  const moveField = (fieldId, direction) => {
    setForm(f => {
      const arr = [...f.custom_fields];
      const idx = arr.findIndex(cf => cf.field_id === fieldId);
      if (idx < 0) return f;
      const newIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (newIdx < 0 || newIdx >= arr.length) return f;
      [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
      return { ...f, custom_fields: arr };
    });
  };

  // ---- AI Suggest Fields ----
  const [suggesting, setSuggesting] = useState(false);
  const [suggestError, setSuggestError] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [pickedSuggestions, setPickedSuggestions] = useState({});

  const fetchSuggestions = async () => {
    setSuggesting(true);
    setSuggestError('');
    setSuggestions([]);
    setPickedSuggestions({});
    try {
      const themeObj = themes.find(t => t.theme_id === form.theme_id);
      const res = await api.post('/events/suggest-fields', {
        title: form.title,
        description: form.description,
        theme_id: form.theme_id,
        theme_name: themeObj?.name || '',
        venue: form.venue,
        existing_field_labels: form.custom_fields.map(cf => cf.label).filter(Boolean),
      });
      const list = res.data.suggestions || [];
      setSuggestions(list);
      // Pre-select all by default
      const picked = {};
      list.forEach(s => { picked[s.field_id] = true; });
      setPickedSuggestions(picked);
    } catch (e) {
      setSuggestError(e.response?.data?.detail || 'AI suggestion failed. Try again.');
    }
    setSuggesting(false);
  };

  const acceptSuggestions = () => {
    const chosen = suggestions.filter(s => pickedSuggestions[s.field_id]);
    if (chosen.length === 0) { setSuggestions([]); return; }
    setForm(f => ({ ...f, custom_fields: [...f.custom_fields, ...chosen] }));
    setSuggestions([]);
    setPickedSuggestions({});
  };

  // ---- Import from Google Form ----
  const [gformUrl, setGformUrl] = useState('');
  const [importing, setImporting] = useState(false);
  const [importNotice, setImportNotice] = useState(null);

  const importGoogleForm = async () => {
    if (!gformUrl.trim()) return;
    setImporting(true);
    setSuggestError('');
    setSuggestions([]);
    setPickedSuggestions({});
    setImportNotice(null);
    try {
      const res = await api.post('/events/import-google-form', { url: gformUrl.trim() });
      const { title, description, suggestions: imported = [], skipped = 0 } = res.data;

      // Auto-fill empty event fields
      const patch = {};
      if (title && !form.title) patch.title = title;
      if (description && !form.description) patch.description = description;
      // Try to detect venue from description (lines like "Venue: ...")
      if (description && !form.venue) {
        const m = description.match(/(?:venue|location|at)\s*[:\-]\s*(.+)/i);
        if (m) patch.venue = m[1].trim().split('\n')[0].slice(0, 200);
      }
      if (Object.keys(patch).length > 0) setForm(f => ({ ...f, ...patch }));

      setSuggestions(imported);
      const picked = {};
      imported.forEach(s => { picked[s.field_id] = true; });
      setPickedSuggestions(picked);
      setGformUrl('');

      const parts = [];
      if (Object.keys(patch).length > 0) parts.push(`Auto-filled ${Object.keys(patch).join(', ')}`);
      if (imported.length > 0) parts.push(`${imported.length} field${imported.length > 1 ? 's' : ''} ready to add`);
      if (skipped > 0) parts.push(`${skipped} skipped (built-in / unsupported)`);
      setImportNotice(parts.join(' · ') || 'Imported');
      if (imported.length === 0 && skipped > 0) {
        setImportNotice(`All ${skipped} form questions already match HomeCommand's built-in fields — nothing extra to add.`);
      }
    } catch (e) {
      setSuggestError(e.response?.data?.detail || 'Could not import. Check the URL and try again.');
    }
    setImporting(false);
  };

  // Track if the mouse press started on the backdrop. Prevents the modal from
  // closing when a drag (e.g., selecting text inside an input) ends on the backdrop,
  // or when native date/color pickers are dismissed.
  const backdropMouseDownRef = React.useRef(false);
  const handleBackdropMouseDown = (e) => {
    backdropMouseDownRef.current = e.target === e.currentTarget;
  };
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && backdropMouseDownRef.current) {
      onClose();
    }
    backdropMouseDownRef.current = false;
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto" onMouseDown={handleBackdropMouseDown} onClick={handleBackdropClick}>
      <div className="glass-card w-full max-w-xl my-8 p-6 animate-fade-up" data-testid="event-form-modal" onMouseDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading font-semibold text-white text-lg">{event ? 'Edit Event' : 'Create Event'}</h3>
          <button onClick={onClose} className="text-neutral-500 hover:text-white"><X size={20} /></button>
        </div>

        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
          {/* Basic Info */}
          <div className="grid grid-cols-[1fr_60px] gap-3">
            <div>
              <label className="text-xs text-neutral-500 mb-1 block">Event Title *</label>
              <input type="text" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                data-testid="event-title-input" placeholder="Sahana & Shloka's 5th Birthday Party"
                className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none" />
            </div>
            <div>
              <label className="text-xs text-neutral-500 mb-1 block">Emoji</label>
              <input type="text" value={form.theme_emoji} onChange={e => setForm(f => ({ ...f, theme_emoji: e.target.value }))}
                placeholder="🎂" className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none text-center" />
            </div>
          </div>

          <div>
            <label className="text-xs text-neutral-500 mb-1 block">Description</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              data-testid="event-description-input" placeholder="Date, time, venue details..."
              className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none resize-none min-h-[60px]" />
          </div>

          <SlugEditor form={form} setForm={setForm} />

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-neutral-500 mb-1 block">Date *</label>
              <input type="date" value={form.event_date} onChange={e => setForm(f => ({ ...f, event_date: e.target.value }))}
                data-testid="event-date-input"
                className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none" />
            </div>
            <div>
              <label className="text-xs text-neutral-500 mb-1 block">Start Time</label>
              <input type="text" value={form.event_time} onChange={e => setForm(f => ({ ...f, event_time: e.target.value }))}
                placeholder="4:00 PM" className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none" />
            </div>
            <div>
              <label className="text-xs text-neutral-500 mb-1 block">End Time</label>
              <input type="text" value={form.end_time} onChange={e => setForm(f => ({ ...f, end_time: e.target.value }))}
                placeholder="7:00 PM" className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none" />
            </div>
          </div>

          <div>
            <label className="text-xs text-neutral-500 mb-1 block">Venue</label>
            <input type="text" value={form.venue} onChange={e => setForm(f => ({ ...f, venue: e.target.value }))}
              data-testid="event-venue-input" placeholder="Double Decker Play Area, Tardeo"
              className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none" />
          </div>

          <div>
            <label className="text-xs text-neutral-500 mb-1 block">Google Maps Link</label>
            <input type="url" value={form.venue_map_url} onChange={e => setForm(f => ({ ...f, venue_map_url: e.target.value }))}
              placeholder="https://maps.google.com/..." className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-neutral-500 mb-1 block">Accent Color</label>
              <div className="flex items-center gap-2">
                <input type="color" value={form.theme_color} onChange={e => setForm(f => ({ ...f, theme_color: e.target.value }))}
                  className="w-10 h-10 rounded-lg border-0 cursor-pointer" />
                <span className="text-xs text-neutral-400">{form.theme_color}</span>
              </div>
            </div>
            <div>
              <label className="text-xs text-neutral-500 mb-1 block">Status</label>
              <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none">
                <option value="active">Active</option>
                <option value="closed">Closed</option>
                <option value="draft">Draft</option>
              </select>
            </div>
          </div>

          {/* Theme Picker + Live Preview */}
          <div>
            <label className="text-xs text-neutral-500 mb-2 block flex items-center gap-1.5 flex-wrap">
              <Sparkle size={12} weight="fill" className="text-purple-400" /> Party Theme
              <span className="text-[9px] text-neutral-600 ml-auto">Live preview updates as you pick</span>
              <button type="button" onClick={() => setShowThemeBuilder(true)} data-testid="open-theme-builder-btn"
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-purple-500/15 text-purple-300 text-[10px] font-semibold hover:bg-purple-500/25 ml-2">
                <Plus size={10} weight="bold" /> Create Custom
              </button>
            </label>

            {/* Live Preview */}
            <ThemePreview
              theme={themes.find(t => t.theme_id === form.theme_id) || themes.find(t => t.theme_id === 'default')}
              event={form}
            />

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[260px] overflow-y-auto pr-1 mt-3" data-testid="theme-picker-grid">
              {themes.map(t => {
                const selected = form.theme_id === t.theme_id;
                const isCustom = t.is_custom;
                return (
                  <div key={t.theme_id} className="relative group">
                    <button type="button" onClick={() => setForm(f => ({ ...f, theme_id: t.theme_id, theme_color: t.primary }))}
                      data-testid={`theme-option-${t.theme_id}`}
                      className={`w-full relative text-left rounded-xl p-3 transition-all ${selected ? 'scale-[1.02]' : 'hover:scale-[1.01]'}`}
                      style={{
                        background: t.card_bg || `linear-gradient(135deg, ${t.primary}, ${t.accent})`,
                        border: selected ? `2px solid ${t.primary}` : '2px solid transparent',
                        boxShadow: selected ? `0 8px 24px ${t.primary}60` : 'none',
                        minHeight: 76,
                      }}>
                      {selected && (
                        <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-white flex items-center justify-center" style={{ color: t.primary }}>
                          <Check size={12} weight="bold" />
                        </div>
                      )}
                      {isCustom && !selected && (
                        <div className="absolute top-1.5 right-1.5 text-[8px] font-bold uppercase px-1.5 py-0.5 rounded bg-black/30 text-white tracking-wider">
                          Custom
                        </div>
                      )}
                      <div className="flex items-center gap-1 mb-1">
                        {(t.emojis || []).slice(0, 3).map((e, i) => <span key={i} style={{ fontSize: 16 }}>{e}</span>)}
                      </div>
                      <p className="text-[11px] font-bold text-white leading-tight" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>{t.name}</p>
                      {t.tagline && <p className="text-[9px] text-white/85 mt-0.5 leading-tight">{t.tagline}</p>}
                    </button>
                    {isCustom && (
                      <button type="button" onClick={() => handleDeleteCustomTheme(t.theme_id)}
                        data-testid={`delete-custom-theme-${t.theme_id}`}
                        title="Delete this custom theme"
                        className="absolute bottom-1.5 right-1.5 p-1.5 rounded-lg bg-black/40 hover:bg-red-500/60 text-white/70 hover:text-white opacity-0 group-hover:opacity-100 transition-all">
                        <Trash size={10} weight="bold" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {showThemeBuilder && (
            <CustomThemeBuilder api={api} onClose={() => setShowThemeBuilder(false)} onSaved={handleCustomThemeSaved} />
          )}

          <BackgroundImageUploader api={api} form={form} setForm={setForm} eventId={event?.event_id} />

          <div>
            <label className="text-xs text-neutral-500 mb-1 block">Cover Message (shown on RSVP page)</label>
            <input type="text" value={form.cover_message} onChange={e => setForm(f => ({ ...f, cover_message: e.target.value }))}
              placeholder="We'd love you to join us!" className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none" />
          </div>

          <div>
            <label className="text-xs text-neutral-500 mb-1 block">Thank You Message (after submit)</label>
            <input type="text" value={form.thank_you_message} onChange={e => setForm(f => ({ ...f, thank_you_message: e.target.value }))}
              className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none" />
          </div>

          <div>
            <label className="text-xs text-neutral-500 mb-1 block">RSVP Page Footer</label>
            <input type="text" value={form.branding_footer} onChange={e => setForm(f => ({ ...f, branding_footer: e.target.value }))}
              data-testid="branding-footer-input"
              placeholder="Powered by HomeCommand"
              className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none" />
            <p className="text-[10px] text-neutral-600 mt-1">Leave empty to keep "Powered by HomeCommand". Use this to brand the public RSVP page (e.g., "Hosted by the Sharma Family").</p>
          </div>

          {/* RSVP page display toggles */}
          <div className="p-3 rounded-xl bg-white/[0.02] border border-white/5">
            <p className="text-xs font-bold text-white mb-2">Display Options (Public RSVP Page)</p>
            <div className="space-y-3">
              <div>
                <label className="flex items-center gap-2 text-[11px] text-neutral-300 cursor-pointer">
                  <input type="checkbox" checked={form.show_countdown}
                    onChange={e => setForm(f => ({ ...f, show_countdown: e.target.checked }))}
                    data-testid="show-countdown-toggle"
                    className="w-3.5 h-3.5 rounded accent-[#FF6B35]" />
                  Show "X days to go" countdown chip
                </label>
                {form.show_countdown && (
                  <div className="flex items-center gap-2 mt-1.5 ml-5">
                    <span className="text-[10px] text-neutral-500">Auto-hide when event is within</span>
                    <input type="number" min="0" max="168"
                      value={form.countdown_hide_within_hours}
                      onChange={e => setForm(f => ({ ...f, countdown_hide_within_hours: Math.max(0, parseInt(e.target.value || '0', 10)) }))}
                      data-testid="countdown-hide-hours-input"
                      className="w-14 px-2 py-1 rounded-lg bg-white/5 border border-white/10 text-[11px] text-white outline-none text-center" />
                    <span className="text-[10px] text-neutral-500">hours <span className="text-neutral-600">(0 = never hide)</span></span>
                  </div>
                )}
              </div>
              <div>
                <label className="flex items-center gap-2 text-[11px] text-neutral-300 cursor-pointer">
                  <input type="checkbox" checked={form.show_guest_counter}
                    onChange={e => setForm(f => ({ ...f, show_guest_counter: e.target.checked }))}
                    data-testid="show-guest-counter-toggle"
                    className="w-3.5 h-3.5 rounded accent-[#FF6B35]" />
                  Show "N families already coming ✓" social-proof chip
                </label>
                {form.show_guest_counter && (
                  <div className="flex items-center gap-2 mt-1.5 ml-5">
                    <span className="text-[10px] text-neutral-500">Show only after at least</span>
                    <input type="number" min="0" max="500"
                      value={form.guest_counter_min_rsvps}
                      onChange={e => setForm(f => ({ ...f, guest_counter_min_rsvps: Math.max(0, parseInt(e.target.value || '0', 10)) }))}
                      data-testid="guest-counter-min-input"
                      className="w-14 px-2 py-1 rounded-lg bg-white/5 border border-white/10 text-[11px] text-white outline-none text-center" />
                    <span className="text-[10px] text-neutral-500">RSVPs <span className="text-neutral-600">(0 = always show)</span></span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* RSVP Submission Rules */}
          <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500/5 to-indigo-500/5 border border-blue-500/20">
            <div className="flex items-start gap-2 mb-2">
              <Users size={14} className="text-blue-300 mt-0.5" weight="fill" />
              <div className="flex-1">
                <p className="text-xs font-bold text-white">RSVP Rules</p>
                <p className="text-[10px] text-neutral-500 leading-relaxed">Set deadline, capacity, duplicate-prevention, and edit policy for this event.</p>
              </div>
            </div>
            <div className="space-y-3 mt-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] text-neutral-400 mb-1">RSVPs close on</label>
                  <input type="datetime-local"
                    value={(form.rsvp_closes_at || '').slice(0, 16)}
                    onChange={e => setForm(f => ({ ...f, rsvp_closes_at: e.target.value ? new Date(e.target.value).toISOString() : '' }))}
                    data-testid="rsvp-closes-at-input"
                    className="w-full px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 text-[11px] text-white outline-none" />
                </div>
                <div>
                  <label className="block text-[10px] text-neutral-400 mb-1">Max attendees (0 = no limit)</label>
                  <input type="number" min="0" value={form.max_attendees}
                    onChange={e => setForm(f => ({ ...f, max_attendees: parseInt(e.target.value, 10) || 0 }))}
                    data-testid="max-attendees-input"
                    className="w-full px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 text-[11px] text-white outline-none" />
                </div>
              </div>
              {form.max_attendees > 0 && (
                <label className="flex items-center gap-2 text-[11px] text-neutral-300 cursor-pointer">
                  <input type="checkbox" checked={form.enable_waitlist}
                    onChange={e => setForm(f => ({ ...f, enable_waitlist: e.target.checked }))}
                    data-testid="enable-waitlist-toggle"
                    className="w-3.5 h-3.5 rounded accent-blue-400" />
                  When full, accept extras as waitlist (instead of rejecting)
                </label>
              )}
              <label className="flex items-center gap-2 text-[11px] text-neutral-300 cursor-pointer">
                <input type="checkbox" checked={form.require_unique_phone}
                  onChange={e => setForm(f => ({ ...f, require_unique_phone: e.target.checked }))}
                  data-testid="require-unique-phone-toggle"
                  className="w-3.5 h-3.5 rounded accent-blue-400" />
                One RSVP per phone (detect duplicates, offer "edit your response")
              </label>
              <label className="flex items-center gap-2 text-[11px] text-neutral-300 cursor-pointer">
                <input type="checkbox" checked={form.allow_edit_after_submit}
                  onChange={e => setForm(f => ({ ...f, allow_edit_after_submit: e.target.checked }))}
                  data-testid="allow-edit-toggle"
                  className="w-3.5 h-3.5 rounded accent-blue-400" />
                Allow guests to edit their RSVP after submitting
              </label>
            </div>
          </div>

          {/* Resources / Attachments */}
          <EventAttachmentsEditor form={form} setForm={setForm} />

          {/* Photo Gallery Settings */}
          <div className="p-3 rounded-xl bg-gradient-to-br from-pink-500/5 to-orange-500/5 border border-pink-500/20">
            <div className="flex items-start gap-2 mb-2">
              <Camera size={14} className="text-pink-300 mt-0.5" weight="fill" />
              <div className="flex-1">
                <p className="text-xs font-bold text-white">Photo Wall & My Highlights</p>
                <p className="text-[10px] text-neutral-500 leading-relaxed">Upload party photos and let guests see (a) the full gallery and (b) only their photos via face matching.</p>
              </div>
            </div>
            <div className="space-y-3 mt-3">
              <label className="flex items-center gap-2 text-[11px] text-neutral-300 cursor-pointer">
                <input type="checkbox" checked={form.gallery_enabled}
                  onChange={e => setForm(f => ({ ...f, gallery_enabled: e.target.checked }))}
                  data-testid="gallery-enabled-toggle"
                  className="w-3.5 h-3.5 rounded accent-pink-400" />
                Enable photo gallery for this event
              </label>
              {form.gallery_enabled && (
                <>
                  <div className="ml-5 space-y-3">
                    <div>
                      <label className="block text-[10px] text-neutral-400 mb-1">Visible to guests</label>
                      <select value={form.gallery_visibility}
                        onChange={e => setForm(f => ({ ...f, gallery_visibility: e.target.value }))}
                        data-testid="gallery-visibility-select"
                        className="w-full px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 text-[11px] text-white outline-none">
                        <option value="after_event">After the event starts</option>
                        <option value="always">Always (even before event)</option>
                        <option value="host_only">Host only (private)</option>
                      </select>
                    </div>
                    <label className="flex items-center gap-2 text-[11px] text-neutral-300 cursor-pointer">
                      <input type="checkbox" checked={form.gallery_allow_reactions}
                        onChange={e => setForm(f => ({ ...f, gallery_allow_reactions: e.target.checked }))}
                        data-testid="gallery-reactions-toggle"
                        className="w-3.5 h-3.5 rounded accent-pink-400" />
                      Allow emoji reactions ❤️ 🔥 🎉 😂 ✨
                    </label>
                    <label className="flex items-center gap-2 text-[11px] text-neutral-300 cursor-pointer">
                      <input type="checkbox" checked={form.gallery_face_match_enabled}
                        onChange={e => setForm(f => ({ ...f, gallery_face_match_enabled: e.target.checked }))}
                        data-testid="gallery-face-match-toggle"
                        className="w-3.5 h-3.5 rounded accent-pink-400" />
                      Enable face matching — each guest gets a personal "My Photos" link
                    </label>
                    {form.gallery_face_match_enabled && (
                      <div className="ml-5">
                        <label className="block text-[10px] text-neutral-400 mb-1">Custom consent copy (optional)</label>
                        <textarea value={form.gallery_face_consent_text}
                          onChange={e => setForm(f => ({ ...f, gallery_face_consent_text: e.target.value }))}
                          data-testid="gallery-consent-text"
                          rows={2} placeholder="We store only a math signature of your face (no photo)…"
                          className="w-full px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 text-[11px] text-white outline-none resize-none" />
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* WhatsApp Template Designer */}
          <WhatsAppTemplateDesigner api={api} form={form} setForm={setForm} />

          {/* Built-in Form Fields Editor */}
          <BuiltInFieldsEditor form={form} setForm={setForm} api={api} />

          {/* Custom Fields Section */}
          <div className="border-t border-white/5 pt-4 mt-4">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <div>
                <h4 className="text-sm font-bold text-white">Custom Form Fields</h4>
                <p className="text-[10px] text-neutral-500 mt-0.5">Add extra questions to your RSVP form</p>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={fetchSuggestions} disabled={suggesting || !form.title}
                  data-testid="ai-suggest-fields-btn" title={!form.title ? 'Add event title first' : 'AI suggests fields based on your event'}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-xl bg-purple-500/15 text-purple-300 text-xs font-semibold hover:bg-purple-500/25 disabled:opacity-50 disabled:cursor-not-allowed">
                  {suggesting ? (
                    <>
                      <div className="w-3 h-3 border-2 border-purple-300 border-t-transparent rounded-full animate-spin" />
                      Thinking...
                    </>
                  ) : (
                    <>
                      <MagicWand size={12} weight="fill" /> AI Suggest
                    </>
                  )}
                </button>
                <button onClick={() => setShowFieldPicker(!showFieldPicker)} data-testid="add-custom-field-btn"
                  className="flex items-center gap-1 px-3 py-1.5 rounded-xl bg-[#FF6B35]/10 text-[#FF6B35] text-xs font-semibold">
                  <Plus size={12} /> Add Field
                </button>
              </div>
            </div>

            {/* AI Suggest Error */}
            {suggestError && (
              <div className="mb-3 p-2.5 rounded-xl bg-red-500/10 border border-red-500/20 text-[11px] text-red-300" data-testid="ai-suggest-error">
                {suggestError}
              </div>
            )}

            {/* Google Form Import */}
            <div className="mb-3 p-3 rounded-xl" style={{ background: 'rgba(59,130,246,0.05)', border: '1px solid rgba(59,130,246,0.2)' }} data-testid="gform-import-section">
              <div className="flex items-center gap-1.5 mb-2">
                <Link size={11} className="text-blue-300" />
                <span className="text-[11px] font-bold text-blue-200">Import from Google Form</span>
                <span className="text-[10px] text-neutral-500 ml-auto">Public forms only</span>
              </div>
              <div className="flex gap-2">
                <input type="url" value={gformUrl} onChange={e => setGformUrl(e.target.value)}
                  data-testid="gform-url-input" placeholder="https://docs.google.com/forms/d/e/.../viewform"
                  className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-[11px] text-white outline-none" />
                <button type="button" onClick={importGoogleForm} disabled={importing || !gformUrl.trim()}
                  data-testid="gform-import-btn"
                  className="px-3 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed">
                  {importing ? (
                    <span className="flex items-center gap-1">
                      <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Importing
                    </span>
                  ) : 'Import'}
                </button>
              </div>
              {importNotice && (
                <div className="mt-2 text-[10px] text-blue-200" data-testid="gform-import-notice">✓ {importNotice}</div>
              )}
            </div>

            {/* AI Suggestions Panel */}
            {suggestions.length > 0 && (
              <div className="mb-4 p-3 rounded-xl space-y-2" style={{ background: 'linear-gradient(135deg, rgba(168,85,247,0.08), rgba(255,107,53,0.05))', border: '1px solid rgba(168,85,247,0.25)' }} data-testid="ai-suggestions-panel">
                <div className="flex items-center gap-1.5 mb-1">
                  <Sparkle size={12} weight="fill" className="text-purple-300" />
                  <span className="text-[11px] font-bold text-purple-200">Suggested Fields{form.title ? ` for "${form.title}"` : ''}</span>
                  <span className="text-[10px] text-neutral-500 ml-auto">Toggle items below, then accept</span>
                </div>
                <div className="space-y-1.5">
                  {suggestions.map(s => {
                    const TypeInfo = FIELD_TYPES.find(ft => ft.id === s.field_type);
                    const picked = !!pickedSuggestions[s.field_id];
                    return (
                      <label key={s.field_id} className={`flex items-start gap-2 p-2.5 rounded-lg cursor-pointer transition-all ${picked ? 'bg-purple-500/15 border border-purple-400/40' : 'bg-white/[0.02] border border-white/5 hover:bg-white/[0.04]'}`}
                        data-testid={`ai-suggestion-${s.field_id}`}>
                        <input type="checkbox" checked={picked}
                          onChange={e => setPickedSuggestions(p => ({ ...p, [s.field_id]: e.target.checked }))}
                          className="mt-0.5 w-3.5 h-3.5 rounded accent-purple-400" />
                        {TypeInfo && <TypeInfo.icon size={12} className="text-neutral-400 mt-0.5 flex-shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-white">{s.label}{s.required && <span className="text-red-400 ml-1">*</span>}</p>
                          <p className="text-[10px] text-neutral-500 mt-0.5">
                            {TypeInfo?.label}
                            {s.options.length > 0 && ` — ${s.options.join(', ')}`}
                            {!s.options.length && s.placeholder && ` — "${s.placeholder}"`}
                          </p>
                        </div>
                      </label>
                    );
                  })}
                </div>
                <div className="flex items-center gap-2 pt-2">
                  <button type="button" onClick={acceptSuggestions} data-testid="ai-suggestions-accept-btn"
                    className="flex-1 py-2 rounded-lg bg-purple-500 hover:bg-purple-600 text-white text-xs font-semibold">
                    Add Selected ({Object.values(pickedSuggestions).filter(Boolean).length})
                  </button>
                  <button type="button" onClick={fetchSuggestions} disabled={suggesting} data-testid="ai-suggestions-regen-btn"
                    title="Regenerate" className="px-3 py-2 rounded-lg bg-white/5 text-neutral-300 text-xs font-semibold hover:bg-white/10 disabled:opacity-50">
                    <ArrowsClockwise size={12} />
                  </button>
                  <button type="button" onClick={() => { setSuggestions([]); setPickedSuggestions({}); }}
                    data-testid="ai-suggestions-dismiss-btn"
                    className="px-3 py-2 rounded-lg bg-white/5 text-neutral-400 text-xs font-semibold hover:bg-white/10">
                    Dismiss
                  </button>
                </div>
              </div>
            )}

            {/* Field Type Picker */}
            {showFieldPicker && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4 p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                {FIELD_TYPES.map(ft => (
                  <button key={ft.id} onClick={() => addField(ft.id)}
                    className="flex flex-col items-center gap-1.5 p-3 rounded-lg hover:bg-white/5 text-neutral-400 hover:text-white transition-colors">
                    <ft.icon size={18} />
                    <span className="text-[10px] font-medium">{ft.label}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Custom Fields List */}
            {form.custom_fields.length > 0 && (
              <div className="space-y-2">
                {form.custom_fields.map((cf, idx) => {
                  const TypeInfo = FIELD_TYPES.find(ft => ft.id === cf.field_type);
                  const isFirst = idx === 0;
                  const isLast = idx === form.custom_fields.length - 1;
                  return (
                    <div key={cf.field_id} className="flex items-center gap-2 p-3 rounded-xl bg-white/[0.02] border border-white/5" data-testid={`custom-field-${cf.field_id}`}>
                      <span className="text-[10px] font-bold text-neutral-500 w-5 text-center flex-shrink-0">{idx + 1}</span>
                      <div className="flex flex-col gap-0.5 flex-shrink-0">
                        <button type="button" onClick={() => moveField(cf.field_id, 'up')} disabled={isFirst}
                          data-testid={`field-up-${cf.field_id}`} title="Move up"
                          className="text-neutral-500 hover:text-white disabled:opacity-25 disabled:cursor-not-allowed">
                          <ArrowUp size={11} weight="bold" />
                        </button>
                        <button type="button" onClick={() => moveField(cf.field_id, 'down')} disabled={isLast}
                          data-testid={`field-down-${cf.field_id}`} title="Move down"
                          className="text-neutral-500 hover:text-white disabled:opacity-25 disabled:cursor-not-allowed">
                          <ArrowDown size={11} weight="bold" />
                        </button>
                      </div>
                      {TypeInfo && <TypeInfo.icon size={14} className="text-neutral-500 flex-shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-white truncate">{cf.label || 'Untitled field'}</p>
                        <p className="text-[10px] text-neutral-500">{TypeInfo?.label}{cf.required ? ' (Required)' : ''}{cf.options?.length > 0 ? ` — ${cf.options.length} options` : ''}</p>
                      </div>
                      <button type="button" onClick={() => setEditField(cf)} data-testid={`field-edit-${cf.field_id}`} title="Edit field"
                        className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/5 hover:bg-blue-500/15 text-neutral-300 hover:text-blue-300 text-[11px] font-semibold transition-colors">
                        <PencilSimple size={14} weight="bold" /> Edit
                      </button>
                      <button type="button" onClick={() => { if (window.confirm(`Delete field "${cf.label || 'this field'}"?`)) removeField(cf.field_id); }}
                        data-testid={`field-delete-${cf.field_id}`} title="Delete field"
                        className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/5 hover:bg-red-500/15 text-neutral-300 hover:text-red-300 text-[11px] font-semibold transition-colors">
                        <Trash size={14} weight="bold" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <button onClick={handleSubmit} disabled={saving || !form.title || !form.event_date}
          data-testid="save-event-btn"
          className="w-full mt-5 py-3 rounded-xl bg-[#FF6B35] text-white font-semibold text-sm btn-glow disabled:opacity-50">
          {saving ? 'Saving...' : event ? 'Update Event' : 'Create Event'}
        </button>
      </div>

      {/* Field Editor Modal */}
      {editField && (
        <FieldEditorModal field={editField} otherFields={form.custom_fields} builtInFields={form.built_in_fields} attachments={form.attachments || []} onSave={saveField} onClose={() => setEditField(null)} />
      )}
    </div>
  );
}


function FieldEditorModal({ field, otherFields = [], builtInFields = {}, attachments = [], onSave, onClose }) {
  const [f, setF] = useState({
    show_if_field: '',
    show_if_value: '',
    show_if_rules: [],
    show_if_logic: 'and',
    helper_text: '',
    helper_image: '',
    helper_label: '',
    linked_attachment_id: '',
    ...field,
  });

  const addOption = () => setF(prev => ({ ...prev, options: [...prev.options, `Option ${prev.options.length + 1}`] }));
  const updateOption = (idx, val) => setF(prev => ({ ...prev, options: prev.options.map((o, i) => i === idx ? val : o) }));
  const removeOption = (idx) => setF(prev => ({ ...prev, options: prev.options.filter((_, i) => i !== idx) }));
  const hasOptions = ['dropdown', 'radio', 'checkbox'].includes(f.field_type);
  const isImage = f.field_type === 'image';
  const isSiblings = f.field_type === 'siblings';

  // Build list of fields admin can condition on — now supports ALL field types
  // (text/number use contains/range operators; choice fields use equals).
  const accompanyingOptions = builtInFields?.accompanying?.options
    || ["Mom", "Dad", "Parent + Nanny", "Nanny"];
  const dietaryOptions = builtInFields?.dietary?.options
    || ["Jain", "Veg", "Non-Veg", "No Restrictions"];
  const canAttendOptions = builtInFields?.can_attend?.options
    || ["Yes", "Sorry, can't make it"];
  const conditionalSources = [
    { id: 'can_attend', label: builtInFields?.can_attend?.label || 'Can Attend', type: 'choice', options: canAttendOptions },
    ...(builtInFields?.accompanying?.enabled !== false ? [{ id: 'accompanying', label: builtInFields?.accompanying?.label || 'Accompanying', type: 'multi_choice', options: accompanyingOptions }] : []),
    ...(builtInFields?.dietary?.enabled !== false ? [{ id: 'dietary', label: builtInFields?.dietary?.label || 'Dietary', type: 'choice', options: dietaryOptions }] : []),
    ...otherFields.filter(o => o.field_id !== f.field_id && o.field_type !== 'image' && o.field_type !== 'siblings').map(o => ({
      id: o.field_id,
      label: o.label || 'Untitled field',
      type: o.field_type === 'checkbox' ? 'multi_choice' : (['dropdown', 'radio', 'yesno'].includes(o.field_type) ? 'choice' : (o.field_type === 'number' ? 'number' : 'text')),
      options: o.field_type === 'yesno' ? ['Yes', 'No'] : (o.options || []),
    })),
  ];

  // ---- Show-if rule builder helpers ----
  const opsForType = (sourceType) => {
    if (sourceType === 'choice' || sourceType === 'multi_choice') {
      return [
        { id: 'equals', label: 'equals' },
        { id: 'not_equals', label: 'does NOT equal' },
        { id: 'any', label: 'has any answer' },
      ];
    }
    if (sourceType === 'number') {
      return [
        { id: 'equals', label: 'equals' },
        { id: 'not_equals', label: 'does NOT equal' },
        { id: 'gt', label: '>' },
        { id: 'lt', label: '<' },
        { id: 'gte', label: '≥' },
        { id: 'lte', label: '≤' },
        { id: 'between', label: 'between' },
        { id: 'any', label: 'has any answer' },
      ];
    }
    // text
    return [
      { id: 'equals', label: 'equals' },
      { id: 'not_equals', label: 'does NOT equal' },
      { id: 'contains', label: 'contains' },
      { id: 'not_contains', label: 'does NOT contain' },
      { id: 'any', label: 'has any answer' },
    ];
  };

  // Auto-migrate legacy show_if_field/show_if_value into a rule on first edit
  React.useEffect(() => {
    if (f.show_if_field && (f.show_if_rules || []).length === 0) {
      const op = f.show_if_value ? 'equals' : 'any';
      setF(prev => ({
        ...prev,
        show_if_rules: [{ field: f.show_if_field, op, value: f.show_if_value || '', value2: '' }],
        show_if_logic: 'and',
        show_if_field: '',
        show_if_value: '',
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addRule = () => setF(prev => ({
    ...prev,
    show_if_rules: [...(prev.show_if_rules || []), { field: conditionalSources[0]?.id || '', op: 'equals', value: '', value2: '' }],
  }));
  const updateRule = (idx, patch) => setF(prev => ({
    ...prev,
    show_if_rules: prev.show_if_rules.map((r, i) => i === idx ? { ...r, ...patch } : r),
  }));
  const removeRule = (idx) => setF(prev => ({ ...prev, show_if_rules: prev.show_if_rules.filter((_, i) => i !== idx) }));

  // ---- Helper image upload ----
  const helperImgInputRef = React.useRef(null);
  const onHelperFile = (file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { alert('Helper image must be under 3MB'); return; }
    const reader = new FileReader();
    reader.onload = () => setF(prev => ({ ...prev, helper_image: reader.result }));
    reader.readAsDataURL(file);
  };

  const backdropMouseDownRef = React.useRef(false);
  const handleBackdropMouseDown = (e) => {
    backdropMouseDownRef.current = e.target === e.currentTarget;
  };
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && backdropMouseDownRef.current) {
      onClose();
    }
    backdropMouseDownRef.current = false;
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 overflow-y-auto" onMouseDown={handleBackdropMouseDown} onClick={handleBackdropClick}>
      <div className="absolute inset-0 bg-black/40 pointer-events-none" />
      <div className="relative glass-card w-full max-w-sm p-5 animate-fade-up my-8" data-testid="field-editor-modal" onMouseDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()}>
        <h4 className="text-sm font-bold text-white mb-4">Configure Field</h4>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] text-neutral-500 mb-1 block">Label *</label>
            <input type="text" value={f.label} onChange={e => setF(prev => ({ ...prev, label: e.target.value }))}
              data-testid="field-label-input" placeholder="e.g., T-shirt size"
              className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none" />
          </div>
          {!hasOptions && !isImage && !isSiblings && (
            <div>
              <label className="text-[10px] text-neutral-500 mb-1 block">Placeholder</label>
              <input type="text" value={f.placeholder} onChange={e => setF(prev => ({ ...prev, placeholder: e.target.value }))}
                className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none" />
            </div>
          )}
          {isImage && (
            <p className="text-[10px] text-neutral-500 p-2 rounded-lg bg-blue-500/5 border border-blue-500/15">
              ℹ️ Guests will be able to upload a photo (jpg/png/webp/heic, max 5MB) for this field.
            </p>
          )}
          {isSiblings && (
            <p className="text-[10px] text-neutral-500 p-2 rounded-lg bg-pink-500/5 border border-pink-500/15">
              👶 Guests will see "Any siblings attending?" Yes/No. If Yes, they can add multiple siblings with name + age. This makes filtering and the sibling count exact.
            </p>
          )}
          {hasOptions && (
            <div>
              <label className="text-[10px] text-neutral-500 mb-1 block">Options</label>
              <div className="space-y-2">
                {f.options.map((opt, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input type="text" value={opt} onChange={e => updateOption(idx, e.target.value)}
                      className="flex-1 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-xs text-white outline-none" />
                    <button onClick={() => removeOption(idx)} className="text-neutral-500 hover:text-red-400"><X size={12} /></button>
                  </div>
                ))}
                <button onClick={addOption} className="text-xs text-[#FF6B35] font-semibold hover:underline">+ Add Option</button>
              </div>
            </div>
          )}

          {/* Helper text + Helper image */}
          <div className="pt-2 border-t border-white/5 space-y-2">
            <label className="text-[10px] text-neutral-500 font-semibold block">Helper / Reference (optional)</label>
            <div className="grid grid-cols-2 gap-2">
              <input type="text" value={f.helper_label}
                onChange={e => setF(prev => ({ ...prev, helper_label: e.target.value }))}
                data-testid="field-helper-label-input"
                placeholder="Pill label (e.g., 'Size Chart')"
                className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-xs text-white outline-none" />
              <input type="text" value={f.helper_text}
                onChange={e => setF(prev => ({ ...prev, helper_text: e.target.value }))}
                data-testid="field-helper-text-input"
                placeholder="Caption under field"
                className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-xs text-white outline-none" />
            </div>

            {/* Link to event-level attachment (priority over inline helper_image) */}
            {attachments.length > 0 && (
              <div>
                <label className="text-[9px] text-neutral-500 mb-1 block">
                  Link to an event resource <span className="text-neutral-600">(skip uploading the same file again)</span>
                </label>
                <select value={f.linked_attachment_id || ''}
                  onChange={e => setF(prev => ({ ...prev, linked_attachment_id: e.target.value }))}
                  data-testid="field-linked-attachment-select"
                  className="w-full px-2 py-2 rounded-lg bg-white/5 border border-white/10 text-[11px] text-white outline-none">
                  <option value="" style={{ background: '#0a0a0a' }}>— None —</option>
                  {attachments.map(a => (
                    <option key={a.att_id} value={a.att_id} style={{ background: '#0a0a0a' }}>
                      {(a.mime || '').startsWith('image/') ? '🖼' : '📄'} {a.name || a.att_id}
                    </option>
                  ))}
                </select>
                {f.linked_attachment_id && (
                  <p className="text-[9px] text-blue-300 mt-1">
                    ⚡ This field will show the linked resource as its helper image.
                  </p>
                )}
              </div>
            )}

            {!f.linked_attachment_id && (
              <div className="flex items-start gap-2">
                {f.helper_image ? (
                  <div className="relative w-20 h-20 rounded-lg overflow-hidden bg-neutral-900 border border-white/10 shrink-0">
                    <img src={f.helper_image} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => setF(prev => ({ ...prev, helper_image: '' }))}
                      data-testid="remove-helper-image"
                      className="absolute top-0.5 right-0.5 p-1 rounded bg-red-500/80 text-white hover:scale-110">
                      <X size={9} weight="bold" />
                    </button>
                  </div>
                ) : (
                  <button type="button" onClick={() => helperImgInputRef.current?.click()}
                    data-testid="upload-helper-image"
                    className="w-20 h-20 rounded-lg border-2 border-dashed border-white/15 flex flex-col items-center justify-center gap-0.5 text-[9px] text-neutral-500 hover:bg-white/[0.04] hover:border-white/25 shrink-0">
                    <ImageIcon size={16} />
                    <span>Add image</span>
                  </button>
                )}
                <p className="text-[9px] text-neutral-600 leading-relaxed flex-1">
                  Upload a size chart, dress code, menu, or any reference image. Guests will see a small thumbnail next to this field — they can tap to zoom.
                </p>
              </div>
            )}
            <input ref={helperImgInputRef} type="file" accept="image/*" className="hidden"
              onChange={e => onHelperFile(e.target.files?.[0])} />
          </div>

          {/* Conditional visibility — sentence-style rule builder */}
          <div className="pt-2 border-t border-white/5">
            <label className="text-[10px] text-neutral-500 font-semibold block mb-2">When should this field appear?</label>
            <div className="flex gap-1 mb-2 p-0.5 rounded-lg bg-black/30">
              <button type="button"
                onClick={() => setF(prev => ({ ...prev, show_if_rules: [] }))}
                data-testid="show-mode-always"
                className={`flex-1 py-1.5 rounded-md text-[10px] font-bold transition-all ${(f.show_if_rules || []).length === 0 ? 'bg-[#FF6B35] text-white' : 'text-neutral-400 hover:text-white'}`}>
                Always show
              </button>
              <button type="button"
                onClick={() => {
                  if ((f.show_if_rules || []).length === 0) {
                    addRule();
                  }
                }}
                data-testid="show-mode-conditional"
                className={`flex-1 py-1.5 rounded-md text-[10px] font-bold transition-all ${(f.show_if_rules || []).length > 0 ? 'bg-[#FF6B35] text-white' : 'text-neutral-400 hover:text-white'}`}>
                Only when…
              </button>
            </div>

            {(f.show_if_rules || []).length > 0 && (
              <>
                <div className="space-y-1.5">
                  {f.show_if_rules.map((rule, idx) => {
                    const source = conditionalSources.find(s => s.id === rule.field);
                    const sourceType = source?.type || 'text';
                    const ops = opsForType(sourceType);
                    const isChoice = sourceType === 'choice' || sourceType === 'multi_choice';
                    const showValueInput = rule.op !== 'any';
                    return (
                      <div key={idx} className="flex items-center flex-wrap gap-1.5 p-2 rounded-lg bg-white/[0.03] border border-white/5 text-[10px] text-neutral-400" data-testid={`show-if-rule-${idx}`}>
                        {idx === 0 ? (
                          <span className="text-neutral-500 font-semibold">Show when</span>
                        ) : (
                          <button type="button"
                            onClick={() => setF(prev => ({ ...prev, show_if_logic: prev.show_if_logic === 'and' ? 'or' : 'and' }))}
                            data-testid="show-if-logic-toggle"
                            className="px-1.5 py-0.5 rounded text-[9px] uppercase font-bold bg-orange-500/20 text-orange-300 hover:bg-orange-500/30">
                            {f.show_if_logic === 'or' ? 'or' : 'and'}
                          </button>
                        )}
                        <select value={rule.field}
                          onChange={e => updateRule(idx, { field: e.target.value, op: 'equals', value: '', value2: '' })}
                          data-testid={`rule-field-${idx}`}
                          className="px-1.5 py-1 rounded bg-black/40 border border-white/10 text-white outline-none max-w-[140px]">
                          <option value="" style={{ background: '#0a0a0a' }}>— pick field —</option>
                          {conditionalSources.map(s => (
                            <option key={s.id} value={s.id} style={{ background: '#0a0a0a' }}>{s.label}</option>
                          ))}
                        </select>
                        <select value={rule.op}
                          onChange={e => updateRule(idx, { op: e.target.value, value2: e.target.value === 'between' ? rule.value2 : '' })}
                          data-testid={`rule-op-${idx}`}
                          className="px-1.5 py-1 rounded bg-black/40 border border-white/10 text-white outline-none">
                          {ops.map(o => (
                            <option key={o.id} value={o.id} style={{ background: '#0a0a0a' }}>{o.label}</option>
                          ))}
                        </select>
                        {showValueInput && isChoice && (
                          <select value={rule.value}
                            onChange={e => updateRule(idx, { value: e.target.value })}
                            data-testid={`rule-value-${idx}`}
                            className="px-1.5 py-1 rounded bg-black/40 border border-white/10 text-white outline-none max-w-[110px]">
                            <option value="" style={{ background: '#0a0a0a' }}>— value —</option>
                            {(source?.options || []).map(o => (
                              <option key={o} value={o} style={{ background: '#0a0a0a' }}>{o}</option>
                            ))}
                          </select>
                        )}
                        {showValueInput && !isChoice && (
                          <input value={rule.value}
                            onChange={e => updateRule(idx, { value: e.target.value })}
                            data-testid={`rule-value-${idx}`}
                            type={sourceType === 'number' ? 'number' : 'text'}
                            placeholder={sourceType === 'number' ? 'Number' : 'Text'}
                            className="px-1.5 py-1 rounded bg-black/40 border border-white/10 text-white outline-none w-20" />
                        )}
                        {rule.op === 'between' && (
                          <>
                            <span className="text-neutral-500">and</span>
                            <input value={rule.value2}
                              onChange={e => updateRule(idx, { value2: e.target.value })}
                              data-testid={`rule-value2-${idx}`}
                              type="number" placeholder="High"
                              className="px-1.5 py-1 rounded bg-black/40 border border-white/10 text-white outline-none w-16" />
                          </>
                        )}
                        <button type="button" onClick={() => removeRule(idx)}
                          data-testid={`remove-rule-${idx}`}
                          className="ml-auto text-neutral-500 hover:text-red-400 p-0.5"><X size={11} /></button>
                      </div>
                    );
                  })}
                </div>
                <button type="button" onClick={addRule} data-testid="add-show-if-rule"
                  className="mt-2 text-[10px] text-[#FF6B35] font-semibold hover:underline">
                  + Add another condition
                </button>
                {(f.show_if_rules || []).length >= 2 && (
                  <p className="text-[9px] text-neutral-600 mt-1.5">
                    Tip: tap the <span className="text-orange-300 font-bold">AND</span> pill to toggle to <span className="text-orange-300 font-bold">OR</span> (match any rule).
                  </p>
                )}
              </>
            )}
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={f.required} onChange={e => setF(prev => ({ ...prev, required: e.target.checked }))}
              className="w-4 h-4 rounded" />
            <span className="text-xs text-neutral-300">Required field {(f.show_if_rules || []).length > 0 && <span className="text-[9px] text-neutral-600">(only when shown)</span>}</span>
          </label>
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-white/5 text-neutral-400 text-xs font-semibold">Cancel</button>
          <button onClick={() => { if (f.label) onSave(f); }} disabled={!f.label} data-testid="save-field-btn"
            className="flex-1 py-2.5 rounded-xl bg-[#FF6B35] text-white text-xs font-semibold disabled:opacity-50">Save Field</button>
        </div>
      </div>
    </div>
  );
}


function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return dateStr; }
}


// ============================================================
// Drafts Tab — incomplete / abandoned RSVPs (auto-saved while guest was typing)
// ============================================================
function DraftsTab({ event, drafts, onDelete, onPromote, onRefresh }) {
  const fmtTime = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
      if (diffMin < 1) return 'just now';
      if (diffMin < 60) return `${diffMin} min ago`;
      const diffHr = Math.floor(diffMin / 60);
      if (diffHr < 24) return `${diffHr}h ago`;
      const diffDay = Math.floor(diffHr / 24);
      if (diffDay < 7) return `${diffDay}d ago`;
      return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
    } catch { return iso; }
  };

  const cleanPhone = (p) => (p || '').replace(/[^\d+]/g, '');

  const buildReminderWA = (d) => {
    const slug = event.slug;
    const link = `${window.location.origin}/party/${slug}`;
    const name = d.parent_name || d.child_name || 'there';
    const msg = `Hi ${name}, this is a quick reminder for "${event.title}". ` +
      `We noticed you started filling the RSVP but didn't finish — ` +
      `would you mind completing it? Here's the link again:\n\n${link}\n\nThanks!`;
    const phone = cleanPhone(d.parent_phone);
    return phone ? `https://wa.me/${phone}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`;
  };

  if (!drafts || drafts.length === 0) {
    return (
      <div className="glass-card p-8 text-center" data-testid="drafts-empty">
        <div className="mx-auto w-14 h-14 rounded-2xl bg-white/5 flex items-center justify-center mb-3">
          <CheckCircle size={26} className="text-green-400" weight="fill" />
        </div>
        <h3 className="font-heading font-bold text-base">No incomplete forms — great!</h3>
        <p className="text-sm text-neutral-400 mt-1 max-w-md mx-auto">
          When a guest starts filling out the RSVP but doesn't submit, you'll see them here so you can send a polite nudge.
        </p>
        <button onClick={onRefresh} className="mt-4 px-4 py-2 rounded-full border border-white/10 text-xs font-medium hover:bg-white/5">
          Refresh
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="drafts-section">
      <div className="glass-card p-4 flex items-center gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-xl bg-yellow-500/15 flex items-center justify-center">
          <Clock size={18} className="text-yellow-400" weight="fill" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading text-sm font-bold">{drafts.length} incomplete {drafts.length === 1 ? 'form' : 'forms'}</h3>
          <p className="text-xs text-neutral-400 mt-0.5">
            Guests who started but never clicked Submit. Send them a WhatsApp nudge or promote to a confirmed RSVP if they told you in person.
          </p>
        </div>
        <button onClick={onRefresh} data-testid="drafts-refresh-btn" className="px-3 py-2 rounded-full border border-white/10 text-xs font-medium hover:bg-white/5">
          Refresh
        </button>
      </div>

      {drafts.map(d => (
        <div key={d.draft_id} data-testid={`draft-${d.draft_id}`}
          className="glass-card p-4 flex items-start gap-4 flex-wrap">
          <div className="flex-1 min-w-[180px]">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-white">
                {d.child_name || d.parent_name || <span className="text-neutral-500 italic">no name yet</span>}
              </span>
              {d.parent_name && d.child_name && (
                <span className="text-[11px] text-neutral-400">/ {d.parent_name}</span>
              )}
            </div>
            <div className="flex items-center gap-3 mt-1 text-[11px] text-neutral-400 flex-wrap">
              {d.parent_phone && <span><Phone size={11} className="inline mr-0.5" /> {d.parent_phone}</span>}
              {d.can_attend && <span className={`px-2 py-0.5 rounded-full ${d.can_attend.toLowerCase().includes('yes') ? 'bg-green-500/15 text-green-400' : 'bg-yellow-500/15 text-yellow-400'}`}>
                {d.can_attend}
              </span>}
              <span><Clock size={11} className="inline mr-0.5" /> {fmtTime(d.updated_at)}</span>
            </div>
          </div>

          <div className="w-40">
            <div className="flex items-center justify-between text-[10px] text-neutral-400 mb-1">
              <span>Progress</span>
              <span className="font-bold text-white">{d.progress_pct ?? 0}%</span>
            </div>
            <div className="w-full h-1.5 rounded-full bg-white/5 overflow-hidden">
              <div className="h-full rounded-full transition-all"
                style={{
                  width: `${Math.min(100, d.progress_pct || 0)}%`,
                  background: (d.progress_pct || 0) >= 75 ? '#22C55E' : (d.progress_pct || 0) >= 40 ? '#F59E0B' : '#EF4444',
                }} />
            </div>
            <div className="text-[10px] text-neutral-500 mt-1">
              {d.progress_filled ?? 0}/{d.progress_total ?? 0} fields filled
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <a href={buildReminderWA(d)} target="_blank" rel="noopener noreferrer"
              data-testid={`draft-remind-${d.draft_id}`}
              className="px-3 py-2 rounded-full bg-[#25D366]/15 text-[#25D366] border border-[#25D366]/30 text-xs font-semibold hover:bg-[#25D366]/25 flex items-center gap-1.5">
              <WhatsappLogo size={14} weight="fill" /> Remind
            </a>
            <button onClick={() => onPromote(d.draft_id)}
              data-testid={`draft-promote-${d.draft_id}`}
              className="px-3 py-2 rounded-full bg-blue-500/15 text-blue-400 border border-blue-500/30 text-xs font-semibold hover:bg-blue-500/25 flex items-center gap-1.5"
              title="Convert this draft to a confirmed RSVP (use only if guest confirmed offline)">
              <Check size={14} weight="bold" /> Promote
            </button>
            <button onClick={() => onDelete(d.draft_id)}
              data-testid={`draft-delete-${d.draft_id}`}
              className="p-2 rounded-full bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20"
              title="Discard this draft">
              <Trash size={14} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}




// ============================================================
// Match Tab — invitation list vs actual RSVPs side-by-side
// ============================================================
function MatchTab({ event }) {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeBucket, setActiveBucket] = useState('pending'); // most actionable first

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/events/${event.event_id}/invitation-match`);
      setData(r.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api, event.event_id]);

  useEffect(() => { load(); }, [load]);

  const cleanPhone = (p) => (p || '').replace(/[^\d+]/g, '');

  const waUrlPending = (row) => {
    const link = `${window.location.origin}/party/${event.slug}`;
    const name = row.name || 'there';
    const msg = `Hi ${name}, gentle reminder for "${event.title}"${event.event_date ? ` on ${formatDate(event.event_date)}` : ''}. ` +
      `Could you please RSVP? It helps us plan better:\n\n${link}\n\nThanks!`;
    const ph = cleanPhone(row.phone);
    return ph ? `https://wa.me/${ph}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`;
  };

  const waUrlBulkPending = () => {
    // Open WhatsApp without a number, pre-fill the same message — host can broadcast manually
    const link = `${window.location.origin}/party/${event.slug}`;
    const msg = `Hi! Gentle reminder for "${event.title}"${event.event_date ? ` on ${formatDate(event.event_date)}` : ''}. ` +
      `Please RSVP here: ${link}`;
    return `https://wa.me/?text=${encodeURIComponent(msg)}`;
  };

  if (loading) return <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return <div className="glass-card p-6 text-sm text-neutral-400">Could not load match data.</div>;

  const s = data.stats;
  const buckets = [
    { id: 'pending',   label: 'Pending',   color: '#F59E0B', count: s.pending,   rows: data.pending,   help: 'Invited but no response yet. Send a nudge.' },
    { id: 'attending', label: 'Attending', color: '#22C55E', count: s.attending, rows: data.attending, help: 'Invited and confirmed yes.' },
    { id: 'declined',  label: 'Declined',  color: '#EF4444', count: s.declined,  rows: data.declined,  help: 'Invited and said they cannot make it.' },
    { id: 'uninvited', label: 'Surprise',  color: '#00E5FF', count: s.uninvited, rows: data.uninvited, help: 'Responded but not in your invitee list (gate-crashers, additions or phone mismatches).' },
  ];
  const current = buckets.find(b => b.id === activeBucket) || buckets[0];

  return (
    <div className="space-y-4" data-testid="match-section">
      {/* Headline summary */}
      <div className="glass-card p-5 flex items-center gap-4 flex-wrap">
        <div className="flex-1 min-w-[180px]">
          <h3 className="font-heading text-base font-bold">Invitation vs RSVP</h3>
          <p className="text-xs text-neutral-400 mt-1">
            <span className="text-white font-semibold">{s.responded}</span>
            <span> of </span>
            <span className="text-white font-semibold">{s.total_invited}</span>
            <span> invitees responded — </span>
            <span className="text-[#FF6B35] font-bold">{s.response_rate_pct}%</span>
            <span> response rate.</span>
          </p>
        </div>
        {s.pending > 0 && (
          <a href={waUrlBulkPending()} target="_blank" rel="noopener noreferrer"
            data-testid="match-bulk-remind"
            className="px-4 py-2 rounded-full bg-[#25D366]/15 text-[#25D366] border border-[#25D366]/30 text-xs font-semibold hover:bg-[#25D366]/25 flex items-center gap-2">
            <WhatsappLogo size={14} weight="fill" /> Broadcast reminder
          </a>
        )}
        <button onClick={load} className="px-3 py-2 rounded-full border border-white/10 text-xs font-medium hover:bg-white/5">
          Refresh
        </button>
      </div>

      {/* Bucket pills */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {buckets.map(b => (
          <button key={b.id} onClick={() => setActiveBucket(b.id)}
            data-testid={`match-bucket-${b.id}`}
            className={`p-4 rounded-2xl text-left transition-all border ${activeBucket === b.id ? 'shadow-lg' : 'opacity-80 hover:opacity-100'}`}
            style={{
              background: activeBucket === b.id ? `${b.color}18` : 'rgba(255,255,255,0.02)',
              borderColor: activeBucket === b.id ? `${b.color}55` : 'rgba(255,255,255,0.06)',
            }}>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-extrabold" style={{ color: b.color }}>{b.count}</span>
              <span className="text-xs uppercase tracking-wider text-neutral-300 font-semibold">{b.label}</span>
            </div>
            <p className="text-[11px] text-neutral-400 mt-1">{b.help}</p>
          </button>
        ))}
      </div>

      {/* Active bucket list */}
      <div className="glass-card p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full" style={{ background: current.color, boxShadow: `0 0 8px ${current.color}` }} />
            <h4 className="font-heading text-sm font-bold">{current.label} ({current.count})</h4>
          </div>
        </div>

        {current.rows.length === 0 ? (
          <p className="text-sm text-neutral-500 text-center py-6" data-testid={`match-empty-${current.id}`}>
            {current.id === 'pending' && 'Everyone has responded — beautiful! 🎉'}
            {current.id === 'attending' && 'No confirmed attendees yet.'}
            {current.id === 'declined' && 'No declines so far.'}
            {current.id === 'uninvited' && 'No surprise responses.'}
          </p>
        ) : (
          <div className="space-y-2" data-testid={`match-list-${current.id}`}>
            {current.rows.map(row => (
              <MatchRow key={row.invitee_id || row.rsvp_id} row={row} bucket={current.id}
                color={current.color} waUrl={current.id === 'pending' ? waUrlPending(row) : null} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function MatchRow({ row, bucket, color, waUrl }) {
  const fmtTime = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
      if (diffMin < 60) return `${Math.max(1, diffMin)} min ago`;
      const diffHr = Math.floor(diffMin / 60);
      if (diffHr < 24) return `${diffHr}h ago`;
      const diffDay = Math.floor(diffHr / 24);
      if (diffDay < 7) return `${diffDay}d ago`;
      return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
    } catch { return ''; }
  };
  const isPending = bucket === 'pending';
  const isUninvited = bucket === 'uninvited';
  const primaryName = isPending ? row.name : (row.child_name || row.parent_name || row.invitee_name || '(unnamed)');
  const subline = isPending
    ? `Invited ${fmtTime(row.invited_at)}${row.reminder_count ? ` • ${row.reminder_count} reminder${row.reminder_count > 1 ? 's' : ''} sent` : ''}`
    : isUninvited
      ? `Responded ${fmtTime(row.submitted_at)}`
      : `Invited as "${row.invitee_name || '—'}" • Responded ${fmtTime(row.submitted_at)}${row.rsvp_count_for_invitee > 1 ? ` (${row.rsvp_count_for_invitee} entries on this phone)` : ''}`;

  return (
    <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/[0.03] transition-colors">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${color}18` }}>
        <span className="text-[11px] font-bold" style={{ color }}>
          {(primaryName || '?').slice(0, 1).toUpperCase()}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-white truncate">{primaryName}</div>
        <div className="text-[11px] text-neutral-400 flex items-center gap-2 flex-wrap">
          {row.phone && <span><Phone size={10} className="inline mr-0.5" />{row.phone}</span>}
          <span>{subline}</span>
        </div>
      </div>
      {isPending && waUrl && (
        <a href={waUrl} target="_blank" rel="noopener noreferrer"
          data-testid={`match-remind-${row.invitee_id}`}
          className="px-2.5 py-1.5 rounded-full bg-[#25D366]/15 text-[#25D366] border border-[#25D366]/30 text-[11px] font-semibold hover:bg-[#25D366]/25 flex items-center gap-1">
          <WhatsappLogo size={12} weight="fill" /> Remind
        </a>
      )}
      {!isPending && row.can_attend && (
        <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full"
          style={{ background: `${color}18`, color }}>
          {row.can_attend}
        </span>
      )}
    </div>
  );
}


// ============================================================
// Photo Gallery Admin Modal — bulk upload, list, delete, reprocess
// ============================================================
function PhotoGalleryAdminModal({ api, event, onClose }) {
  const API = apiOrigin();
  const [photos, setPhotos] = useState([]);
  const [status, setStatus] = useState({ total_photos: 0, processed_photos: 0, face_references: 0 });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadQueue, setUploadQueue] = useState({ done: 0, total: 0 });
  const [errors, setErrors] = useState([]);
  const fileInputRef = React.useRef(null);

  const fetchAll = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([
        api.get(`/events/${event.event_id}/photos`),
        api.get(`/events/${event.event_id}/photos/processing-status`),
      ]);
      setPhotos(p.data || []);
      setStatus(s.data || { total_photos: 0, processed_photos: 0, face_references: 0 });
    } catch (e) { /* noop */ }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, event.event_id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    setErrors([]);
    setUploadQueue({ done: 0, total: files.length });
    const fails = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const fd = new FormData();
        fd.append('file', file);
        // eslint-disable-next-line no-await-in-loop
        await api.post(`/events/${event.event_id}/photos/upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } catch (e) {
        fails.push(`${file.name}: ${e.response?.data?.detail || 'upload failed'}`);
      }
      setUploadQueue({ done: i + 1, total: files.length });
    }
    setErrors(fails);
    setUploading(false);
    await fetchAll();
  };

  const handleDelete = async (photoId) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await api.delete(`/events/${event.event_id}/photos/${photoId}`);
      await fetchAll();
    } catch { /* noop */ }
  };

  const reprocess = async () => {
    try {
      await api.post(`/events/${event.event_id}/photos/process-faces`);
      setTimeout(fetchAll, 1500);
    } catch { /* noop */ }
  };

  const galleryUrl = `${window.location.origin}/party/${event.slug}/gallery`;
  const enabled = event.gallery_enabled;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-3" onClick={onClose}>
      <div className="bg-[#0E0E0E] border border-white/10 rounded-2xl w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()} data-testid="photo-gallery-admin-modal">
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/5">
          <div className="flex items-center gap-2">
            <Camera size={18} className="text-pink-300" weight="fill" />
            <div>
              <h3 className="text-white text-base font-bold">Photo Gallery — {event.title}</h3>
              <p className="text-[10px] text-neutral-500">
                {status.total_photos} photos · {status.face_references} face refs · {status.processed_photos}/{status.total_photos} processed
              </p>
            </div>
          </div>
          <button onClick={onClose} data-testid="close-photo-modal" className="p-1.5 rounded-lg hover:bg-white/10">
            <X size={16} className="text-neutral-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {!enabled && (
            <div className="p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/30 text-xs text-yellow-300">
              ⚠️ Gallery is currently <b>disabled</b>. Open Edit Event → Photo Wall & My Highlights to enable it.
            </div>
          )}

          <div className="rounded-2xl border-2 border-dashed border-white/15 p-6 text-center bg-white/[0.02] hover:bg-white/[0.04] transition-colors">
            <Camera size={28} className="text-pink-300 mx-auto mb-2" weight="duotone" />
            <p className="text-sm font-bold text-white mb-1">Upload party photos</p>
            <p className="text-[11px] text-neutral-500 mb-3">JPG, PNG, WebP, HEIC · up to 15MB each · multiple at a time</p>
            <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden"
              onChange={e => { handleFiles(e.target.files); e.target.value = ''; }} />
            <button onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="bulk-upload-btn"
              className="px-5 py-2.5 rounded-xl text-sm font-bold bg-gradient-to-r from-pink-500 to-orange-500 text-white inline-flex items-center gap-2 hover:scale-[1.02] active:scale-[0.98] transition-transform disabled:opacity-60">
              {uploading ? (
                <><span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> Uploading {uploadQueue.done}/{uploadQueue.total}</>
              ) : (
                <><Plus size={14} weight="bold" /> Choose photos</>
              )}
            </button>
            {errors.length > 0 && (
              <div className="mt-3 p-2 rounded-lg bg-red-500/10 border border-red-500/30 text-[10px] text-red-300 text-left">
                {errors.slice(0, 5).map((e, i) => <div key={i}>• {e}</div>)}
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <a href={galleryUrl} target="_blank" rel="noopener noreferrer" data-testid="open-public-gallery"
              className="px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-xs font-semibold text-neutral-300 inline-flex items-center gap-1.5">
              <Eye size={12} /> Open public gallery
            </a>
            <button onClick={() => { navigator.clipboard?.writeText(galleryUrl); }} data-testid="copy-gallery-link"
              className="px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-xs font-semibold text-neutral-300 inline-flex items-center gap-1.5">
              <Link size={12} /> Copy link
            </button>
            <button onClick={reprocess} data-testid="reprocess-faces-btn"
              className="px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-xs font-semibold text-neutral-300 inline-flex items-center gap-1.5 ml-auto">
              <ArrowsClockwise size={12} /> Rematch faces
            </button>
          </div>

          {loading ? (
            <div className="py-10 text-center text-neutral-500 text-sm">Loading…</div>
          ) : photos.length === 0 ? (
            <div className="py-12 text-center">
              <div className="text-4xl opacity-50 mb-2">📷</div>
              <p className="text-sm text-neutral-500">No photos uploaded yet. Choose some above to start the gallery.</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
              {photos.map(ph => (
                <div key={ph.photo_id} data-testid={`admin-photo-${ph.photo_id}`}
                  className="relative aspect-square rounded-lg overflow-hidden bg-neutral-900 group">
                  <img src={`${API}${ph.thumb_url}`} alt="" loading="lazy"
                    className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <button onClick={() => handleDelete(ph.photo_id)} data-testid={`admin-photo-delete-${ph.photo_id}`}
                    className="absolute top-1.5 right-1.5 p-1.5 rounded-full bg-red-500/80 text-white opacity-0 group-hover:opacity-100 hover:scale-110 transition-all">
                    <Trash size={10} weight="bold" />
                  </button>
                  <div className="absolute bottom-1 left-1 right-1 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="text-[9px] font-bold text-white">
                      {ph.face_count > 0 ? `${ph.face_count} 👤` : 'no face'}
                    </span>
                    {ph.detected_rsvp_ids?.length > 0 && (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-green-500/30 text-green-300">
                        ✓ {ph.detected_rsvp_ids.length}
                      </span>
                    )}
                  </div>
                  {Object.values(ph.reactions || {}).reduce((a, b) => a + b, 0) > 0 && (
                    <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-black/60 text-pink-300 backdrop-blur-md">
                      <HeartIcon size={9} weight="fill" className="inline mr-0.5" />
                      {Object.values(ph.reactions || {}).reduce((a, b) => a + b, 0)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ============================================================
// EventAttachmentsEditor — admin uploads reference docs/images
// (size charts, dress code, menus, FAQs) shown to guests at top of RSVP page.
// ============================================================
function EventAttachmentsEditor({ form, setForm }) {
  const fileRef = React.useRef(null);
  const [error, setError] = useState('');

  const onFile = (file) => {
    if (!file) return;
    setError('');
    if (file.size > 4 * 1024 * 1024) { setError('File too large (4MB max)'); return; }
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif', 'application/pdf'];
    if (!allowed.includes(file.type)) { setError('Only images or PDF allowed'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const newAtt = {
        att_id: `att_${Math.random().toString(36).slice(2, 9)}`,
        name: file.name.replace(/\.[^.]+$/, '').slice(0, 50) || 'Attachment',
        description: '',
        data_url: reader.result,
        mime: file.type,
        size: file.size,
        uploaded_at: new Date().toISOString(),
      };
      setForm(f => ({ ...f, attachments: [...(f.attachments || []), newAtt] }));
    };
    reader.readAsDataURL(file);
  };

  const removeAtt = (att_id) => {
    if (!window.confirm('Remove this attachment?')) return;
    setForm(f => ({ ...f, attachments: (f.attachments || []).filter(a => a.att_id !== att_id) }));
  };

  const updateAtt = (att_id, patch) => {
    setForm(f => ({ ...f, attachments: (f.attachments || []).map(a => a.att_id === att_id ? { ...a, ...patch } : a) }));
  };

  return (
    <div className="p-3 rounded-xl bg-white/[0.02] border border-white/5" data-testid="event-attachments-editor">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <p className="text-xs font-bold text-white">Resources / Attachments</p>
          <p className="text-[10px] text-neutral-500 leading-relaxed">Size chart, dress code, menu, FAQ, etc. Guests see a 'Resources' section at the top of the RSVP page with previews.</p>
        </div>
        <button type="button" onClick={() => fileRef.current?.click()} data-testid="add-attachment-btn"
          className="px-2.5 py-1.5 rounded-lg bg-[#FF6B35] text-white text-[10px] font-bold inline-flex items-center gap-1 hover:scale-105 transition-transform">
          <Plus size={10} weight="bold" /> Add
        </button>
        <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden"
          onChange={e => { onFile(e.target.files?.[0]); e.target.value = ''; }} />
      </div>

      {error && <p className="text-[10px] text-red-300 mb-2" data-testid="attachment-error">{error}</p>}

      {(form.attachments || []).length === 0 ? (
        <p className="text-[10px] text-neutral-600 italic">No resources yet. Click "Add" to upload one.</p>
      ) : (
        <div className="space-y-2">
          {form.attachments.map((att) => (
            <div key={att.att_id} className="flex items-start gap-2 p-2 rounded-lg bg-black/30 border border-white/5" data-testid={`attachment-row-${att.att_id}`}>
              {att.mime?.startsWith('image/') ? (
                <img src={att.data_url} alt="" className="w-12 h-12 rounded object-cover bg-neutral-900 shrink-0" />
              ) : (
                <div className="w-12 h-12 rounded bg-red-500/15 border border-red-500/30 flex items-center justify-center text-red-300 text-[9px] font-bold shrink-0">
                  PDF
                </div>
              )}
              <div className="flex-1 min-w-0 space-y-1">
                <input type="text" value={att.name}
                  onChange={e => updateAtt(att.att_id, { name: e.target.value })}
                  data-testid={`attachment-name-${att.att_id}`}
                  placeholder="Name (e.g., T-shirt size chart)"
                  className="w-full px-2 py-1 rounded bg-white/5 border border-white/10 text-[11px] text-white outline-none font-semibold" />
                <input type="text" value={att.description}
                  onChange={e => updateAtt(att.att_id, { description: e.target.value })}
                  placeholder="Short description (optional)"
                  className="w-full px-2 py-1 rounded bg-white/5 border border-white/10 text-[10px] text-neutral-400 outline-none" />
              </div>
              <button type="button" onClick={() => removeAtt(att.att_id)}
                data-testid={`remove-attachment-${att.att_id}`}
                className="p-1.5 rounded text-neutral-500 hover:text-red-400 hover:bg-red-500/10 shrink-0">
                <Trash size={11} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// ExportMenu — dropdown with CSV / Excel / PDF download options
// ============================================================
function ExportMenu({ rsvpsCount = 0, onCSV, onXLSX, onPDF }) {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const empty = rsvpsCount === 0;

  const handle = (fn) => () => {
    setOpen(false);
    if (empty) {
      alert('No RSVPs yet — nothing to export.');
      return;
    }
    fn?.();
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(o => !o)} data-testid="export-menu-btn"
        className="flex items-center gap-1 px-3 py-2 rounded-xl bg-blue-500/10 text-blue-400 text-xs font-semibold hover:bg-blue-500/20">
        <Export size={12} />
        Export
        <ArrowDown size={9} weight="bold" className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1.5 z-40 min-w-[180px] rounded-xl bg-[#0E0E0E] border border-white/10 shadow-2xl overflow-hidden"
          data-testid="export-menu-dropdown">
          <button onClick={handle(onCSV)} data-testid="export-csv-btn"
            className="w-full px-3 py-2.5 flex items-center gap-2 text-left text-xs text-neutral-300 hover:bg-blue-500/15 hover:text-blue-300 transition-colors">
            <FileText size={12} weight="duotone" />
            <div className="flex-1">
              <div className="font-bold">CSV</div>
              <div className="text-[9px] text-neutral-500">Plain spreadsheet · UTF-8</div>
            </div>
          </button>
          <div className="h-px bg-white/5" />
          <button onClick={handle(onXLSX)} data-testid="export-xlsx-btn"
            className="w-full px-3 py-2.5 flex items-center gap-2 text-left text-xs text-neutral-300 hover:bg-green-500/15 hover:text-green-300 transition-colors">
            <FileText size={12} weight="duotone" />
            <div className="flex-1">
              <div className="font-bold">Excel (.xlsx)</div>
              <div className="text-[9px] text-neutral-500">3 sheets · thumbnails embedded</div>
            </div>
          </button>
          <div className="h-px bg-white/5" />
          <button onClick={handle(onPDF)} data-testid="export-pdf-btn"
            className="w-full px-3 py-2.5 flex items-center gap-2 text-left text-xs text-neutral-300 hover:bg-red-500/15 hover:text-red-300 transition-colors">
            <FileText size={12} weight="duotone" />
            <div className="flex-1">
              <div className="font-bold">PDF</div>
              <div className="text-[9px] text-neutral-500">Vendor sheet · A4 landscape</div>
            </div>
          </button>
          {empty && (
            <div className="px-3 py-2 text-[9px] text-yellow-400/80 italic border-t border-white/5">
              No RSVPs yet
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ============================================================
// ShareLiveViewButton — admin opt-in shareable read-only RSVP link
// ============================================================
function ShareLiveViewButton({ api, event }) {
  const [open, setOpen] = useState(false);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editLink, setEditLink] = useState(null); // link being edited or null
  const [creating, setCreating] = useState(false);

  const fetchLinks = async () => {
    try {
      const r = await api.get(`/events/${event.event_id}/share-links`);
      setLinks(r.data.links || []);
    } catch { /* noop */ }
  };

  useEffect(() => { if (open) fetchLinks(); /* eslint-disable-next-line */ }, [open]);

  const createLink = async (data) => {
    setLoading(true);
    try {
      await api.post(`/events/${event.event_id}/share-links`, data);
      await fetchLinks();
      setCreating(false);
    } catch { /* noop */ }
    setLoading(false);
  };
  const updateLink = async (link_id, data) => {
    setLoading(true);
    try {
      await api.patch(`/events/${event.event_id}/share-links/${link_id}`, data);
      await fetchLinks();
      setEditLink(null);
    } catch { /* noop */ }
    setLoading(false);
  };
  const deleteLink = async (link_id) => {
    if (!window.confirm('Revoke this link permanently?')) return;
    setLoading(true);
    try {
      await api.delete(`/events/${event.event_id}/share-links/${link_id}`);
      await fetchLinks();
    } catch { /* noop */ }
    setLoading(false);
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="share-live-view-btn"
        className="flex items-center gap-1 px-3 py-2 rounded-xl bg-purple-500/10 text-purple-400 text-xs font-semibold hover:bg-purple-500/20">
        <Link size={12} /> Share live view
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={() => setOpen(false)}>
          <div className="bg-[#0E0E0E] border border-white/10 rounded-2xl w-full max-w-xl my-8 max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()} data-testid="share-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
              <div className="flex items-center gap-2">
                <Link size={16} className="text-purple-300" />
                <h3 className="text-white text-sm font-bold">Share links</h3>
              </div>
              <button onClick={() => setOpen(false)} className="p-1 rounded-lg hover:bg-white/10 text-neutral-400">
                <X size={14} />
              </button>
            </div>

            <div className="p-5">
              <p className="text-[11px] text-neutral-400 mb-4 leading-relaxed">
                Create one or more share links — each with its own expiry, column visibility, and phone-masking. Perfect for sharing different views with caterer, gift-bag vendor, photographer, etc.
              </p>

              {links.length === 0 && !creating && (
                <div className="text-center py-6 rounded-xl border border-dashed border-white/10">
                  <p className="text-xs text-neutral-500 mb-3">No share links yet</p>
                  <button onClick={() => setCreating(true)} data-testid="add-first-link"
                    className="px-4 py-2 rounded-xl bg-purple-500 text-white text-xs font-bold inline-flex items-center gap-1.5">
                    <Plus size={12} weight="bold" /> Create first link
                  </button>
                </div>
              )}

              <div className="space-y-2">
                {links.map(link => (
                  <ShareLinkRow key={link.link_id} link={link} event={event}
                    onEdit={() => setEditLink(link)}
                    onDelete={() => deleteLink(link.link_id)}
                    onRegenerate={() => updateLink(link.link_id, { regenerate: true })} />
                ))}
              </div>

              {links.length > 0 && !creating && (
                <button onClick={() => setCreating(true)} data-testid="add-share-link"
                  className="mt-3 px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-purple-300 text-xs font-bold inline-flex items-center gap-1.5">
                  <Plus size={12} weight="bold" /> Add another link
                </button>
              )}

              {(creating || editLink) && (
                <ShareLinkForm event={event}
                  link={editLink}
                  loading={loading}
                  onCancel={() => { setCreating(false); setEditLink(null); }}
                  onSave={(data) => editLink ? updateLink(editLink.link_id, data) : createLink(data)} />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ShareLinkRow({ link, event, onEdit, onDelete, onRegenerate }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/events/rsvps/${link.share_token}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* noop */ }
  };
  const expiryLabel = (() => {
    if (!link.expires_at) return 'Never expires';
    try {
      const d = new Date(link.expires_at);
      const days = Math.round((d - Date.now()) / (24*3600*1000));
      return days > 0 ? `Expires in ${days}d` : 'Expired';
    } catch { return ''; }
  })();
  const visibleCount = (link.visible_field_ids || []).length;
  return (
    <div className="p-3 rounded-xl bg-purple-500/5 border border-purple-500/20" data-testid={`share-link-row-${link.link_id}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-bold text-white truncate">{link.name}</span>
          <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/10 text-neutral-400">{expiryLabel}</span>
          {link.hide_phone && <span className="text-[9px] px-1.5 py-0.5 rounded bg-yellow-500/15 text-yellow-300 font-bold">Phone hidden</span>}
          {visibleCount > 0 && <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300 font-bold">{visibleCount} cols</span>}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onEdit} className="p-1 rounded hover:bg-white/10 text-neutral-400" data-testid={`edit-link-${link.link_id}`}><PencilSimple size={11} /></button>
          <button onClick={onRegenerate} className="p-1 rounded hover:bg-white/10 text-neutral-400" title="Regenerate URL"><ArrowsClockwise size={11} /></button>
          <button onClick={onDelete} className="p-1 rounded hover:bg-red-500/15 text-red-400" data-testid={`delete-link-${link.link_id}`}><Trash size={11} /></button>
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        <input readOnly value={url} onFocus={e => e.target.select()}
          className="flex-1 px-2 py-1.5 rounded-lg bg-black/40 border border-white/10 text-[10px] text-white outline-none truncate" />
        <button onClick={copy} className="px-2.5 py-1.5 rounded-lg bg-purple-500 text-white text-[10px] font-bold whitespace-nowrap">
          {copied ? '✓' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

function ShareLinkForm({ event, link, loading, onCancel, onSave }) {
  const allFieldOpts = [
    { id: 'child_name', label: 'Child Name' },
    { id: 'parent_name', label: 'Parent Name' },
    { id: 'parent_phone', label: 'Phone' },
    { id: 'can_attend', label: 'Attending status' },
    { id: 'accompanying', label: 'Accompanying' },
    { id: 'dietary', label: 'Dietary' },
    ...((event.custom_fields || []).map(cf => ({ id: cf.field_id, label: cf.label || cf.field_id }))),
  ];
  const [name, setName] = useState(link?.name || 'Share link');
  const [hidePhone, setHidePhone] = useState(link?.hide_phone || false);
  const [expiresIn, setExpiresIn] = useState(() => {
    if (!link) return 7;
    if (!link.expires_at) return 0; // never
    const d = Math.round((new Date(link.expires_at) - Date.now()) / (24*3600*1000));
    return d >= 28 ? 30 : 7;
  });
  const [allCols, setAllCols] = useState(() => link ? (link.visible_field_ids || []).length === 0 : true);
  const [visible, setVisible] = useState(() => new Set(link?.visible_field_ids || allFieldOpts.map(o => o.id)));

  const toggle = (id) => {
    setVisible(s => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const handleSave = () => {
    onSave({
      name: name || 'Share link',
      hide_phone: hidePhone,
      expires_in_days: expiresIn === 0 ? null : expiresIn,
      visible_field_ids: allCols ? [] : Array.from(visible),
    });
  };

  return (
    <div className="mt-4 p-4 rounded-xl bg-white/[0.03] border border-white/10 space-y-3" data-testid="share-link-form">
      <div className="flex items-center justify-between mb-1">
        <h4 className="text-xs font-bold text-white">{link ? 'Edit link' : 'Create new share link'}</h4>
        <button onClick={onCancel} className="p-1 rounded text-neutral-500 hover:text-white"><X size={12} /></button>
      </div>
      <div>
        <label className="text-[10px] text-neutral-500 block mb-1">Name (just for your reference)</label>
        <input value={name} onChange={e => setName(e.target.value)}
          data-testid="link-name-input"
          placeholder="e.g., Caterer · Gift-bag vendor · Photographer"
          className="w-full px-2.5 py-1.5 rounded-lg bg-black/40 border border-white/10 text-[11px] text-white outline-none" />
      </div>
      <div>
        <label className="text-[10px] text-neutral-500 block mb-1">Expires</label>
        <div className="flex gap-1.5">
          {[{label:'7d',v:7},{label:'30d',v:30},{label:'Never',v:0}].map(o => (
            <button key={o.label} type="button" onClick={() => setExpiresIn(o.v)}
              data-testid={`expiry-${o.label}`}
              className={`flex-1 py-1.5 rounded-lg text-[10px] font-bold transition-all ${expiresIn === o.v ? 'bg-purple-500 text-white' : 'bg-white/5 text-neutral-400 hover:bg-white/10'}`}>
              {o.label}
            </button>
          ))}
        </div>
      </div>
      <label className="flex items-center gap-2 text-[11px] text-neutral-300 cursor-pointer">
        <input type="checkbox" checked={hidePhone} onChange={e => setHidePhone(e.target.checked)}
          data-testid="link-hide-phone"
          className="w-3.5 h-3.5 rounded accent-purple-400" />
        Hide phone numbers (mask as •••••99)
      </label>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-[10px] text-neutral-500 font-semibold uppercase tracking-wider">Visible columns</label>
          <button type="button" onClick={() => setAllCols(c => !c)}
            data-testid="toggle-all-cols"
            className="text-[10px] text-purple-300 font-bold">
            {allCols ? 'Pick specific columns…' : 'Show all'}
          </button>
        </div>
        {!allCols ? (
          <div className="grid grid-cols-2 gap-1.5 max-h-40 overflow-y-auto p-2 rounded-lg bg-black/20 border border-white/5">
            {allFieldOpts.map(opt => (
              <label key={opt.id} className="flex items-center gap-1.5 text-[10px] text-neutral-300 cursor-pointer">
                <input type="checkbox" checked={visible.has(opt.id)} onChange={() => toggle(opt.id)}
                  data-testid={`vis-col-${opt.id}`}
                  className="w-3 h-3 rounded accent-purple-400" />
                <span className="truncate">{opt.label}</span>
              </label>
            ))}
          </div>
        ) : (
          <p className="text-[10px] text-neutral-500 italic">All columns visible</p>
        )}
      </div>

      <div className="flex gap-2 pt-2">
        <button onClick={onCancel} className="flex-1 py-2 rounded-xl bg-white/5 text-neutral-400 text-[11px] font-bold">Cancel</button>
        <button onClick={handleSave} disabled={loading} data-testid="save-share-link"
          className="flex-1 py-2 rounded-xl bg-purple-500 text-white text-[11px] font-bold disabled:opacity-60">
          {link ? 'Save changes' : 'Create link'}
        </button>
      </div>
    </div>
  );
}

function daysFromExpiry(iso) {
  if (!iso) return null;
  try {
    const ms = new Date(iso).getTime() - Date.now();
    const days = Math.round(ms / (24 * 60 * 60 * 1000));
    if (days >= 28 && days <= 32) return 30;
    if (days >= 5 && days <= 8) return 7;
    return days > 0 ? days : null;
  } catch { return null; }
}
